/**
 * CIRKLA forum frontend customizations.
 *
 * This file extends several Flarum forum UI areas that do not expose stable
 * component-level hooks for every required behavior. Some features therefore
 * combine Flarum extension points with guarded DOM observers and idempotent
 * DOM patches.
 *
 * Developer notes:
 * - Keep installer functions idempotent. Most installers are called after route
 *   changes and DOM mutations, so they must be safe to run repeatedly.
 * - Prefer small feature-scoped helpers over adding unrelated logic to existing
 *   observers.
 * - Any new direct DOM patch should mark its inserted/processed elements with a
 *   Cirkla-specific class or data attribute to avoid duplicate work.
 * - Route helpers should continue to support both route names and pathname
 *   fallbacks because third-party Flarum extensions may rename routes.
 */

import app from "flarum/forum/app";
import sessionLoaderSvg, { startSessionLoader, finishSessionLoader, setSessionLoaderPending } from "./session-loader";
import { extend, override } from "flarum/common/extend";
import IndexPage from "flarum/forum/components/IndexPage";
import HeaderPrimary from "flarum/forum/components/HeaderPrimary";
import HeaderSecondary from "flarum/forum/components/HeaderSecondary";
import Composer from "flarum/forum/components/Composer";
import ComposerButton from "flarum/forum/components/ComposerButton";
import ComposerState from "flarum/forum/states/ComposerState";
import UserControls from "flarum/forum/utils/UserControls";
import SessionDropdown from "flarum/forum/components/SessionDropdown";
import UserPage from "flarum/forum/components/UserPage";
import UserCard from "flarum/forum/components/UserCard";
import SettingsPage from "flarum/forum/components/SettingsPage";
import Modal from "flarum/common/components/Modal";
import Button from "flarum/common/components/Button";
import Component from "flarum/common/Component";
import humanTime from "flarum/common/utils/humanTime";
import "./cirkla-home";
import { handleExpiredSessionDetected } from "./cirkla-home";
import { installCirklaLiveUpdates } from "./cirkla-live-updates";
import Search from "flarum/forum/components/Search";
// -----------------------------------------------------------------------------
// Runtime state
// -----------------------------------------------------------------------------

const m = window.m;
const forumTrans = (group, key, parameters = {}) => {
  const result = app.translator.trans(
    `htwsaar-htw-theme.forum.${group}.${key}`,
    parameters,
  );
  // app.translator.trans() always returns an array of vnode/text/number
  // pieces (even without params, and plural forms can yield numbers).
  // Join primitive pieces into one string so String(forumTrans(...)) can't
  // insert stray commas between them; leave real vnodes (objects) alone.
  return Array.isArray(result) &&
    result.every((part) => typeof part === "string" || typeof part === "number")
    ? result.map(String).join("")
    : result;
};
const escapeHtmlText = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char],
  );
let pendingRequests = 0;
let calendarLoadingActive = false;
let calendarLoadingSawRequest = false;
let calendarLoadingStartedAt = 0;
let calendarLoadingSettledAt = 0;
let calendarLoadingObserver = null;
let calendarLoadingStopTimer = null;
let calendarSelectedDate = "";
let lastAlertSignature = "";
let lastAlertShownAt = 0;
const ALERT_DEDUPE_WINDOW_MS = 7000;
const WRITE_DEDUPE_WINDOW_MS = 12000;
const writeRequestPromises = new Map();

function stableRequestPart(value) {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(stableRequestPart);
  }

  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = stableRequestPart(value[key]);
      return result;
    }, {});
}

function requestDedupeKey(options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const url = String(options.url || "");

  if (!["POST", "PATCH", "DELETE"].includes(method)) return "";

  return JSON.stringify({
    method,
    url,
    body: stableRequestPart(options.body || null),
    params: stableRequestPart(options.params || null),
  });
}

function apiUrl(path) {
  return `${String(app.forum?.attribute?.("apiUrl") || "").replace(/\/$/, "")}${path}`;
}

function requestStatus(error) {
  return Number(
    error?.status || error?.response?.status || error?.xhr?.status || 0,
  );
}

function requestMethod(options = {}) {
  return String(options.method || "GET").toUpperCase();
}

function requestBody(options = {}) {
  return (
    options.body?.data?.attributes || options.body?.data || options.body || {}
  );
}

function currentSessionUserId() {
  const user = app.session?.user;

  try {
    return String(
      (typeof user?.id === "function" ? user.id() : user?.id) ||
        user?.data?.id ||
        "",
    );
  } catch (_error) {
    return "";
  }
}

function payloadItemUserId(item) {
  return String(
    item?.relationships?.user?.data?.id ||
      item?.relationships?.lastPostedUser?.data?.id ||
      item?.attributes?.userId ||
      "",
  );
}

function newestIncludedPost(payload, content) {
  const normalizedContent = String(content || "").trim();
  const userId = currentSessionUserId();
  const included = Array.isArray(payload?.included) ? payload.included : [];

  return included.find((item) => {
    if (item?.type !== "posts") return false;
    if (String(item?.attributes?.content || "").trim() !== normalizedContent)
      return false;
    return !userId || payloadItemUserId(item) === userId;
  });
}

function verifyMessageWriteSucceeded(originalRequest, options = {}) {
  const body = requestBody(options);
  const messageContents = String(body.messageContents || "").trim();
  const conversationId = String(body.conversationId || "").trim();

  if (!messageContents || !conversationId) return Promise.resolve(null);

  return originalRequest({
    method: "GET",
    url: apiUrl(`/neoncube-private-messages/messages/${conversationId}`),
    params: { offset: 0 },
  })
    .then((payload) => {
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const match = data.find((item) => {
        const attrs = item?.attributes || {};
        if (String(attrs.message || "").trim() !== messageContents)
          return false;
        return (
          !currentSessionUserId() ||
          String(attrs.userId || "") === currentSessionUserId()
        );
      });

      return match ? { ...payload, data: match } : null;
    })
    .catch(() => null);
}

function verifyDiscussionWriteSucceeded(originalRequest, options = {}) {
  const body = requestBody(options);
  const title = String(body.title || "").trim();
  const content = String(body.content || "").trim();

  if (!title && !content) return Promise.resolve(null);

  return originalRequest({
    method: "GET",
    url: apiUrl("/discussions"),
    params: {
      sort: "-createdAt",
      "page[limit]": 5,
      ...(title ? { "filter[q]": title } : {}),
    },
  })
    .then((payload) => {
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const userId = currentSessionUserId();
      const match = data.find((item) => {
        const attrs = item?.attributes || {};
        if (title && String(attrs.title || "").trim() !== title) return false;
        if (userId && payloadItemUserId(item) !== userId) return false;
        if (content && !newestIncludedPost(payload, content)) return false;
        return true;
      });

      return match ? { ...payload, data: match } : null;
    })
    .catch(() => null);
}

function verifyPostWriteSucceeded(originalRequest, options = {}) {
  const body = requestBody(options);
  const content = String(body.content || "").trim();
  const discussionId = String(
    options.body?.data?.relationships?.discussion?.data?.id ||
      body.discussionId ||
      body.discussion ||
      "",
  ).trim();

  if (!content || !discussionId) return Promise.resolve(null);

  return originalRequest({
    method: "GET",
    url: apiUrl(`/discussions/${discussionId}`),
  })
    .then((payload) => {
      const match = newestIncludedPost(payload, content);
      return match ? { ...payload, data: match } : null;
    })
    .catch(() => null);
}

function verifyWriteSucceededAfterServerError(originalRequest, options = {}) {
  if (requestMethod(options) !== "POST") return Promise.resolve(null);

  const url = String(options.url || "");

  if (/\/neoncube-private-messages\/messages(?:$|\?)/.test(url)) {
    return verifyMessageWriteSucceeded(originalRequest, options);
  }

  if (/\/discussions(?:$|\?)/.test(url)) {
    return verifyDiscussionWriteSucceeded(originalRequest, options);
  }

  if (/\/posts(?:$|\?)/.test(url)) {
    return verifyPostWriteSucceeded(originalRequest, options);
  }

  return Promise.resolve(null);
}

function showRecoveredWriteSuccess() {
  try {
    app.alerts?.show?.(
      { type: "success" },
      forumTrans("common", "recoveredWriteSuccess"),
    );
  } catch (_error) {
    // Recovery must never create another failure.
  }
}

function installConsoleNoiseFilter() {
  if (typeof window === "undefined") return;
  if (window.__cirklaConsoleNoiseFilterInstalled) return;

  ["log", "info", "debug"].forEach((methodName) => {
    const original = console[methodName];

    if (typeof original !== "function") return;

    console[methodName] = function (...args) {
      const message = args
        .map((arg) => String(arg))
        .join(" ")
        .trim();

      if (message === "fas fa-binoculars") {
        return;
      }

      return original.apply(console, args);
    };
  });

  window.__cirklaConsoleNoiseFilterInstalled = true;
}

installConsoleNoiseFilter();
// -----------------------------------------------------------------------------
// Global loading and alert policy
// -----------------------------------------------------------------------------

function safeCloseModal() {
  if (typeof app?.modal?.close !== "function") return;

  const hasModalDom =
    typeof document !== "undefined" &&
    !!document.querySelector(
      ".ModalManager .Modal, .ModalManager .modal-dialog, #modal .Modal, #modal .modal-dialog",
    );

  if (!hasModalDom) return;

  try {
    app.modal.close();
  } catch (_error) {
    // Flarum's ModalManager can briefly lose its root during route changes.
  }
}

function ensureInitialSessionLoader() {
  if (typeof document === "undefined") return;

  if (document.querySelector(".CirklaInitialSessionLoader")) return;

  const loader = document.createElement("div");
  loader.className = "CirklaInitialSessionLoader";
  loader.setAttribute("role", "status");
  loader.setAttribute("aria-live", "polite");

  const card = document.createElement("div");
  card.className = "CirklaInitialSessionLoader-card";
  const spinner = document.createElement("div");
  spinner.className = "CirklaInitialSessionLoader-spinner";
  spinner.setAttribute("aria-hidden", "true");
  spinner.innerHTML = sessionLoaderSvg();
  const text = document.createElement("div");
  text.className = "CirklaInitialSessionLoader-text";
  text.textContent = forumTrans("accessGate", "loading");
  card.append(spinner, text);
  loader.append(card);

  startSessionLoader(loader);
  document.body.appendChild(loader);
}

function removeInitialSessionLoader() {
  if (typeof document === "undefined") return;

  finishSessionLoader();
}

function setGlobalLoadingState(isLoading) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("CirklaNetworkLoading", isLoading);
  setSessionLoaderPending(isLoading);
}

function setInitialSessionBooting(isBooting) {
  if (typeof document === "undefined") return;

  if (isBooting) {
    document.body.classList.add("CirklaSessionBooting");
    ensureInitialSessionLoader();
  } else {
    removeInitialSessionLoader();
  }
}

function beginInitialSessionBoot() {
  if (app.__cirklaInitialSessionBootStarted) return;
  app.__cirklaInitialSessionBootStarted = true;
  setInitialSessionBooting(true);
}

function endInitialSessionBootSoon() {
  setInitialSessionBooting(false);
}

/**
 * Adds a single global network-loading class while Flarum requests are pending.
 * The installer is guarded because this file can run multiple route-level passes.
 */
function installGlobalRequestLoader() {
  if (app.__cirklaRequestLoaderInstalled || typeof app.request !== "function")
    return;

  const originalRequest = app.request.bind(app);
  app.request = function (options = {}) {
    const isBackgroundRequest = options?.background === true;
    const dedupeKey = requestDedupeKey(options);
    const inFlight = dedupeKey ? writeRequestPromises.get(dedupeKey) : null;

    if (inFlight) {
      return inFlight.promise;
    }

    if (!isBackgroundRequest) {
      pendingRequests += 1;
      setGlobalLoadingState(true);
    }

    const requestPromise = Promise.resolve(
      originalRequest.apply(this, arguments),
    )
      .catch((error) => {
        const status = requestStatus(error);

        // The server-side session can expire while the private-messages chat
        // is still open (cookie expiry, server restart, etc). The client
        // still thinks a user is logged in, so the chat just keeps retrying
        // its own message fetch and failing with 403 forever instead of
        // prompting the user to sign back in. This is the one endpoint a
        // conversation participant should never get 403 from while actually
        // authenticated, so treat it as an expired-session signal.
        if (
          status === 403 &&
          typeof options?.url === "string" &&
          options.url.includes("/neoncube-private-messages/messages") &&
          app.session?.user
        ) {
          handleExpiredSessionDetected();
        }

        if (status < 500 || status >= 600) {
          throw error;
        }

        return verifyWriteSucceededAfterServerError(
          originalRequest,
          options,
        ).then((verifiedPayload) => {
          if (!verifiedPayload) {
            throw error;
          }

          showRecoveredWriteSuccess();
          return verifiedPayload;
        });
      })
      .finally(() => {
        if (dedupeKey) {
          window.setTimeout(() => {
            const current = writeRequestPromises.get(dedupeKey);

            if (current?.promise === requestPromise) {
              writeRequestPromises.delete(dedupeKey);
            }
          }, WRITE_DEDUPE_WINDOW_MS);
        }

        if (!isBackgroundRequest) {
          pendingRequests = Math.max(0, pendingRequests - 1);
          setGlobalLoadingState(pendingRequests > 0);
        }
      });

    if (dedupeKey) {
      writeRequestPromises.set(dedupeKey, {
        promise: requestPromise,
        createdAt: Date.now(),
      });
    }

    return requestPromise;
  };

  app.__cirklaRequestLoaderInstalled = true;
}

/**
 * Keeps alert UX calm by showing only one alert at a time and suppressing
 * identical alerts that arrive within a short debounce window.
 */
function installSingleAlertPolicy() {
  if (
    app.__cirklaSingleAlertPolicyInstalled ||
    !app.alerts ||
    typeof app.alerts.show !== "function"
  ) {
    return;
  }

  const originalShow = app.alerts.show.bind(app.alerts);
  app.alerts.show = function () {
    const signature = Array.from(arguments)
      .map((value) => {
        if (typeof value === "string") return value;
        if (value?.content) return String(value.content);
        if (value?.children) return String(value.children);
        return "";
      })
      .join("|")
      .replace(/\s+/g, " ")
      .trim();
    const now = Date.now();

    if (
      signature &&
      signature === lastAlertSignature &&
      now - lastAlertShownAt < ALERT_DEDUPE_WINDOW_MS
    ) {
      return null;
    }

    lastAlertSignature = signature;
    lastAlertShownAt = now;

    if (typeof app.alerts.clear === "function") {
      app.alerts.clear();
    }

    return originalShow.apply(this, arguments);
  };

  app.__cirklaSingleAlertPolicyInstalled = true;
}

// -----------------------------------------------------------------------------
// Post content enhancements
// -----------------------------------------------------------------------------

/**
 * GDPR-compliant 2-click gate for third-party video embeds (YouTube, Vimeo,
 * Panopto). Appends a data-transfer disclosure and an explicit "activate"
 * button under an existing preview element; the embed is only requested from
 * the third-party provider once that button is clicked, never on a click
 * anywhere else in the card/thumbnail. No consent is remembered across
 * embeds or sessions - every embed always shows its own disclosure + button.
 */
function appendExternalEmbedConsentGate(
  container,
  { noticeText, buttonLabel, onActivate },
) {
  const notice = document.createElement("div");
  notice.className = "CirklaExternalEmbedNotice";

  const text = document.createElement("p");
  text.className = "CirklaExternalEmbedNotice-text";
  text.textContent = noticeText;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "CirklaExternalEmbedNotice-button";
  button.textContent = buttonLabel;
  button.addEventListener(
    "click",
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onActivate();
    },
    { once: true },
  );

  notice.append(text, button);
  container.appendChild(notice);
  return notice;
}

/**
 * Confirmation popup shown before navigating away to an outbound link
 * (website/document/other-provider link card). Same message family as the
 * inline video-embed consent notice, just presented as a modal with an
 * explicit stay/continue choice instead of an always-visible inline box.
 */
class CirklaExternalLinkModal extends Modal {
  className() {
    return "Modal--small CirklaExternalLinkModal";
  }

  title() {
    return forumTrans("media", "externalLinkModalTitle");
  }

  content() {
    const { host } = this.attrs;

    return m("div", { className: "Modal-body CirklaExternalLinkModal-body" }, [
      m(
        "p",
        { className: "CirklaExternalLinkModal-text" },
        forumTrans("media", "externalLinkNotice", { host }),
      ),
      m("div", { className: "CirklaExternalLinkModal-actions" }, [
        m(
          Button,
          {
            className: "Button CirklaExternalLinkModal-cancel",
            onclick: () => this.hide(),
          },
          forumTrans("media", "stayOnSite"),
        ),
        m(
          Button,
          {
            className: "Button Button--primary CirklaExternalLinkModal-continue",
            onclick: () => {
              this.hide();
              this.attrs.onConfirm();
            },
          },
          forumTrans("media", "openExternalLink"),
        ),
      ]),
    ]);
  }
}

CirklaExternalLinkModal.isDismissibleViaBackdropClick = true;
CirklaExternalLinkModal.isDismissibleViaEscKey = true;

/**
 * Shows the leaving-site confirmation popup for a plain outbound navigation
 * (website/document/other-provider link card) - no data-transfer claim like
 * the video-embed notice, just an explicit confirm-or-stay choice before we
 * open the link in a new tab.
 */
function confirmExternalLinkNavigation({ url, onConfirm }) {
  const host = urlHostLabel(url) || String(forumTrans("media", "externalLink"));

  app.modal.show(CirklaExternalLinkModal, { host, onConfirm });
}

/**
 * Converts Panopto viewer links inside post bodies into lazy, in-place embeds.
 */
function enhancePanoptoEmbeds() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      ".Post-body a[href*='video.htwsaar.de/Panopto/Pages/Viewer.aspx']",
    )
    .forEach((link) => {
      if (link.dataset.cirklaPanoptoDone === "1") return;
      link.dataset.cirklaPanoptoDone = "1";

      let viewerUrl;
      try {
        viewerUrl = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }

      const videoId = viewerUrl.searchParams.get("id");
      if (!videoId) return;

      const embedUrl = `https://video.htwsaar.de/Panopto/Pages/Embed.aspx?id=${encodeURIComponent(videoId)}&v=1`;
      const wrapper = document.createElement("div");
      wrapper.className = "CirklaPanoptoEmbed";
      const title =
        String(link.textContent || "").trim() ||
        forumTrans("media", "playVideo");
      const openEmbed = () => {
        const iframe = document.createElement("iframe");
        iframe.src = embedUrl;
        iframe.loading = "lazy";
        iframe.allowFullscreen = true;
        iframe.title = forumTrans("media", "panoptoVideo");
        wrapper.replaceChildren(iframe);
      };

      const preview = document.createElement("div");
      preview.className = "CirklaPanoptoEmbed-preview";
      const badge = document.createElement("span");
      badge.className = "CirklaPanoptoEmbed-badge";
      badge.textContent = forumTrans("media", "video");
      const play = document.createElement("span");
      play.className = "CirklaPanoptoEmbed-play";
      play.setAttribute("aria-hidden", "true");
      play.textContent = "▶";
      const titleNode = document.createElement("span");
      titleNode.className = "CirklaPanoptoEmbed-title";
      titleNode.textContent = title;
      const meta = document.createElement("span");
      meta.className = "CirklaPanoptoEmbed-meta";
      meta.textContent = forumTrans("media", "openPanopto");
      preview.append(badge, play, titleNode, meta);
      wrapper.append(preview);

      appendExternalEmbedConsentGate(wrapper, {
        noticeText: String(
          forumTrans("media", "externalMediaNotice", { provider: "Panopto" }),
        ),
        buttonLabel: String(forumTrans("media", "activateOnce")),
        onActivate: openEmbed,
      });

      const anchorBlock = link.closest("p, div, li") || link;
      anchorBlock.insertAdjacentElement("afterend", wrapper);
    });
}

// -----------------------------------------------------------------------------
// Link and image previews inside posts
// -----------------------------------------------------------------------------

const CIRKLA_IMAGE_URL_PATTERN = /\.(png|jpe?g|gif|webp|avif|svg)(?:[?#].*)?$/i;
const CIRKLA_VIDEO_FILE_URL_PATTERN = /\.(mp4|webm|ogg|ogv|m4v)(?:[?#].*)?$/i;
const CIRKLA_DOCUMENT_URL_PATTERN =
  /\.(pdf|docx?|odt|rtf|xlsx?|ods|csv|pptx?|odp)(?:[?#].*)?$/i;

function isDirectImageUrl(url) {
  return CIRKLA_IMAGE_URL_PATTERN.test(String(url || ""));
}

function isDirectVideoUrl(url) {
  return CIRKLA_VIDEO_FILE_URL_PATTERN.test(String(url || ""));
}

function documentFileTypeFromUrl(url) {
  const match = String(url || "").match(CIRKLA_DOCUMENT_URL_PATTERN);
  const ext = match ? String(match[1] || "").toLowerCase() : "";

  if (ext === "pdf") return "pdf";
  if (["doc", "docx", "odt", "rtf"].includes(ext)) return "document";
  if (["xls", "xlsx", "ods", "csv"].includes(ext)) return "spreadsheet";
  if (["ppt", "pptx", "odp"].includes(ext)) return "presentation";

  return "";
}

function documentLabelForType(type) {
  if (type === "pdf") return forumTrans("media", "pdfDocument");
  if (type === "spreadsheet") return forumTrans("media", "spreadsheet");
  if (type === "presentation") return forumTrans("media", "presentation");
  if (type === "document") return forumTrans("media", "document");
  return forumTrans("media", "file");
}

function isProbablyUrlText(value) {
  return /^(https?:\/\/|www\.)/i.test(String(value || "").trim());
}

function isInternalCirklaUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch (_error) {
    return false;
  }
}

function urlHostLabel(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname.replace(/^www\./i, "");
  } catch (_error) {
    return "";
  }
}

function normalizePreviewUrl(value) {
  try {
    const url = new URL(value, window.location.origin);
    url.hash = "";
    return url.href;
  } catch (_error) {
    return String(value || "").trim();
  }
}

function urlsMatchForPreview(a, b) {
  return normalizePreviewUrl(a) === normalizePreviewUrl(b);
}

function extractYouTubeVideoId(value) {
  try {
    const url = new URL(value, window.location.origin);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();

    if (host === "youtu.be") {
      return url.pathname.replace(/^\/+/, "").split("/")[0] || "";
    }

    if (
      host === "youtube.com" ||
      host === "m.youtube.com" ||
      host === "music.youtube.com"
    ) {
      if (url.pathname === "/watch") {
        return url.searchParams.get("v") || "";
      }

      const embedMatch = url.pathname.match(/^\/embed\/([^/?#]+)/i);
      if (embedMatch) return embedMatch[1];

      const shortsMatch = url.pathname.match(/^\/shorts\/([^/?#]+)/i);
      if (shortsMatch) return shortsMatch[1];
    }
  } catch (_error) {
    return "";
  }

  return "";
}

function extractVimeoVideoId(value) {
  try {
    const url = new URL(value, window.location.origin);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();

    if (host !== "vimeo.com" && host !== "player.vimeo.com") return "";

    const match = url.pathname.match(/\/(?:video\/)?(\d+)/);
    return match ? match[1] : "";
  } catch (_error) {
    return "";
  }
}

function videoProviderFromUrl(value) {
  const youtubeId = extractYouTubeVideoId(value);

  if (youtubeId) {
    return {
      type: "youtube",
      title: forumTrans("media", "youtubeVideo"),
      host: "youtube.com",
      thumbnailUrl: `https://img.youtube.com/vi/${encodeURIComponent(youtubeId)}/hqdefault.jpg`,
      openUrl: value,
    };
  }

  const vimeoId = extractVimeoVideoId(value);

  if (vimeoId) {
    return {
      type: "vimeo",
      title: forumTrans("media", "vimeoVideo"),
      host: "vimeo.com",
      thumbnailUrl: "",
      openUrl: value,
    };
  }

  return null;
}

function postAlreadyHasCirklaPreviewForUrl(link, url) {
  const postBody = link.closest(".Post-body");
  if (!postBody) return false;

  const normalizedUrl = normalizePreviewUrl(url);

  return Array.from(postBody.querySelectorAll(".CirklaLinkPreview")).some(
    (preview) => {
      const previewUrl = preview.getAttribute("data-cirkla-preview-url");

      return previewUrl && urlsMatchForPreview(previewUrl, normalizedUrl);
    },
  );
}

function linkPreviewSourceBlock(link) {
  const postBody = link.closest(".Post-body");
  const block = link.closest("p, li, blockquote");

  if (block && (!postBody || postBody.contains(block))) {
    return block;
  }

  const parent = link.parentElement;

  if (
    parent &&
    parent !== postBody &&
    (!postBody || postBody.contains(parent))
  ) {
    return parent;
  }

  return link;
}

function hideStandaloneLinkSourceForPreview(sourceBlock, link) {
  const target = link;

  if (!(target instanceof HTMLElement)) return;

  target.classList.add("CirklaLinkPreview-sourceHidden");
  target.setAttribute("aria-hidden", "true");
  target.dataset.cirklaLinkPreviewSourceHidden = "1";
}

function insertPostLinkPreviewCard(link, sourceBlock, card, isStandaloneLink) {
  if (!isStandaloneLink) {
    link.replaceWith(card);
    return;
  }

  sourceBlock.insertAdjacentElement("afterend", card);
}

function createImagePreviewCard(link, url) {
  const wrapper = document.createElement("div");
  wrapper.className = "CirklaLinkPreview CirklaLinkPreview--image";
  wrapper.dataset.cirklaPreviewUrl = normalizePreviewUrl(url);

  const image = document.createElement("img");
  image.className = "CirklaLinkPreview-image";
  image.src = url;
  image.alt =
    String(link?.textContent || link?.alt || "").trim() ||
    forumTrans("media", "imagePreview");
  image.loading = "lazy";
  image.referrerPolicy = "no-referrer";

  wrapper.append(image);

  wrapper.addEventListener("click", () => {
    window.open(url, "_blank", "noopener,noreferrer");
  });

  return wrapper;
}

function createDirectVideoPreviewCard(link, url) {
  const wrapper = document.createElement("div");
  wrapper.className = "CirklaLinkPreview CirklaLinkPreview--video";
  wrapper.dataset.cirklaPreviewUrl = normalizePreviewUrl(url);

  const video = document.createElement("video");
  video.className = "CirklaLinkPreview-video";
  video.src = url;
  video.controls = true;
  video.preload = "metadata";

  wrapper.append(video);

  return wrapper;
}

function createProviderVideoPreviewCard(link, url, provider) {
  const label = String(link?.textContent || "").trim();

  const wrapper = document.createElement("div");
  wrapper.className = `CirklaLinkPreview CirklaLinkPreview--video CirklaLinkPreview--${provider.type}`;
  wrapper.dataset.cirklaPreviewUrl = normalizePreviewUrl(url);
  wrapper.setAttribute("role", "button");
  wrapper.tabIndex = 0;

  const thumb = document.createElement("div");
  thumb.className = "CirklaLinkPreview-videoThumb";

  if (provider.thumbnailUrl) {
    thumb.style.backgroundImage = `url("${provider.thumbnailUrl}")`;
  }

  const play = document.createElement("span");
  play.className = "CirklaLinkPreview-videoPlay";
  play.innerHTML = '<i class="fas fa-play" aria-hidden="true"></i>';

  const text = document.createElement("div");
  text.className = "CirklaLinkPreview-videoText";

  const title = document.createElement("div");
  title.className = "CirklaLinkPreview-videoTitle";
  title.textContent =
    label && !isProbablyUrlText(label)
      ? label
      : provider.title || forumTrans("media", "video");

  const meta = document.createElement("div");
  meta.className = "CirklaLinkPreview-videoMeta";
  meta.textContent =
    provider.host || urlHostLabel(url) || forumTrans("media", "video");

  text.append(title, meta);
  thumb.append(play, text);
  wrapper.append(thumb);

  const openTarget = provider.openUrl || url;
  const requestOpen = () => {
    confirmExternalLinkNavigation({
      url: openTarget,
      onConfirm: () => {
        window.open(openTarget, "_blank", "noopener,noreferrer");
      },
    });
  };

  wrapper.addEventListener("click", requestOpen);
  wrapper.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      requestOpen();
    }
  });

  return wrapper;
}

function createWebsitePreviewCard(link, url) {
  const host = urlHostLabel(url);
  const label = String(link.textContent || "").trim();

  const wrapper = document.createElement("div");
  wrapper.className = "CirklaLinkPreview CirklaLinkPreview--website";
  wrapper.dataset.cirklaPreviewUrl = normalizePreviewUrl(url);
  wrapper.setAttribute("role", "button");
  wrapper.tabIndex = 0;

  const iconBox = document.createElement("div");
  iconBox.className = "CirklaLinkPreview-siteIcon";
  iconBox.innerHTML = '<i class="fas fa-link" aria-hidden="true"></i>';

  const body = document.createElement("div");
  body.className = "CirklaLinkPreview-body";

  const title = document.createElement("div");
  title.className = "CirklaLinkPreview-title";
  title.textContent =
    label && !isProbablyUrlText(label)
      ? label
      : host
        ? forumTrans("media", "openHost", { host })
        : forumTrans("media", "openWebsite");

  const meta = document.createElement("div");
  meta.className = "CirklaLinkPreview-meta";
  meta.textContent = host || forumTrans("media", "externalLink");

  body.append(title, meta);

  const action = document.createElement("div");
  action.className = "CirklaLinkPreview-action";
  action.innerHTML =
    '<i class="fas fa-external-link-alt" aria-hidden="true"></i>';

  wrapper.append(iconBox, body, action);

  const requestOpen = () => {
    confirmExternalLinkNavigation({
      url,
      onConfirm: () => {
        window.open(url, "_blank", "noopener,noreferrer");
      },
    });
  };

  wrapper.addEventListener("click", requestOpen);
  wrapper.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      requestOpen();
    }
  });

  return wrapper;
}

function createDocumentPreviewCard(link, url, documentType) {
  const label = String(link.textContent || "").trim();
  const host = urlHostLabel(url);
  const typeLabel = documentLabelForType(documentType);

  const wrapper = document.createElement("div");
  wrapper.className = `CirklaLinkPreview CirklaLinkPreview--document CirklaLinkPreview--document-${documentType || "file"}`;
  wrapper.dataset.cirklaPreviewUrl = normalizePreviewUrl(url);

  const summary = document.createElement("button");
  summary.type = "button";
  summary.className = "CirklaLinkPreview-documentSummary";
  summary.setAttribute(
    "aria-label",
    String(forumTrans("media", "openInNewTab", { type: typeLabel })),
  );

  const iconBox = document.createElement("span");
  iconBox.className =
    "CirklaLinkPreview-siteIcon CirklaLinkPreview-documentIcon";
  iconBox.innerHTML =
    documentType === "pdf"
      ? '<i class="fas fa-file-pdf" aria-hidden="true"></i>'
      : '<i class="fas fa-file-alt" aria-hidden="true"></i>';

  const body = document.createElement("span");
  body.className = "CirklaLinkPreview-body";

  const title = document.createElement("span");
  title.className = "CirklaLinkPreview-title";
  title.textContent =
    label && !isProbablyUrlText(label)
      ? label
      : host
        ? String(forumTrans("media", "documentFromHost", { type: typeLabel, host }))
        : typeLabel;

  const meta = document.createElement("span");
  meta.className = "CirklaLinkPreview-meta";
  meta.textContent = host ? `${typeLabel} · ${host}` : typeLabel;

  body.append(title, meta);

  const action = document.createElement("span");
  action.className = "CirklaLinkPreview-action";
  action.innerHTML =
    '<i class="fas fa-external-link-alt" aria-hidden="true"></i>';

  summary.append(iconBox, body, action);
  summary.addEventListener("click", () => {
    confirmExternalLinkNavigation({
      url,
      onConfirm: () => {
        window.open(url, "_blank", "noopener,noreferrer");
      },
    });
  });

  wrapper.append(summary);

  return wrapper;
}

function enhanceNativeImagePreviews() {
  if (typeof document === "undefined") return;

  document.querySelectorAll(".Post-body img[src]").forEach((img) => {
    if (!(img instanceof HTMLImageElement)) return;
    if (img.closest(".CirklaLinkPreview")) return;
    if (img.dataset.cirklaNativeImagePreviewDone === "1") return;

    const url = String(
      img.currentSrc || img.src || img.getAttribute("src") || "",
    ).trim();
    if (!url) return;

    img.dataset.cirklaNativeImagePreviewDone = "1";

    const postBody = img.closest(".Post-body");
    const alreadyExists = Array.from(
      postBody?.querySelectorAll(".CirklaLinkPreview") || [],
    ).some((preview) =>
      urlsMatchForPreview(
        preview.getAttribute("data-cirkla-preview-url") || "",
        url,
      ),
    );

    const sourceBlock = linkPreviewSourceBlock(img);

    if (alreadyExists) {
      hideStandaloneLinkSourceForPreview(sourceBlock, img);
      return;
    }

    const card = createImagePreviewCard(img, url);
    sourceBlock.insertAdjacentElement("afterend", card);
    hideStandaloneLinkSourceForPreview(sourceBlock, img);
  });
}

/**
 * Flarum core auto-embeds YouTube/Vimeo links as raw cross-origin iframes
 * (s9e mediaembed) before our post-render enhancers ever see a plain <a>
 * link to intercept. Those iframes render each provider's own busy chrome
 * (title, channel, description) that we cannot restyle, which looks
 * inconsistent with our other CirklaLinkPreview cards. Replace them with a
 * styled click-to-play card that matches the rest of the preview family;
 * the original embed is swapped back in (and actually plays) on click.
 */
function enhancePostMediaEmbeds() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(".Post-body [data-s9e-mediaembed]")
    .forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      if (wrapper.dataset.cirklaMediaEmbedDone === "1") return;

      const providerAttr = String(
        wrapper.getAttribute("data-s9e-mediaembed") || "",
      ).toLowerCase();
      if (providerAttr !== "youtube" && providerAttr !== "vimeo") return;

      const iframe = wrapper.querySelector("iframe");
      if (!iframe) return;

      wrapper.dataset.cirklaMediaEmbedDone = "1";

      const embedSrc = String(iframe.src || iframe.getAttribute("src") || "");
      let thumbnailUrl = "";

      if (providerAttr === "youtube") {
        const idMatch = embedSrc.match(/embed\/([a-zA-Z0-9_-]{6,})/);
        const videoId = idMatch ? idMatch[1] : "";
        if (videoId) {
          thumbnailUrl = `https://img.youtube.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
        }
      }

      const card = document.createElement("div");
      card.className = `CirklaLinkPreview CirklaLinkPreview--video CirklaLinkPreview--${providerAttr}`;

      const thumb = document.createElement("div");
      thumb.className = "CirklaLinkPreview-videoThumb";
      if (thumbnailUrl) {
        thumb.style.backgroundImage = `url("${thumbnailUrl}")`;
      }

      const play = document.createElement("span");
      play.className = "CirklaLinkPreview-videoPlay";
      play.innerHTML = '<i class="fas fa-play" aria-hidden="true"></i>';

      const text = document.createElement("div");
      text.className = "CirklaLinkPreview-videoText";

      const title = document.createElement("div");
      title.className = "CirklaLinkPreview-videoTitle";
      title.textContent = String(
        forumTrans(
          "media",
          providerAttr === "youtube" ? "youtubeVideo" : "vimeoVideo",
        ),
      );

      const meta = document.createElement("div");
      meta.className = "CirklaLinkPreview-videoMeta";
      meta.textContent = providerAttr === "youtube" ? "youtube.com" : "vimeo.com";

      text.append(title, meta);
      thumb.append(play, text);
      card.append(thumb);

      const playEmbed = () => {
        card.replaceWith(wrapper);
      };

      appendExternalEmbedConsentGate(card, {
        noticeText: String(
          forumTrans("media", "externalMediaNotice", {
            provider: providerAttr === "youtube" ? "YouTube" : "Vimeo",
          }),
        ),
        buttonLabel: String(forumTrans("media", "activateOnce")),
        onActivate: playEmbed,
      });

      wrapper.replaceWith(card);
    });
}

// Delegate to post text links so newly rendered posts and retained document
// URLs get the same confirmation as cards, without binding duplicate handlers.
function installPostTextLinkConfirmation() {
  if (app.__cirklaPostTextLinkConfirmationInstalled) return;
  app.__cirklaPostTextLinkConfirmationInstalled = true;

  const requestOpen = (event) => {
    if (event.defaultPrevented) return;
    if (event.type === "auxclick" ? event.button !== 1 : event.button !== 0) return;
    const link = event.target instanceof Element
      ? event.target.closest(".Post-body a[href]")
      : null;
    if (!link || link.closest(".CirklaLinkPreview")) return;
    if (link.querySelector("img, video, audio, iframe")) return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (_error) {
      return;
    }
    if (!/^https?:$/.test(url.protocol) || isInternalCirklaUrl(url.href)) return;

    event.preventDefault();
    event.stopPropagation();
    confirmExternalLinkNavigation({
      url: url.href,
      onConfirm: () => window.open(url.href, "_blank", "noopener,noreferrer"),
    });
  };

  document.addEventListener("click", requestOpen, true);
  document.addEventListener("auxclick", requestOpen, true);
}

function enhancePostLinkPreviews() {
  if (typeof document === "undefined") return;
  installPostTextLinkConfirmation();

  document.querySelectorAll(".Post-body a[href]").forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.dataset.cirklaLinkPreviewDone === "1") return;

    const url = String(link.href || "").trim();
    if (!url) return;

    const rawText = String(link.textContent || "").trim();

    const isImageUrl = isDirectImageUrl(url);
    const isVideoFileUrl = isDirectVideoUrl(url);
    const documentType = documentFileTypeFromUrl(url);
    const videoProvider = videoProviderFromUrl(url);

    /*
     * Skip internal forum pages, but do NOT skip internal media files.
     */
    if (
      isInternalCirklaUrl(url) &&
      !isImageUrl &&
      !isVideoFileUrl &&
      !documentType &&
      !videoProvider
    ) {
      return;
    }

    /*
     * Panopto is handled by enhancePanoptoEmbeds().
     */
    if (url.includes("video.htwsaar.de/Panopto/Pages/Viewer.aspx")) return;

    const sourceBlock = linkPreviewSourceBlock(link);
    const sourceText = String(sourceBlock?.textContent || "").trim();

    const isStandaloneLink =
      sourceText === rawText ||
      sourceText === url ||
      rawText === url ||
      (!rawText && !sourceText);

    link.dataset.cirklaLinkPreviewDone = "1";

    if (postAlreadyHasCirklaPreviewForUrl(link, url)) {
      if (isStandaloneLink)
        hideStandaloneLinkSourceForPreview(sourceBlock, link);
      return;
    }

    const card = videoProvider
      ? createProviderVideoPreviewCard(link, url, videoProvider)
      : isVideoFileUrl
        ? createDirectVideoPreviewCard(link, url)
        : isImageUrl
          ? createImagePreviewCard(link, url)
          : documentType
            ? createDocumentPreviewCard(link, url, documentType)
            : createWebsitePreviewCard(link, url);

    insertPostLinkPreviewCard(link, sourceBlock, card, isStandaloneLink);
    if (isStandaloneLink && !documentType) {
      hideStandaloneLinkSourceForPreview(sourceBlock, link);
    }
  });

  enhanceNativeImagePreviews();
}

// -----------------------------------------------------------------------------
// Dropdown and mobile drawer stability
// -----------------------------------------------------------------------------

function stabilizeOpenDropdowns() {
  if (typeof document === "undefined") return;

  const menus = document.querySelectorAll(
    ".App-header .item-session .Dropdown-menu",
  );

  menus.forEach((menu) => {
    if (!(menu instanceof HTMLElement)) return;
    if (!menu.getClientRects().length) return;

    const dropdown = menu.closest(".Dropdown") || menu.closest(".item-session");
    const directToggle =
      dropdown?.querySelector(":scope > .Dropdown-toggle") ||
      menu.previousElementSibling;
    const toggle =
      directToggle instanceof HTMLElement &&
      directToggle.matches(".Dropdown-toggle, .Button--icon, .Button")
        ? directToggle
        : dropdown?.querySelector(".Dropdown-toggle");

    if (!menu || !toggle) return;

    // Let the hamburger drawer use a normal stacked menu instead of
    // desktop-style viewport repositioning.
    if (window.innerWidth <= 767 && dropdown.closest(".App-drawer")) {
      menu.style.position = "";
      menu.style.left = "";
      menu.style.top = "";
      menu.style.right = "";
      menu.style.zIndex = "";
      menu.style.maxHeight = "";
      menu.style.overflowY = "";
      return;
    }

    const toggleRect = toggle.getBoundingClientRect();
    const wasHidden = menu.style.display === "none";

    if (wasHidden) {
      menu.style.display = "block";
    }

    const menuRect = menu.getBoundingClientRect();
    const menuWidth = Math.max(menuRect.width || 0, 220);
    const menuHeight = Math.max(menuRect.height || 0, 120);
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;

    let left = toggleRect.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > viewportWidth - 8)
      left = Math.max(8, viewportWidth - menuWidth - 8);

    let top = toggleRect.bottom + 8;
    let bottom = "auto";
    if (top + menuHeight > viewportHeight - 8) {
      const upwardTop = Math.max(8, toggleRect.top - menuHeight - 8);

      if (upwardTop + menuHeight <= viewportHeight - 8) {
        top = upwardTop;
      } else {
        top = Math.max(8, viewportHeight - menuHeight - 8);
      }
    }

    menu.style.position = "fixed";
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
    menu.style.bottom = bottom;
    menu.style.right = "auto";
    menu.style.zIndex = "24000";
    menu.style.maxHeight = `${Math.max(160, viewportHeight - 16)}px`;
    menu.style.overflowY = "auto";

    if (wasHidden) {
      menu.style.display = "";
    }
  });
}

/**
 * Keeps account dropdowns inside the viewport on desktop while leaving the
 * mobile drawer menu layout untouched.
 */
function installDropdownViewportFix() {
  if (typeof document === "undefined" || app.__cirklaDropdownFixInstalled)
    return;

  const schedule = () => {
    window.requestAnimationFrame(() => stabilizeOpenDropdowns());
  };

  document.addEventListener("click", schedule, true);
  document.addEventListener("keyup", schedule, true);
  window.addEventListener("resize", schedule, { passive: true });
  window.addEventListener("scroll", schedule, { passive: true });

  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver(() => schedule());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  app.__cirklaDropdownFixInstalled = true;
  schedule();
}

function closeContextualDropdownsOnScroll() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      [
        ".DiscussionListItem-controls .Dropdown.open",
        ".Post-controls .Dropdown.open",
        ".UserCard-controls .Dropdown.open",
      ].join(","),
    )
    .forEach((dropdown) => {
      const toggle = dropdown.querySelector(":scope > .Dropdown-toggle");
      if (toggle instanceof HTMLElement) {
        toggle.click();
      }
    });
}

function installContextDropdownScrollCloser() {
  if (
    typeof window === "undefined" ||
    app.__cirklaContextDropdownScrollCloserInstalled
  )
    return;

  const closeMenus = () => {
    window.requestAnimationFrame(() => closeContextualDropdownsOnScroll());
  };

  window.addEventListener("scroll", closeMenus, { passive: true });
  app.__cirklaContextDropdownScrollCloserInstalled = true;
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia
    ? window.matchMedia("(max-width: 767px)").matches
    : window.innerWidth <= 767;
}

let mobileDrawerOpenRequested = false;

function drawerIsOpen() {
  if (typeof document === "undefined") return false;

  const appRoot = document.getElementById("app");
  const drawer = document.querySelector(".App-drawer");
  const hasOpenClass = Boolean(
    appRoot?.classList.contains("drawerOpen") ||
    appRoot?.classList.contains("paneShowing") ||
    document.body.classList.contains("drawerOpen") ||
    document.body.classList.contains("paneShowing"),
  );
  const hasExpandedToggle = Boolean(
    document.querySelector(".App-navigation [aria-expanded='true']"),
  );
  const hasBackdrop = Boolean(document.querySelector(".drawer-backdrop"));

  if (!(drawer instanceof HTMLElement)) {
    return hasOpenClass || hasExpandedToggle || hasBackdrop;
  }

  if (hasOpenClass || hasExpandedToggle || hasBackdrop) {
    return true;
  }

  const styles = window.getComputedStyle(drawer);
  const rect = drawer.getBoundingClientRect();
  const isVisible =
    styles.visibility !== "hidden" &&
    styles.pointerEvents !== "none" &&
    rect.width > 0 &&
    rect.height > 0 &&
    rect.right > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth);

  return isVisible;
}

function mobileDrawerToggleFromEvent(event) {
  if (typeof document === "undefined" || !event?.target) return null;

  const target =
    event.target instanceof Element
      ? event.target
      : event.target?.parentElement;
  if (!target) return null;

  const toggle = target.closest(
    [
      ".App-navigation .Navigation-drawer",
      ".App-navigation [aria-label='Open Navigation Drawer']",
      ".App-navigation [class*='drawer']",
      ".App-navigation [class*='Drawer']",
      ".App-navigation .App-titleControl.item-nav .Dropdown-toggle",
      ".App-navigation .item-nav .Dropdown-toggle",
      ".App-navigation [aria-label*='navigation']",
      ".App-navigation [aria-label*='Navigation']",
      ".App-navigation [aria-label*='menu']",
      ".App-navigation [aria-label*='Menu']",
    ].join(","),
  );

  if (toggle instanceof HTMLElement) return toggle;

  const barsIcon = target.closest(".fa-bars, .icon-bars, [class*='bars']");
  const barsToggle = barsIcon?.closest("button, .Button, a");

  return barsToggle instanceof HTMLElement ? barsToggle : null;
}

function removeDrawerBackdrops({ keepOne = false } = {}) {
  if (typeof document === "undefined") return;

  const backdrops = Array.from(document.querySelectorAll(".drawer-backdrop"));
  const keeper = keepOne ? backdrops[backdrops.length - 1] : null;

  backdrops.forEach((backdrop) => {
    if (backdrop !== keeper) backdrop.remove();
  });
}

function ensureMobileDrawerBackdrop() {
  if (typeof document === "undefined") return;

  if (document.querySelector(".drawer-backdrop")) return;

  const backdrop = document.createElement("div");
  backdrop.className = "drawer-backdrop fade in";
  document.body.appendChild(backdrop);
}

function openMobileDrawer(toggle = null) {
  if (typeof document === "undefined") return;

  mobileDrawerOpenRequested = true;

  const appRoot = document.getElementById("app");
  appRoot?.classList.add("drawerOpen");
  appRoot?.classList.remove("paneShowing");
  document.body.classList.add("drawerOpen");
  document.body.classList.remove("paneShowing");

  if (toggle instanceof HTMLElement) {
    toggle.setAttribute("aria-expanded", "true");
  }

  ensureMobileDrawerBackdrop();
  removeDrawerBackdrops({ keepOne: true });
}

function closeDrawerDropdowns() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      ".App-drawer .Dropdown.open, .App-drawer .dropdown.open, .App-drawer .Dropdown.show, .App-drawer .dropdown.show",
    )
    .forEach((dropdown) => dropdown.classList.remove("open", "show"));

  document
    .querySelectorAll(".App-drawer [aria-expanded='true']")
    .forEach((toggle) => toggle.setAttribute("aria-expanded", "false"));
}

function clearMobileScreenOverlayResidue() {
  if (typeof document === "undefined") return;

  mobileDrawerOpenRequested = false;

  const appRoot = document.getElementById("app");
  appRoot?.classList.remove("drawerOpen", "paneShowing");
  document.body.classList.remove("drawerOpen", "paneShowing");
  closeDrawerDropdowns();
  removeDrawerBackdrops();

  document.querySelectorAll(".drawer-backdrop").forEach((backdrop) => {
    backdrop.remove();
  });

  // Only touch modal-related state/DOM when Flarum itself has no modal
  // open or opening (app.modal.modal is set on a requestAnimationFrame
  // AFTER backdropShown flips true in ModalManagerState.show(), so both
  // need checking - during that brief window `modal` is still null even
  // though a modal legitimately IS being opened). This function runs on
  // every route change (via the m.route.set patch below) - if it
  // unconditionally stripped `.Modal-backdrop`/`modal-open` any time it
  // ran, it would just as happily rip out a modal that a route change
  // legitimately just opened as it would genuine leftover drawer residue,
  // which desyncs Flarum's ModalManagerState the same way the guest-lock
  // heartbeat used to (see enforceGlobalGuestLock in cirkla-home.js).
  if (!app?.modal?.modal && !app?.modal?.backdropShown) {
    document.body.classList.remove("modal-open");
    document
      .querySelectorAll(".Modal-backdrop, .ModalManager-invisibleBackdrop")
      .forEach((backdrop) => backdrop.remove());
  }
}

function closeMobileDrawer() {
  if (typeof document === "undefined") return;

  mobileDrawerOpenRequested = false;

  const appRoot = document.getElementById("app");
  appRoot?.classList.remove("drawerOpen", "paneShowing");
  document.body.classList.remove("drawerOpen", "paneShowing");
  closeDrawerDropdowns();
  removeDrawerBackdrops();

  document
    .querySelectorAll(
      ".App-navigation .item-nav .Dropdown-toggle, .App-navigation [aria-expanded='true']",
    )
    .forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
    });
}

function normalizeMobileDrawerBackdrops() {
  if (!isMobileViewport()) {
    closeMobileDrawer();
    return;
  }

  const open = mobileDrawerOpenRequested || drawerIsOpen();

  if (open) {
    openMobileDrawer(
      document.querySelector(
        ".App-navigation .item-nav .Dropdown-toggle, .App-navigation [aria-expanded='true']",
      ),
    );
  } else if (
    document.getElementById("app")?.classList.contains("drawerOpen") ||
    document.body.classList.contains("drawerOpen") ||
    document.querySelector(".drawer-backdrop")
  ) {
    closeMobileDrawer();
  }

  removeDrawerBackdrops({ keepOne: open });
}

/**
 * Normalizes the mobile drawer state across Flarum classes, ARIA attributes,
 * and backdrop elements.
 */
function installMobileDrawerToggleGuard() {
  if (
    typeof document === "undefined" ||
    app.__cirklaMobileDrawerToggleGuardInstalled
  ) {
    return;
  }

  let lastDrawerPointerToggleAt = 0;

  const consumeEvent = (event) => {
    if (event?.cancelable !== false) event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  const handleDrawerToggle = (event) => {
    if (!isMobileViewport()) return;

    const toggle = mobileDrawerToggleFromEvent(event);
    if (!toggle) return;

    consumeEvent(event);
    lastDrawerPointerToggleAt = Date.now();

    if (mobileDrawerOpenRequested || drawerIsOpen()) {
      closeMobileDrawer();
      return;
    }

    openMobileDrawer(toggle);
  };

  document.addEventListener("pointerdown", handleDrawerToggle, true);

  document.addEventListener(
    "click",
    (event) => {
      if (!mobileDrawerToggleFromEvent(event)) return;

      if (Date.now() - lastDrawerPointerToggleAt < 450) {
        consumeEvent(event);
        return;
      }

      handleDrawerToggle(event);
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;
      if (!target?.closest(".drawer-backdrop")) return;

      event.preventDefault();
      event.stopPropagation();
      window.requestAnimationFrame(closeMobileDrawer);
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!isMobileViewport() || !drawerIsOpen()) return;

      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;
      if (!target) return;
      if (mobileDrawerToggleFromEvent(event)) return;
      if (target.closest(".App-drawer")) return;

      closeMobileDrawer();
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!isMobileViewport() || !drawerIsOpen()) return;

      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;
      if (!target?.closest(".App-drawer")) return;

      if (
        target.closest(
          [
            ".App-drawer .item-locale",
            ".App-drawer .item-language",
            ".App-drawer .item-languages",
            ".App-drawer [class*='item-locale']",
            ".App-drawer [class*='item-language']",
            ".App-drawer .LocaleDropdown",
            ".App-drawer .Dropdown--select",
          ].join(","),
        )
      ) {
        return;
      }

      const drawerHeaderAction = target.closest(
        [
          ".App-drawer .item-flags .Dropdown-toggle",
          ".App-drawer .item-notifications .Dropdown-toggle",
          ".App-drawer .item-session .Dropdown-toggle",
        ].join(","),
      );
      if (drawerHeaderAction) {
        window.requestAnimationFrame(closeMobileDrawer);
        return;
      }

      const searchResultAction = target.closest(
        [
          ".App-drawer .Search-results a[href]",
          ".App-drawer .Search-results li",
          ".App-drawer .Search a[href]",
        ].join(","),
      );
      if (searchResultAction) {
        window.requestAnimationFrame(clearMobileScreenOverlayResidue);
        setTimeout(clearMobileScreenOverlayResidue, 80);
        setTimeout(clearMobileScreenOverlayResidue, 240);
        return;
      }

      if (
        target.closest(
          [
            ".App-drawer .Dropdown-toggle",
            ".App-drawer input",
            ".App-drawer textarea",
            ".App-drawer select",
            ".App-drawer label",
            ".App-drawer .Search",
          ].join(","),
        )
      ) {
        return;
      }

      const action = target.closest(
        [
          ".App-drawer a[href]",
          ".App-drawer .Navigation-link",
          ".App-drawer .Dropdown-menu a",
        ].join(","),
      );
      if (!action) return;

      closeMobileDrawer();
      setTimeout(clearMobileScreenOverlayResidue, 80);
    },
    true,
  );

  window.addEventListener("resize", normalizeMobileDrawerBackdrops, {
    passive: true,
  });

  if (
    m?.route &&
    typeof m.route.set === "function" &&
    !app.__cirklaMobileDrawerRouteCleanupInstalled
  ) {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      clearMobileScreenOverlayResidue();
      setTimeout(clearMobileScreenOverlayResidue, 80);
      setTimeout(clearMobileScreenOverlayResidue, 240);
      return result;
    };
    app.__cirklaMobileDrawerRouteCleanupInstalled = true;
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => normalizeMobileDrawerBackdrops()).observe(
      document.body,
      {
        childList: true,
        subtree: false,
      },
    );
  }

  app.__cirklaMobileDrawerToggleGuardInstalled = true;
  normalizeMobileDrawerBackdrops();
}

function headerSearchItem() {
  const items = Array.from(
    document.querySelectorAll(".App-header .Header-controls .item-search"),
  );

  return (
    items.find((item) => !item.closest(".App-drawer")) ||
    items.find((item) => item instanceof HTMLElement) ||
    null
  );
}

let mobileSearchOriginalParent = null;
let mobileSearchOriginalNextSibling = null;

function mobileSearchMount() {
  let mount = document.querySelector(".CirklaMobileSearchMount");

  if (!(mount instanceof HTMLElement)) {
    mount = document.createElement("div");
    mount.className = "CirklaMobileSearchMount";
    document.body.appendChild(mount);
  }

  return mount;
}

function closeMobileSearchOverlay() {
  if (typeof document === "undefined") return;

  document.body.classList.remove("CirklaMobileSearchOpen");

  const mount = document.querySelector(".CirklaMobileSearchMount");
  const search =
    mount?.querySelector?.(".item-search") instanceof HTMLElement
      ? mount.querySelector(".item-search")
      : headerSearchItem();
  const input = search?.querySelector?.("input");
  if (input instanceof HTMLInputElement) {
    input.blur();
  }

  if (
    search instanceof HTMLElement &&
    mobileSearchOriginalParent instanceof Node
  ) {
    mobileSearchOriginalParent.insertBefore(
      search,
      mobileSearchOriginalNextSibling,
    );
  }

  mobileSearchOriginalParent = null;
  mobileSearchOriginalNextSibling = null;
}

function openMobileSearchOverlay() {
  if (typeof document === "undefined" || !isMobileViewport()) return;

  closeMobileDrawer();

  const search = headerSearchItem();
  const mount = mobileSearchMount();

  if (search instanceof HTMLElement && search.parentElement !== mount) {
    mobileSearchOriginalParent = search.parentNode;
    mobileSearchOriginalNextSibling = search.nextSibling;
    mount.appendChild(search);
  }

  document.body.classList.add("CirklaMobileSearchOpen");

  window.requestAnimationFrame(() => {
    const input = mount.querySelector?.("input");
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select?.();
    }
  });
}

function ensureMobileSearchLauncher() {
  if (typeof document === "undefined") return;

  let button = document.querySelector(".CirklaMobileSearchButton");
  if (!(button instanceof HTMLButtonElement)) {
    button = document.createElement("button");
    button.type = "button";
    button.className = "Button Button--icon CirklaMobileSearchButton";
    button.setAttribute("aria-label", String(forumTrans('header', 'searchForum')));
    button.innerHTML = '<i class="fas fa-search" aria-hidden="true"></i>';
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      openMobileSearchOverlay();
    });
    document.body.appendChild(button);
  }

  let closeButton = document.querySelector(".CirklaMobileSearchClose");
  if (!(closeButton instanceof HTMLButtonElement)) {
    closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "Button Button--icon CirklaMobileSearchClose";
    closeButton.setAttribute("aria-label", String(forumTrans('header', 'closeSearch')));
    closeButton.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';
    closeButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeMobileSearchOverlay();
    });
    document.body.appendChild(closeButton);
  }
}

function installMobileSearchOverlay() {
  if (
    typeof document === "undefined" ||
    app.__cirklaMobileSearchOverlayInstalled
  ) {
    return;
  }

  ensureMobileSearchLauncher();

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key === "Escape") closeMobileSearchOverlay();
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!document.body.classList.contains("CirklaMobileSearchOpen")) return;

      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;
      if (!target) return;
      if (
        target.closest(
          ".CirklaMobileSearchMount, .App-header .item-search, .CirklaMobileSearchButton, .CirklaMobileSearchClose",
        )
      ) {
        return;
      }

      closeMobileSearchOverlay();
    },
    true,
  );

  window.addEventListener(
    "resize",
    () => {
      ensureMobileSearchLauncher();
      if (!isMobileViewport()) closeMobileSearchOverlay();
    },
    { passive: true },
  );

  if (m?.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    if (!app.__cirklaMobileSearchRouteCloseInstalled) {
      m.route.set = function () {
        closeMobileSearchOverlay();
        return originalRouteSet.apply(this, arguments);
      };
      app.__cirklaMobileSearchRouteCloseInstalled = true;
    }
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => ensureMobileSearchLauncher()).observe(
      document.body,
      {
        childList: true,
        subtree: false,
      },
    );
  }

  app.__cirklaMobileSearchOverlayInstalled = true;
}

// -----------------------------------------------------------------------------
// Route and page-state helpers
// -----------------------------------------------------------------------------

function currentPathname() {
  if (typeof window === "undefined") return "";
  return String(window.location?.pathname || "").toLowerCase();
}

function isMasqueradeConfigureRoute() {
  const routeName = String(app.current?.get?.("routeName") || "").toLowerCase();
  const pathname = currentPathname();

  return (
    routeName === "fof-masquerade-configure-profile" ||
    pathname === "/masquerade/configure" ||
    pathname.startsWith("/masquerade/configure/")
  );
}

function removeMasqueradeProfileNavItems() {
  // Keep FoF Masquerade's native "Edit profile details" entry visible.
}

function ensureMobileUserPageNavPlacement() {
  if (typeof document === "undefined") return;

  const containers = document.querySelectorAll(".UserPage .sideNavContainer");
  containers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    const nav =
      container.querySelector(":scope > .UserPage-nav") ||
      container.querySelector(":scope > .sideNav") ||
      container.querySelector(".UserPage-nav");
    const content =
      container.querySelector(":scope > .UserPage-content") ||
      container.querySelector(":scope > .sideNavOffset") ||
      container.querySelector(".UserPage-content, .sideNavOffset");

    if (!(nav instanceof HTMLElement) || !(content instanceof HTMLElement)) {
      return;
    }

    nav.classList.add("CirklaMobileProfileNav");

    if (isMobileViewport() && nav.nextElementSibling !== content) {
      container.insertBefore(nav, content);
    }
  });
}

function currentHeaderPageTitle() {
  if (isDedicatedChatRoute()) {
    return String(forumTrans("discussion", "messagesTitle"));
  }

  const routeName = String(app.current?.get?.("routeName") || "").toLowerCase();
  const pathname = String(window?.location?.pathname || "").toLowerCase();

  if (
    routeName === "byobuprivate" ||
    pathname === "/private" ||
    pathname.startsWith("/private/")
  ) {
    return String(forumTrans("sidebar", "privateDiscussions"));
  }

  if (typeof document === "undefined") return "";

  const raw = String(document.title || "").trim();
  const prefix = "CIRKLA Community";
  const forumTitle =
    (app.forum &&
      typeof app.forum.attribute === "function" &&
      app.forum.attribute("title")) ||
    "";

  let title = raw.replace(
    new RegExp(
      `^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*-?\\s*`,
      "i",
    ),
    "",
  );

  if (forumTitle) {
    title = title.replace(
      new RegExp(
        `\\s*-\\s*${String(forumTitle).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
        "i",
      ),
      "",
    );
  }

  title = title.trim();

  if (!title) {
    return "";
  }

  return title;
}

function getCurrentPathname() {
  if (typeof window === "undefined" || !window.location) return "/";

  return String(window.location.pathname || "/");
}

const CIRKLA_LEGAL_PAGE_SLUGS = new Set([
  "terms",
  "support",
  "legal-notice",
  "privacy",
  "user-guides",
  "impressum",
  "datenschutzerklaerung",
  "benutzerhandbuch",
  "mentions-legales",
  "politique-de-confidentialite",
  "guides-utilisateur",
]);

function isLegalReadingPage() {
  const pathname = getCurrentPathname().toLowerCase().replace(/\/+$/, "");
  const slug = pathname.split("/").pop() || "";

  if (CIRKLA_LEGAL_PAGE_SLUGS.has(slug) || /-(terms|privacy)$/.test(slug)) return true;

  // The hardcoded list above only knows about the default pages shipped
  // with the theme - any page an admin links afterwards (in any locale,
  // via "Link Pages") needs the same reading-page treatment, or it'll only
  // get it for whichever locale variant happens to match the old list.
  return parseCustomPageEntries().some((entry) =>
    ["en", "de", "fr"].some((locale) => entry[locale]?.slug === slug),
  );
}

function applyLegalPageBodyClass() {
  if (typeof document === "undefined") return;

  document.body.classList.toggle("CirklaLegalPage", isLegalReadingPage());
}

/**
 * Mirrors important route states onto body classes so CSS can target complex
 * extension pages without duplicating route logic in stylesheets.
 */
function syncRouteBodyClasses() {
  if (typeof document === "undefined") return;

  const body = document.body;
  const routeName = String(app.current?.get?.("routeName") || "");
  const normalizedRouteName = routeName.toLowerCase();
  const pathname = String(window?.location?.pathname || "").toLowerCase();
  const isConversationsRoute = isDedicatedChatRoute();
  const isConversationDetailRoute = /^\/conversations\/[^/]+/.test(pathname);

  body.classList.toggle("CirklaLimitedMember", false);

  body.classList.toggle(
    "CirklaRoute--byobuPrivate",
    normalizedRouteName === "byobuprivate" ||
      normalizedRouteName === "private" ||
      pathname === "/private" ||
      pathname.startsWith("/private/"),
  );
  body.classList.toggle(
    "CirklaRoute--eventDetail",
    routeName === "advevent" || /^\/events\/[^/]+/.test(pathname),
  );
  body.classList.toggle("CirklaRoute--conversations", isConversationsRoute);
  body.classList.toggle(
    "CirklaConversationList",
    isConversationListRoute() ||
      (isConversationsRoute && !isConversationDetailRoute),
  );
  body.classList.toggle("CirklaConversationDetail", isConversationDetailRoute);

  if (isConversationListRoute()) {
    setTimeout(ensureConversationListBackButton, 0);
    setTimeout(ensureConversationListBackButton, 150);
  }

  ensureMobileUserPageNavPlacement();
  ensureMobileTagModalActions();
}

const CIRKLA_BACK_TAG_SLUGS = [
  "announcements",
  "best-practices",
  "knowledge-hub",
  "knowledgehub",
  "project-collaboration",
];

function currentDiscussionTagSlugs() {
  const discussion = app.current?.get?.("discussion");
  const tags =
    discussion?.tags?.() || discussion?.data?.relationships?.tags?.data;

  if (!Array.isArray(tags)) return [];

  return tags
    .map((tag) => tag?.slug?.() || tag?.attributes?.slug || tag?.id || "")
    .map((slug) => String(slug).toLowerCase())
    .filter(Boolean);
}

function shouldShowRouteBackButton() {
  // Discussion pages already expose Flarum's native back control. Keep the
  // custom route button disabled so the header never renders two controls.
  return false;
}

function ensureRouteBackButton() {
  if (typeof document === "undefined") return;

  const existingButtons = document.querySelectorAll(".CirklaRouteBackButton");
  document
    .querySelectorAll(
      ".App-content > .CirklaRouteBackButton, #content > .CirklaRouteBackButton",
    )
    .forEach((button) => button.remove());

  if (
    !shouldShowRouteBackButton() ||
    document.body.classList.contains("CirklaConversationDetail")
  ) {
    existingButtons.forEach((button) => button.remove());
    document
      .querySelectorAll(".item-cirkla-route-back")
      .forEach((item) => item.remove());
    document.body.classList.remove("CirklaHasRouteBackButton");
    if (document.body.classList.contains("CirklaConversationDetail")) {
      ensureConversationBackButton();
    }
    return;
  }

  document.body.classList.add("CirklaHasRouteBackButton");
  const showMobileButton = isMobileViewport();

  const goHome = (event) => {
    event.preventDefault();

    if (m?.route?.set) {
      m.route.set("/");
      return;
    }

    window.location.assign(`${window.location.origin}/`);
  };

  const createButton = (className, label = "") => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.setAttribute(
      "aria-label",
      forumTrans("discussion", "back"),
    );
    button.innerHTML = [
      '<i class="fas fa-arrow-left" aria-hidden="true"></i>',
      label ? `<span class="CirklaRouteBackButton-label">${label}</span>` : "",
    ].join("");
    button.addEventListener("click", goHome);
    return button;
  };

  const headerControls = document.querySelector(
    ".App-header .Header-primary > .Header-controls",
  );
  document.querySelectorAll(".item-cirkla-route-back").forEach((item) => {
    if (showMobileButton || !item.closest(".App-header")) item.remove();
  });
  if (
    !showMobileButton &&
    headerControls instanceof HTMLElement &&
    !headerControls.querySelector(".item-cirkla-route-back")
  ) {
    const item = document.createElement("li");
    item.className = "item-cirkla-route-back";
    item.appendChild(
      createButton(
        "Button Button--icon CirklaRouteBackButton CirklaRouteBackButton--header",
        String(forumTrans("common", "back")),
      ),
    );
    headerControls.insertBefore(item, headerControls.firstChild);
  }

  document
    .querySelectorAll(".CirklaRouteBackButton--mobile")
    .forEach((button) => button.remove());
  // Mobile already has Flarum's native back control. Styling that control is
  // more reliable than inserting a second button beside it.
}

function normalizeText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function stripLegalPageTitleCard() {
  if (typeof document === "undefined" || !isLegalReadingPage()) return;

  const contentRoot = document.getElementById("content");
  const title = normalizeText(currentHeaderPageTitle());

  if (!contentRoot || !title) return;

  const candidates = Array.from(
    contentRoot.querySelectorAll("h1, h2, h3, div, span"),
  ).filter((node) => normalizeText(node.textContent) === title);

  candidates.forEach((node) => {
    let current = node;
    let target = null;

    while (current && current !== contentRoot) {
      const text = normalizeText(current.textContent);
      if (text !== title) break;
      target = current;
      current = current.parentElement;
    }

    if (
      target &&
      target !== contentRoot &&
      !target.classList.contains("CirklaLegalTitleCardHidden")
    ) {
      target.classList.add("CirklaLegalTitleCardHidden");
      target.setAttribute("aria-hidden", "true");
      target.style.display = "none";
    }
  });
}

function polishLegalPageContent() {
  if (typeof document === "undefined" || !isLegalReadingPage()) return;

  document
    .querySelectorAll(".CirklaLegalPage .Post-body")
    .forEach((body) => {
      if (!(body instanceof HTMLElement)) return;
      if (body.dataset.cirklaLegalPolished === "1") return;

      const existingBlocks = body.querySelector(
        "p, h1, h2, h3, ul, ol, section, article",
      );
      if (existingBlocks) {
        body.dataset.cirklaLegalPolished = "1";
        return;
      }

      const raw = String(body.textContent || "")
        .replace(/([a-zäöüß])(?=Please update)/i, "$1 ")
        .replace(/([a-zäöüß])(?=Bitte aktualisieren)/i, "$1 ")
        .replace(/([a-zäöüß])(?=Veuillez mettre)/i, "$1 ")
        .replace(/^(Impressum)(Please update)/i, "$1 $2")
        .replace(/^(User Guides)(Please update)/i, "$1 $2")
        .trim();

      if (!raw) return;

      const match = raw.match(
        /^(Privacy Policy \/ Datenschutzerklaerung|Privacy Policy|Datenschutzerklärung|Impressum|User Guides)(.*)$/i,
      );

      body.replaceChildren();

      if (match) {
        const heading = document.createElement("h1");
        heading.textContent = match[1];
        body.appendChild(heading);

        const text = document.createElement("p");
        text.textContent = match[2].trim() || raw;
        body.appendChild(text);
      } else {
        const text = document.createElement("p");
        text.textContent = raw;
        body.appendChild(text);
      }

      body.dataset.cirklaLegalPolished = "1";
    });
}

// -----------------------------------------------------------------------------
// Sidebar and header cleanup helpers
// -----------------------------------------------------------------------------

function removeOriginalNewsletterBox() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      ".IndexPage-nav li.item-newsLetterBox, .sideNav li.item-newsLetterBox, .IndexPage-nav > .item-cirklaNewsletterBox, .sideNav > .item-cirklaNewsletterBox",
    )
    .forEach((item) => {
      if (!item.classList.contains("item-cirklaNewsletterBox")) {
        item.remove();
      }
    });
}

function ensureNewsletterNavPlacement() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(".IndexPage-nav ul, .sideNav ul")
    .forEach((container) => {
      const newsletter = container.querySelector(".item-cirklaNewsletterBox");
      if (!newsletter) return;

      const eventItem = Array.from(container.children).find((item) => {
        if (item === newsletter) return false;
        const text = String(item.textContent || "")
          .trim()
          .toLowerCase();
        return text === "events";
      });

      if (eventItem && eventItem.nextSibling !== newsletter) {
        container.insertBefore(newsletter, eventItem.nextSibling);
      }
    });
}

function normalizeLegalPageUrl(url, fallback) {
  const value = String(url || "").trim();
  if (!value) return fallback;

  try {
    const parsed = new URL(value, window.location.origin);
    if (parsed.pathname === "/terms" || parsed.pathname === "/terms/") {
      return "/p/terms";
    }
    if (parsed.pathname === "/privacy" || parsed.pathname === "/privacy/") {
      return "/p/privacy";
    }
  } catch (error) {
    // Keep the original value below if URL parsing is unavailable.
  }

  return value;
}

// -----------------------------------------------------------------------------
// Header branding fallback
// -----------------------------------------------------------------------------

function ensureHeaderBrandFallback() {
  if (typeof document === "undefined") return;

  const primary = document.querySelector(".App-header .Header-primary");
  if (!primary) return;

  let link = primary.querySelector(".CirklaHeaderInterregLink");
  if (!link) {
    link = document.createElement("a");
    link.className = "CirklaHeaderInterregLink CirklaHeaderBrandFallback";
    link.href = "/";
    link.title = forumTrans("common", "goHome");
    link.setAttribute("aria-label", forumTrans("common", "goHome"));

    const projectLogo = document.createElement("img");
    projectLogo.className =
      "CirklaHeaderInterregLogo CirklaHeaderProjectLogo";
    projectLogo.src = "/assets/branding/cirkla_wider_interreg_color.png";
    projectLogo.alt = forumTrans("header", "projectLogoAlt");

    link.append(projectLogo);
    link.addEventListener("click", (event) => {
      if (!m?.route?.set) return;
      event.preventDefault();
      m.route.set("/");
    });

    primary.insertBefore(link, primary.firstChild);
  }

  const projectLogo = link.querySelector(".CirklaHeaderProjectLogo");
  if (
    projectLogo &&
    projectLogo.getAttribute("src") !==
      "/assets/branding/cirkla_wider_interreg_color.png"
  ) {
    projectLogo.setAttribute(
      "src",
      "/assets/branding/cirkla_wider_interreg_color.png",
    );
    projectLogo.setAttribute(
      "alt",
      "Interreg Grande Région and CIRKLA UniGR",
    );
  }

  primary
    .querySelectorAll(
      ".CirklaHeaderPageTitleFallback, .item-cirkla-page-title, .Header-title",
    )
    .forEach((title) => title.remove());
}

// -----------------------------------------------------------------------------
// User and legal-policy helpers
// -----------------------------------------------------------------------------

function getCurrentUser() {
  return app.session?.user || null;
}

function currentUserSecurityHref() {
  const user = app.session?.user;
  if (!user) return "/";

  const slug =
    (typeof user.slug === "function" && user.slug()) ||
    (typeof user.username === "function" && user.username()) ||
    user.data?.attributes?.slug ||
    user.data?.attributes?.username ||
    "";

  if (!slug) return "/settings";

  return `/u/${encodeURIComponent(slug)}/security`;
}

function setConversationRecipient(user) {
  if (!user) return false;

  app.cache = app.cache || {};
  app.cache.conversationsRecipient = user;

  return true;
}

function isMandatoryLegalPolicy(policy) {
  if (!policy) return false;

  const name = String(policy.name?.() || "").toLowerCase();
  const url = String(policy.url?.() || "").toLowerCase();

  const termsPattern = /(term|condition|bedingungen|agb|conditions)/i;
  const privacyPattern =
    /(privacy|datenschutz|confidentialit|donnees personnelles)/i;

  return (
    termsPattern.test(name) ||
    privacyPattern.test(name) ||
    termsPattern.test(url) ||
    privacyPattern.test(url)
  );
}

function getMandatoryLegalPolicyState() {
  const user = getCurrentUser();
  if (!user) {
    return [];
  }

  const state =
    (typeof user.fofTermsPoliciesState === "function"
      ? user.fofTermsPoliciesState()
      : user.data?.attributes?.fofTermsPoliciesState) || {};
  const policies = app.store?.all?.("fof-terms-policies") || [];

  return policies
    .filter((policy) => policy?.id?.() && isMandatoryLegalPolicy(policy))
    .map((policy) => {
      const policyId = String(policy.id());
      const policyState = state[policyId] || state[Number(policyId)] || {};

      return {
        policy,
        policyId,
        hasUpdate: !!policyState.has_update,
      };
    })
    .filter(({ hasUpdate }) => hasUpdate);
}

function userMustAcceptMandatoryPolicies() {
  return getMandatoryLegalPolicyState().length > 0;
}

function hasMandatoryPoliciesModalOpen() {
  if (typeof document === "undefined") return false;

  return !!document.querySelector(
    ".ModalManager .CirklaMandatoryPoliciesModal",
  );
}

function lockForumForMandatoryPolicies() {
  if (typeof document === "undefined") return;

  document.body.classList.add("CirklaLegalAcceptanceRequired");
}

function unlockForumForMandatoryPolicies() {
  if (typeof document === "undefined") return;

  document.body.classList.remove("CirklaLegalAcceptanceRequired");
}

function acceptMandatoryPolicy(policy) {
  return app.request({
    url: `${app.forum.attribute("apiUrl")}${policy.apiEndpoint()}/accept`,
    method: "POST",
  });
}

// -----------------------------------------------------------------------------
// Mandatory legal-policy modal
// -----------------------------------------------------------------------------

class CirklaMandatoryPoliciesModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.accepted = {};
    this.submitting = false;
    this.errorMessage = null;
  }

  className() {
    return "CirklaMandatoryPoliciesModal Modal--medium";
  }

  title() {
    return forumTrans("legal", "title");
  }

  pendingPolicies() {
    return getMandatoryLegalPolicyState();
  }

  canSubmit() {
    const pending = this.pendingPolicies();
    return (
      pending.length > 0 &&
      pending.every(({ policyId }) => this.accepted[policyId] === true)
    );
  }

  onsubmit(event) {
    event.preventDefault();
    if (this.submitting || !this.canSubmit()) {
      return;
    }

    this.submitting = true;
    this.errorMessage = null;

    const pending = this.pendingPolicies();
    Promise.all(pending.map(({ policy }) => acceptMandatoryPolicy(policy)))
      .then(() => {
        window.location.reload();
      })
      .catch(() => {
        this.errorMessage = forumTrans("legal", "error");
        this.submitting = false;
        m.redraw();
      });
  }

  content() {
    const pending = this.pendingPolicies();

    return m("div", { className: "Modal-body" }, [
      m("form", { className: "Form", onsubmit: this.onsubmit.bind(this) }, [
        m("div", { className: "CirklaMandatoryPolicies" }, [
          m(
            "p",
            { className: "CirklaMandatoryPolicies-copy" },
            forumTrans("legal", "description"),
          ),
          m(
            "div",
            { className: "CirklaMandatoryPolicies-list" },
            pending.map(({ policy, policyId }) =>
              m(
                "label",
                { className: "checkbox CirklaMandatoryPolicies-check" },
                [
                  m("input", {
                    type: "checkbox",
                    checked: !!this.accepted[policyId],
                    disabled: this.submitting,
                    onchange: () => {
                      this.accepted[policyId] = !this.accepted[policyId];
                      this.errorMessage = null;
                    },
                  }),
                  m("span", [
                    `${forumTrans("legal", "acceptPolicyPrefix")} `,
                    policy.url?.()
                      ? m(
                          "a",
                          {
                            href: normalizeLegalPageUrl(policy.url?.(), "#"),
                            target: "_blank",
                            rel: "noopener noreferrer",
                          },
                          policy.name?.() || forumTrans("legal", "policyFallback"),
                        )
                      : m(
                          "strong",
                          policy.name?.() || forumTrans("legal", "policyFallback"),
                        ),
                  ]),
                ],
              ),
            ),
          ),
          this.errorMessage
            ? m(
                "p",
                { className: "helpText CirklaMandatoryPolicies-error" },
                this.errorMessage,
              )
            : null,
          m(
            Button,
            {
              className: "Button Button--primary Button--block",
              type: "submit",
              loading: this.submitting,
              disabled: this.submitting || !this.canSubmit(),
            },
            forumTrans("legal", "acceptContinue"),
          ),
        ]),
      ]),
    ]);
  }
}

CirklaMandatoryPoliciesModal.isDismissible = false;
CirklaMandatoryPoliciesModal.isDismissibleViaCloseButton = false;
CirklaMandatoryPoliciesModal.isDismissibleViaBackdropClick = false;
CirklaMandatoryPoliciesModal.isDismissibleViaEscKey = false;

/**
 * Blocks normal forum usage until required Terms/Privacy policies have been
 * accepted, while still allowing users to read the legal pages themselves.
 */
function enforceMandatoryPoliciesGate() {
  const mustAccept = userMustAcceptMandatoryPolicies();

  if (!mustAccept) {
    unlockForumForMandatoryPolicies();
    endInitialSessionBootSoon();
    return;
  }

  if (isLegalReadingPage()) {
    unlockForumForMandatoryPolicies();
    endInitialSessionBootSoon();
    return;
  }

  lockForumForMandatoryPolicies();

  if (!hasMandatoryPoliciesModalOpen() && app.modal?.show) {
    app.modal.show(CirklaMandatoryPoliciesModal);
  }

  endInitialSessionBootSoon();
}

// -----------------------------------------------------------------------------
// Conversation route helpers
// -----------------------------------------------------------------------------

function isUserProfileRoute() {
  const pathname = String(window?.location?.pathname || "").toLowerCase();
  return /^\/u\/[^/]+(?:\/[^/]+)?\/?$/.test(pathname);
}

function isUserDirectoryRoute() {
  if (typeof window === "undefined") return false;

  const pathname = String(window.location.pathname || "").toLowerCase();
  return /^\/users\/?$/.test(pathname);
}

function canUseConversationFeatures() {
  if (!app.session?.user) return false;

  return Boolean(
    app.forum?.attribute?.("canMessage") ||
    app.forum?.attribute?.("canStartPrivateDiscussion") ||
    app.forum?.attribute?.("canStartPrivateDiscussionWithUsers") ||
    app.forum?.attribute?.("canStartPrivateDiscussionWithGroups"),
  );
}

function resolveRouteHref(routeName) {
  try {
    if (!routeName) return null;
    if (!app.routes || !app.routes[routeName]) return null;
    if (typeof app.route !== "function") return null;
    return app.route(routeName);
  } catch (error) {
    return null;
  }
}

function resolveRouteHrefByPredicate(predicate) {
  try {
    if (!app.routes || typeof predicate !== "function") return null;
    if (typeof app.route !== "function") return null;

    const routeName = Object.keys(app.routes).find((name) => predicate(name));
    return routeName ? app.route(routeName) : null;
  } catch (error) {
    return null;
  }
}

/**
 * Finds the best available dedicated chat route across supported chat extensions.
 * Falls back to path-based routing because extension route names vary.
 */
function resolveDedicatedChatRouteHref() {
  if (!canUseConversationFeatures()) return null;

  const explicitCandidates = [
    "neoncube-private-messages.conversations",
    "neoncube-private-messages.messages",
    "conversations",
    "messages",
    "conversationsIndex",
    "blomstra-conversations",
    "blomstraConversations",
    "blomstra-chat",
    "blomstraChat",
    "flarumite-simple-chat",
    "flarumiteSimpleChat",
    "nearata-chat",
    "nearataChat",
    "chat",
    "chatbox",
    "fof-chat",
    "fofChat",
  ];

  for (const routeName of explicitCandidates) {
    const href = resolveRouteHref(routeName);
    if (href) return href;
  }

  const discoveredChatRoute = resolveRouteHrefByPredicate((name) => {
    const key = String(name || "").toLowerCase();
    if (!key) return false;
    if (!key.includes("chat")) return false;
    return !/admin|private|byobu|composer/.test(key);
  });

  if (discoveredChatRoute) return discoveredChatRoute;
  return null;
}

function resolveChatRouteHref() {
  const dedicatedChatRoute = resolveDedicatedChatRouteHref();
  return dedicatedChatRoute || "/conversations";
}

function resolvePrivateDiscussionsRouteHref() {
  const explicitCandidates = [
    "byobuPrivate",
    "byobuprivate",
    "fof-byobu.private",
    "private",
  ];

  for (const routeName of explicitCandidates) {
    const href = resolveRouteHref(routeName);
    if (href) return href;
  }

  const discoveredPrivateRoute = resolveRouteHrefByPredicate((name) => {
    const key = String(name || "").toLowerCase();
    if (!key) return false;
    return (
      key.includes("byobu") ||
      key === "private" ||
      key.includes("private-discussion")
    );
  });

  return discoveredPrivateRoute || "/private";
}

function isDedicatedChatRoute() {
  const routeName = String(app.current?.get?.("routeName") || "").toLowerCase();
  const pathname = String(window?.location?.pathname || "").toLowerCase();

  return (
    routeName === "conversations" ||
    routeName === "messages" ||
    routeName === "neoncube-private-messages.conversations" ||
    routeName === "neoncube-private-messages.messages" ||
    pathname === "/conversations" ||
    pathname.startsWith("/conversations/")
  );
}

function isConversationRoute() {
  return isDedicatedChatRoute();
}

function isConversationListRoute() {
  const pathname = currentPathname();

  return pathname === "/conversations" || pathname === "/conversations/";
}

function isPrivateDiscussionListRoute() {
  const routeName = String(app.current?.get?.("routeName") || "").toLowerCase();
  const pathname = String(window?.location?.pathname || "").toLowerCase();

  return (
    routeName === "byobuprivate" ||
    routeName === "private" ||
    pathname === "/private" ||
    pathname.startsWith("/private/")
  );
}

function currentConversationId() {
  try {
    if (m?.route?.param) {
      const id = m.route.param("id");
      if (id !== undefined && id !== null && id !== "") return String(id);
    }
  } catch (error) {
    // Fall back to pathname parsing when route params are not ready yet.
  }

  const pathname = String(window?.location?.pathname || "");
  const match = pathname.match(/\/conversations\/([^/?#]+)/i);
  return match ? String(match[1]) : null;
}

// -----------------------------------------------------------------------------
// Conversation scrolling and layout
// -----------------------------------------------------------------------------

let chatInitialScrollDoneForRoute = null;
let chatInitialScrollTimer = null;

/**
 * Returns a stable key for the currently opened chat route.
 * We use this to scroll only once per opened conversation.
 */
function currentChatScrollKey() {
  if (typeof window === "undefined") return null;
  if (!isConversationRoute()) return null;

  const conversationId = currentConversationId() || "";

  return `${window.location.pathname}${window.location.search}#${conversationId}`;
}

function getChatHistoryElements() {
  if (typeof document === "undefined") return [];

  return Array.from(
    document.querySelectorAll(
      ".ConversationsList .chat-history, .chat-history",
    ),
  ).filter((history) => history instanceof HTMLElement);
}

function scrollChatHistoryToBottomOnce(history) {
  if (!(history instanceof HTMLElement)) return;

  const maxScrollTop = Math.max(history.scrollHeight - history.clientHeight, 0);
  const lastMessage = history.querySelector("li:last-child");

  history.scrollTop = maxScrollTop;

  if (typeof history.scrollTo === "function") {
    history.scrollTo({
      top: maxScrollTop,
      left: 0,
      behavior: "auto",
    });
  }

  const messageList = history.querySelector("ul, ol");

  if (messageList instanceof HTMLElement) {
    messageList.scrollTop = messageList.scrollHeight;
  }

  if (lastMessage instanceof HTMLElement) {
    lastMessage.scrollIntoView({
      block: "end",
      inline: "nearest",
      behavior: "auto",
    });
  }

  history.scrollTop = Math.max(history.scrollHeight - history.clientHeight, 0);
}

/**
 * Scrolls the active chat to the bottom once, then stops.
 *
 * It retries briefly because the chat DOM can render after the route changes.
 * After the first successful scroll, the user controls scrolling manually.
 */
function runInitialChatScrollOnce({ reset = false } = {}) {
  if (typeof window === "undefined") return;

  const routeKey = currentChatScrollKey();
  if (!routeKey) return;

  if (reset) {
    chatInitialScrollDoneForRoute = null;
  }

  if (chatInitialScrollDoneForRoute === routeKey) {
    return;
  }

  window.clearTimeout(chatInitialScrollTimer);

  let attempts = 0;
  const maxAttempts = 20;

  const tryScroll = () => {
    const currentRouteKey = currentChatScrollKey();

    if (!currentRouteKey) return;

    if (chatInitialScrollDoneForRoute === currentRouteKey) {
      return;
    }

    const histories = getChatHistoryElements();

    if (histories.length) {
      histories.forEach(scrollChatHistoryToBottomOnce);
      chatInitialScrollDoneForRoute = currentRouteKey;
      return;
    }

    attempts += 1;

    if (attempts < maxAttempts) {
      chatInitialScrollTimer = window.setTimeout(tryScroll, 100);
    }
  };

  if (window.requestAnimationFrame) {
    window.requestAnimationFrame(tryScroll);
  } else {
    tryScroll();
  }
}

/**
 * Keep these function names for compatibility with the rest of this file.
 * They now only perform a one-time initial scroll instead of continuous forcing.
 */
function scrollActiveChatToBottom({ reset = false, force = false } = {}) {
  runInitialChatScrollOnce({ reset: reset || force });
}

function syncChatHistoryAutoScroll({ reset = false, force = false } = {}) {
  runInitialChatScrollOnce({ reset: reset || force });
}

function scheduleChatScrollToBottom({ reset = false, force = false } = {}) {
  runInitialChatScrollOnce({ reset: reset || force });
}

function settleChatScrollToBottom(
  _duration = 0,
  { reset = false, force = false } = {},
) {
  runInitialChatScrollOnce({ reset: reset || force });
}

function ensureConversationBackButton() {
  if (typeof document === "undefined") return;
  if (!document.body.classList.contains("CirklaConversationDetail")) return;

  document
    .querySelectorAll(".ConversationsList .chat .chat-header")
    .forEach((header) => {
      if (!(header instanceof HTMLElement)) return;
      if (header.querySelector(".CirklaChatBackButton")) return;

      const button = document.createElement("button");
      button.type = "button";
      button.className = "CirklaChatBackButton";
      button.setAttribute("aria-label", String(forumTrans('discussion', 'backToConversations')));
      button.innerHTML = '<i class="fas fa-arrow-left" aria-hidden="true"></i>';
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (m?.route?.set) {
          m.route.set("/conversations");
          return;
        }

        window.location.assign(`${window.location.origin}/conversations`);
      });

      header.insertBefore(button, header.firstChild);
    });
}

function ensureConversationListBackButton() {
  if (typeof document === "undefined") return;

  const existingToolbars = document.querySelectorAll(
    ".CirklaConversationListToolbar",
  );

  if (
    !document.body.classList.contains("CirklaConversationList") &&
    !isConversationListRoute()
  ) {
    existingToolbars.forEach((toolbar) => toolbar.remove());
    return;
  }

  document.querySelectorAll(".ConversationsList").forEach((list) => {
    if (!(list instanceof HTMLElement)) return;
    if (list.querySelector(":scope > .CirklaConversationListToolbar")) return;

    const toolbar = document.createElement("div");
    toolbar.className = "CirklaConversationListToolbar";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "CirklaConversationHomeButton";
    button.setAttribute("aria-label", String(forumTrans("common", "goHome")));
    button.innerHTML = '<i class="fas fa-arrow-left" aria-hidden="true"></i>';
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (m?.route?.set) {
        m.route.set("/");
        return;
      }

      window.location.assign(`${window.location.origin}/`);
    });

    const title = document.createElement("div");
    title.className = "CirklaConversationListTitle";
    title.textContent = String(forumTrans("discussion", "messagesTitle"));

    toolbar.append(button, title);
    list.insertBefore(toolbar, list.firstChild);
  });
}

let conversationsSyncObserver = null;
let pendingChatStarterUser = null;
let pendingChatStarterUsername = "";
let chatUnreadIndicatorInterval = null;
let chatScrollSettleUntil = 0;
let chatScrollForceUntil = 0;
let chatScrollSettleTimer = null;
let conversationsSyncFrame = null;

// -----------------------------------------------------------------------------
// Conversation integration
// -----------------------------------------------------------------------------

/**
 * Bridges third-party conversation UI with CIRKLA-specific behaviors: unread
 * state, auto-scroll, back buttons, and rerouting user-card message actions.
 */
function installConversationsSyncObserver() {
  if (
    typeof document === "undefined" ||
    conversationsSyncObserver ||
    !canUseConversationFeatures()
  ) {
    return;
  }

  const runConversationSync = () => {
    conversationsSyncFrame = null;
    syncSidebarChatUnreadState();
    syncConversationListUnreadState();
    if (!isConversationRoute()) return;
    settleChatScrollToBottom();
    enhanceStartConversationModal();
    ensureConversationBackButton();
    ensureConversationListBackButton();
  };

  const handleDomChange = () => {
    if (conversationsSyncFrame !== null) return;

    conversationsSyncFrame = window.requestAnimationFrame(runConversationSync);
  };

  conversationsSyncObserver = new MutationObserver(handleDomChange);
  conversationsSyncObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest(".ConversationsList .UserListItem")) {
        scheduleChatScrollToBottom({ reset: true });

        setTimeout(syncSidebarChatUnreadState, 300);
        setTimeout(syncConversationListUnreadState, 300);

        setTimeout(syncSidebarChatUnreadState, 900);
        setTimeout(syncConversationListUnreadState, 900);

        setTimeout(syncSidebarChatUnreadState, 1600);
        setTimeout(syncConversationListUnreadState, 1600);

        return;
      }
      const genericSendMessageAction = target.closest(
        ".Dropdown-menu .Button, .Dropdown-menu a, .Dropdown-menu li",
      );
      if (
        genericSendMessageAction &&
        /send\s+.+\s+a\s+message/i.test(
          String(genericSendMessageAction.textContent || ""),
        )
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const user = resolveUserFromAction(genericSendMessageAction);
        if (user) {
          openConversationStarterForUser(user);
        } else {
          const textMatch = String(
            genericSendMessageAction.textContent || "",
          ).match(/send\s+(.+?)\s+a\s+message/i);
          openConversationStarterByUsername(
            textMatch ? textMatch[1].trim() : "",
          );
        }
        return;
      }

      // Route user-card "Send message" action to the chat extension,
      // not to private discussions (Byobu). This must work on every page.
      const byobuMessageAction = target.closest(
        ".item-private-discussion .Button, .item-private-discussion a",
      );
      if (byobuMessageAction) {
        const user = resolveUserFromAction(target);
        event.preventDefault();
        event.stopImmediatePropagation();
        if (user) {
          openConversationStarterForUser(user);
        } else {
          openConversationStarterByUsername("");
        }
        return;
      }

      if (!isConversationRoute()) return;
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!isPrivateDiscussionListRoute()) return;

      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;
      if (!target) return;

      const card = target.closest(".DiscussionListItem");
      if (!card) return;
      if (
        target.closest(
          "a, button, input, textarea, select, label, .Dropdown, .Dropdown-menu, .DiscussionListItem-controls",
        )
      ) {
        return;
      }

      const link = card.querySelector(
        ".DiscussionListItem-main a[href], .DiscussionListItem-title a[href], a[href*='/d/']",
      );
      if (!(link instanceof HTMLAnchorElement)) return;

      event.preventDefault();
      if (m?.route?.set && link.getAttribute("href")) {
        m.route.set(link.getAttribute("href"));
        return;
      }

      window.location.assign(link.href);
    },
    true,
  );

  ensureConversationBackButton();
  ensureConversationListBackButton();
}

function resolveUserFromAction(target) {
  const resolveFromActionLabel = () => {
    const users = app.store?.all?.("users") || [];
    const actionRoot = target.closest("li, .Dropdown-menu, .Button, a");
    const actionText = String(actionRoot?.textContent || "").trim();
    const match = actionText.match(/send\s+(.+?)\s+a\s+message/i);
    if (!match) return null;

    const hintedName = match[1].trim().toLowerCase();
    return (
      users.find((candidate) => {
        const username = String(candidate?.username?.() || "").toLowerCase();
        const displayName = String(
          candidate?.displayName?.() || "",
        ).toLowerCase();
        return username === hintedName || displayName === hintedName;
      }) || null
    );
  };

  const host =
    target.closest("[data-user-id]") ||
    target.closest(".UserCard-controls") ||
    target.closest(".UserCard") ||
    target.closest(".UserCard-hover") ||
    target.closest(".UserCardInfo") ||
    target.closest(".UsersUser") ||
    target.closest(".UserCard--popover") ||
    target.closest(".UserCard") ||
    target.closest(".PostUser");

  if (!host) return resolveFromActionLabel();

  const dataUserId = host.getAttribute?.("data-user-id");
  if (dataUserId) {
    const byId = app.store?.getById?.("users", String(dataUserId));
    if (byId) return byId;
  }

  const profileLink = host.querySelector(
    "a[href*='/u/'], .username a, .UserCard-profile a, .UsersUser-user a",
  );
  if (!profileLink) return resolveFromActionLabel();

  const href = profileLink.getAttribute("href") || "";
  const match = href.match(/\/u\/([^/?#]+)/i);
  if (!match) return null;

  const slug = decodeURIComponent(match[1]);
  const users = app.store?.all?.("users") || [];

  return (
    users.find((candidate) => String(candidate?.slug?.() || "") === slug) ||
    users.find(
      (candidate) =>
        String(candidate?.username?.() || "").toLowerCase() ===
        slug.toLowerCase(),
    ) ||
    null
  );
}

/**
 * Patches the Start Conversation modal only when CIRKLA has preselected a
 * recipient, allowing the extension-native flow to handle all other cases.
 */
function installStartConversationSubmitPatch(modalRoot) {
  if (!modalRoot || modalRoot.dataset.cirklaSubmitPatchInstalled === "1") {
    return;
  }

  modalRoot.dataset.cirklaSubmitPatchInstalled = "1";

  const form = modalRoot.querySelector("form");
  const submitButton =
    modalRoot.querySelector("button[type='submit']") ||
    Array.from(modalRoot.querySelectorAll("button.Button")).find((button) =>
      /start|send|conversation|message/i.test(button.textContent || ""),
    );

  const handler = (event) => {
    const recipient = app.cache?.conversationsRecipient || null;
    const textarea = modalRoot.querySelector("textarea");
    const messageContents = String(textarea?.value || "").trim();

    if (!recipient || !messageContents) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    submitStartConversationFromModal(modalRoot);
  };

  if (form) {
    form.addEventListener("submit", handler, true);
  }

  if (submitButton) {
    submitButton.addEventListener("click", handler, true);
  }
}
function clickStartConversationButtonWithRetry(attempt = 0) {
  if (attempt > 20) return;

  const startButton =
    document.querySelector(".StartConversationModal") ||
    document.querySelector(
      ".ConversationsList .people-list .Button--primary",
    ) ||
    Array.from(document.querySelectorAll("button.Button, a.Button")).find(
      (button) =>
        /start\s+(a\s+)?conversation|new\s+conversation/i.test(
          button.textContent || "",
        ),
    );

  if (
    startButton &&
    !startButton.classList?.contains("StartConversationModal")
  ) {
    startButton.click();
    return;
  }

  setTimeout(() => clickStartConversationButtonWithRetry(attempt + 1), 150);
}

function openConversationStarterForUser(user) {
  if (!user || !canUseConversationFeatures()) return;
  pendingChatStarterUser = user;
  pendingChatStarterUsername = String(user.username?.() || "");
  const dedicatedChatRoute = resolveDedicatedChatRouteHref();
  if (!dedicatedChatRoute) {
    return;
  }
  m.route.set(dedicatedChatRoute);
  setTimeout(() => clickStartConversationButtonWithRetry(), 180);
}

function openConversationStarterByUsername(username) {
  if (!canUseConversationFeatures()) return;

  const dedicatedChatRoute = resolveDedicatedChatRouteHref();
  if (!dedicatedChatRoute) {
    return;
  }

  pendingChatStarterUser = null;
  pendingChatStarterUsername = String(username || "").trim();
  m.route.set(dedicatedChatRoute);

  setTimeout(() => clickStartConversationButtonWithRetry(), 180);
}

// -----------------------------------------------------------------------------
// Menu and sidebar action fallbacks
// -----------------------------------------------------------------------------

function buildFallbackMenuItem({ key, label, iconClass, onClick }) {
  const item = document.createElement("li");
  item.className = `item-${key}`;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "Button hasIcon";
  button.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick?.(event);
  });

  const icon = document.createElement("i");
  icon.className = `icon ${iconClass} Button-icon`;

  const text = document.createElement("span");
  text.textContent = label;

  button.appendChild(icon);
  button.appendChild(text);
  item.appendChild(button);

  return item;
}

function ensureHeaderQuickActionFallbacks() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      [
        ".cirkla-private-chat-header-fallback",
        ".item-cirkla-private-chat-header",
        ".item-Messages",
        ".item-messages",
        ".MessagesDropdown",
        ".cirkla-invite-member-header-fallback",
        ".item-cirkla-invite-member-header",
      ].join(","),
    )
    .forEach((item) => item.remove());
}

function ensurePrivateDiscussionFallback() {
  if (
    !app.session?.user ||
    typeof document === "undefined" ||
    !canUseConversationFeatures()
  ) {
    return;
  }
  if (!resolveDedicatedChatRouteHref()) return;

  document
    .querySelectorAll(
      ".UserCard-controls .Dropdown-menu, .UsersUser .Dropdown-menu, .PostUser .Dropdown-menu",
    )
    .forEach((menu) => {
      if (
        menu.querySelector(".item-private-discussion") ||
        menu.querySelector(".item-cirkla-private-discussion-fallback")
      ) {
        return;
      }
      if (isUserProfileRoute() && menu.closest(".UserPage, .UserCard")) return;

      const user = resolveUserFromAction(menu);
      if (!user || user === app.session.user) return;

      const privateDiscussionItem = buildFallbackMenuItem({
        key: "cirkla-private-discussion-fallback",
        label: buildSendMessageLabel(user),
        iconClass: "fas fa-comments",
        onClick: () => openConversationStarterForUser(user),
      });

      menu.appendChild(privateDiscussionItem);
    });
}

function removeUnsupportfromSideNavItems() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      [
        ".IndexPage-nav .item-cirkla-support",
        ".sideNav .item-cirkla-support",
        ".IndexPage-nav .item-cirkla-support-fallback",
        ".sideNav .item-cirkla-support-fallback",
      ].join(","),
    )
    .forEach((node) => (node.closest("li") || node).remove());
}

function runChatInviteFallbacks() {
  if (!app.session?.user || !canUseConversationFeatures()) {
    removeProfileChatPrivateItems();
    removeMasqueradeProfileNavItems();
    return;
  }

  removeProfileChatPrivateItems();
  removeMasqueradeProfileNavItems();
  removeUnsupportfromSideNavItems();
  ensureSideNavLinkFallbacks();
  ensureHeaderQuickActionFallbacks();
  ensurePrivateDiscussionFallback();

  setTimeout(removeProfileChatPrivateItems, 0);
  setTimeout(removeProfileChatPrivateItems, 100);
  setTimeout(removeProfileChatPrivateItems, 300);
  setTimeout(removeMasqueradeProfileNavItems, 0);
  setTimeout(removeMasqueradeProfileNavItems, 100);
  setTimeout(removeMasqueradeProfileNavItems, 300);
}

// -----------------------------------------------------------------------------
// Profile and Masquerade field rendering
// -----------------------------------------------------------------------------

function answerHasRenderableContent(answer) {
  try {
    if (typeof answer?.content === "function") {
      return String(answer.content() ?? "").trim().length > 0;
    }
  } catch (_error) {
    return false;
  }

  return false;
}

function optionsFromMasqueradeValidation(validation) {
  const match = String(validation || "").match(/^in:(.*)$/i);
  if (!match) return [];

  return match[1]
    .split(",")
    .map((option) => option.trim())
    .filter(Boolean);
}

function answerValueForMasqueradeField(field, includedAnswers) {
  const answerId = field?.relationships?.answer?.data?.id;
  if (!answerId) return "";

  return String(includedAnswers.get(String(answerId)) || "");
}

const MASQUERADE_COMPLETION_ROUTE = "/masquerade/configure";
const MASQUERADE_COMPLETION_NEXT_URL_KEY =
  "cirklaMasqueradeNextAfterCompletion";
const MASQUERADE_COMPLETION_CACHE_MS = 15000;

// true = every Masquerade field is required
// false = only fields marked required in admin are required
const CIRKLA_FORCE_ALL_MASQUERADE_FIELDS = false;

let masqueradeCompletionState = {
  checkedAt: 0,
  loading: false,
  loaded: false,
  profileComplete: true,
  fields: [],
  answers: {},
  missingFields: [],
  promise: null,
};

let lastMasqueradeCompletionAlertAt = 0;

function booleanAttribute(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;

  const normalized = String(value).trim().toLowerCase();
  return ["1", "true", "yes", "on"].includes(normalized);
}

function forumSetting(name, fallback = "") {
  try {
    const value = app.forum?.attribute?.(name);
    return typeof value === "undefined" || value === null ? fallback : value;
  } catch (_error) {
    return fallback;
  }
}

function settingEnabled(name, fallback = false) {
  const value = forumSetting(name, fallback);
  if (typeof value === "boolean") return value;
  return booleanAttribute(value);
}

/**
 * Admin-configurable sidebar/dropdown pages (cirkla.custom_pages), a JSON
 * array of groups built by the "Link Pages" admin UI:
 *   [{ key, placements: ["sidebar","dropdown"], pages: { en: {slug,label}, de: {...}, fr: {...} } }]
 * de/fr are optional in `pages` and fall back to en. Malformed JSON, or a
 * malformed individual group (missing key or an en page), degrades to an
 * empty list rather than throwing - a bad setting value shouldn't break
 * navigation rendering.
 */
function parseCustomPageEntries() {
  const raw = String(forumSetting("cirkla.custom_pages", "") || "");
  if (!raw.trim()) return [];

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_error) {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((group) => {
      if (!group || typeof group !== "object") return null;

      const key = String(group.key || "").trim();
      const en = group.pages?.en;
      if (!key || !en?.slug) return null;

      const enVariant = { label: en.label || key, slug: en.slug };
      const de = group.pages?.de;
      const fr = group.pages?.fr;

      return {
        key,
        placements: new Set(
          Array.isArray(group.placements)
            ? group.placements.map((p) => String(p).toLowerCase())
            : ["sidebar", "dropdown"],
        ),
        icon:
          typeof group.icon === "string" && group.icon.trim()
            ? group.icon.trim()
            : "fas fa-file-alt",
        // "isSupport" is the explicit admin designation (set via the "Use as
        // the site's Support link" checkbox in the Link Pages modal) for
        // which group is treated as the one, always-visible Support link.
        // key === "support" is kept as a fallback for groups saved before
        // that checkbox existed.
        isSupport: Boolean(group.isSupport) || key === "support",
        en: enVariant,
        de: de?.slug ? { label: de.label || enVariant.label, slug: de.slug } : enVariant,
        fr: fr?.slug ? { label: fr.label || enVariant.label, slug: fr.slug } : enVariant,
      };
    })
    .filter(Boolean);
}

function currentSiteLocale() {
  if (typeof document === "undefined") return "en";

  const rawLocale = document.documentElement.lang || app.data?.locale || "en";
  const locale = String(rawLocale).toLowerCase().split(/[-_]/)[0];

  return ["en", "de", "fr"].includes(locale) ? locale : "en";
}

function customPageHref(entry, locale) {
  const variant = (entry && entry[locale]) || entry?.en;
  if (!variant?.slug) return null;

  return { label: variant.label || entry.key, href: `/p/${variant.slug}` };
}

/**
 * Resolves a list of custom page-group entries for the current locale,
 * dropping any whose resolved href collides with a reserved href (e.g. the
 * built-in "Support" link, or - for the footer - the three built-in legal
 * links) or with an earlier entry already resolved in this same list. An
 * admin can pick per-locale page variants independently for EN/DE/FR, so a
 * combination can accidentally resolve to the exact same /p/<slug> as one of
 * those fixed links for one locale even though it looked like a distinct
 * page in the admin UI - this keeps that from rendering as a duplicate.
 */
function resolveDedupedCustomEntries(entries, locale, reservedHrefs) {
  const seen = new Set(reservedHrefs || []);
  const resolved = [];

  entries.forEach((entry) => {
    const variant = customPageHref(entry, locale);
    if (!variant || seen.has(variant.href)) return;

    seen.add(variant.href);
    resolved.push({ entry, href: variant.href, label: variant.label });
  });

  return resolved;
}

/**
 * The "support" link (sidebar nav, account dropdown, its DOM fallback, and
 * the guest access-gate modal) predates cirkla.custom_pages and used to be
 * hardcoded to /p/support everywhere. It now resolves through the same
 * config as any other custom page, falling back to the exact previous
 * hardcoded label/href if no "support" entry is configured at all.
 */
function supportPageEntry() {
  return parseCustomPageEntries().find((entry) => entry.isSupport) || null;
}

function resolvedSupportLink() {
  const entry = supportPageEntry();
  const resolved = entry ? customPageHref(entry, currentSiteLocale()) : null;

  return resolved || { label: String(forumTrans("common", "support")), href: "/p/support" };
}

/**
 * Support's own EN/DE/FR hrefs, so sidebar/dropdown dedup can reserve all of
 * them regardless of which locale is currently being viewed - same reason
 * the footer's built-in links are reserved across all locales, see
 * renderCustomFooterLinks().
 */
function allLocaleSupportHrefs() {
  const entry = supportPageEntry();

  const hrefs = ["en", "de", "fr"].map(
    (locale) => (entry && customPageHref(entry, locale)?.href) || "/p/support",
  );

  return Array.from(new Set(hrefs));
}

function applyCirklaRuntimeSettings() {
  if (typeof document === "undefined") return;

  const rawFontSize = Number(forumSetting("cirkla.font_size_percent", 106));
  const fontSize = Number.isFinite(rawFontSize)
    ? Math.min(120, Math.max(100, rawFontSize))
    : 106;

  document.documentElement.style.setProperty(
    "--cirkla-admin-font-scale",
    `${fontSize}%`,
  );
}

function normalizedLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function configuredMasqueradeLabels(scope) {
  const settingName =
    scope === "directory"
      ? "cirkla.user_card.fields"
      : "cirkla.masquerade.profile_fields";
  const configured = String(
    forumSetting(
      settingName,
      "Organization, Job Title, Organization Type, Region",
    ) || "",
  );

  return new Set(configured.split(",").map(normalizedLabel).filter(Boolean));
}

function shouldShowMasqueradeAnswer(answer, scope) {
  const labels = configuredMasqueradeLabels(scope);
  if (!labels.size) return true;

  return labels.has(normalizedLabel(answerFieldLabel(answer)));
}

function ensureUserDisplayPreferenceStyles() {
  if (typeof document === "undefined") return;

  let style = document.getElementById("cirkla-user-display-preference-styles");
  if (!style) {
    style = document.createElement("style");
    style.id = "cirkla-user-display-preference-styles";
    document.head.appendChild(style);
  }

  const hideSelectors = [];
  const addHide = (scope, selectors) => {
    hideSelectors.push(
      ...selectors.map((selector) =>
        scope === "directory"
          ? `.User--directory ${selector}, body[class*="UserDirectory"] ${selector}`
          : `.UserPage ${selector}`,
      ),
    );
  };

  if (!settingEnabled("cirkla.display.directory_discussions")) {
    addHide("directory", [".item-discussion-count"]);
  }
  if (!settingEnabled("cirkla.display.directory_posts")) {
    addHide("directory", [".item-comment-count"]);
  }
  if (!settingEnabled("cirkla.display.directory_best_answers")) {
    addHide("directory", [".item-best-answer-count", ".item-bestAnswerCount"]);
  }
  if (!settingEnabled("cirkla.display.directory_follow_counts")) {
    addHide("directory", [
      ".item-followers",
      ".item-following",
      ".item-followed",
      ".item-follow-users-followers",
      ".item-follow-users-following",
    ]);
  }

  if (!settingEnabled("cirkla.display.profile_discussions")) {
    addHide("profile", [".item-discussion-count", ".item-discussions"]);
  }
  if (!settingEnabled("cirkla.display.profile_posts")) {
    addHide("profile", [
      ".item-comment-count",
      ".item-comments",
      ".item-posts",
    ]);
  }
  if (!settingEnabled("cirkla.display.profile_best_answers")) {
    addHide("profile", [".item-best-answer-count", ".item-bestAnswerCount"]);
  }
  if (!settingEnabled("cirkla.display.profile_follow_counts")) {
    addHide("profile", [
      ".item-followers",
      ".item-following",
      ".item-followed",
      ".item-follow-users-followers",
      ".item-follow-users-following",
    ]);
  }

  hideSelectors.push(
    ".UserCard .item-points",
    ".UserCard .item-votes",
    ".UserCard .item-gamification",
    ".UserCard [class*='points']",
    ".UserCard [class*='Points']",
    ".UserCard [class*='votes']",
    ".UserCard [class*='Votes']",
  );

  style.textContent = hideSelectors.length
    ? `${hideSelectors.join(",\n")} { display: none !important; }`
    : "";
}

function masqueradeFieldRequiresCompletion(field) {
  return (
    CIRKLA_FORCE_ALL_MASQUERADE_FIELDS ||
    booleanAttribute(field?.attributes?.required)
  );
}

function masqueradeFieldLabel(field) {
  return String(
    field?.attributes?.name || forumTrans("profileCompletion", "fieldFallback"),
  ).trim();
}

function masqueradeFieldHasAnswer(field, value) {
  if (!masqueradeFieldRequiresCompletion(field)) return true;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;

  return String(value ?? "").trim().length > 0;
}

function masqueradeAnswersFromPayload(payload) {
  return new Map(
    (payload?.included || [])
      .filter((item) => item?.type === "masquerade-answer")
      .map((item) => [
        String(item.id),
        String(item?.attributes?.content || ""),
      ]),
  );
}

function masqueradeFieldsFromPayload(payload) {
  return (payload?.data || [])
    .filter((field) => field?.type === "masquerade-field")
    .sort((a, b) => {
      const aSort = Number(a?.attributes?.sort);
      const bSort = Number(b?.attributes?.sort);

      return (
        (Number.isFinite(aSort) ? aSort : 999) -
        (Number.isFinite(bSort) ? bSort : 999)
      );
    });
}

function updateMasqueradeCompletionStateFromPayload(payload) {
  const includedAnswers = masqueradeAnswersFromPayload(payload);
  const fields = masqueradeFieldsFromPayload(payload);

  const answers = fields.reduce((out, field) => {
    out[field.id] = answerValueForMasqueradeField(field, includedAnswers);
    return out;
  }, {});

  const missingFields = fields.filter(
    (field) => !masqueradeFieldHasAnswer(field, answers[field.id]),
  );

  masqueradeCompletionState = {
    checkedAt: Date.now(),
    loading: false,
    loaded: true,
    profileComplete: missingFields.length === 0,
    fields,
    answers,
    missingFields,
    promise: null,
  };

  if (typeof document !== "undefined") {
    document.body.classList.toggle(
      "CirklaMasqueradeCompletionRequired",
      missingFields.length > 0,
    );
  }

  return masqueradeCompletionState;
}

function canUseMasqueradeCompletionGate() {
  return Boolean(
    app.session?.user && app.forum?.attribute?.("canHaveMasquerade"),
  );
}

function isMasqueradeCompletionPublicPath(path = currentPathname()) {
  const normalized = String(path || "").toLowerCase();

  return (
    normalized === MASQUERADE_COMPLETION_ROUTE ||
    normalized.startsWith(`${MASQUERADE_COMPLETION_ROUTE}/`) ||
    normalized === "/p/support" ||
    normalized.startsWith("/p/support") ||
    normalized === "/support" ||
    normalized.startsWith("/support") ||
    normalized === "/p/terms" ||
    normalized.startsWith("/p/terms") ||
    normalized === "/p/privacy" ||
    normalized.startsWith("/p/privacy")
  );
}

function currentMasqueradeReturnPath() {
  if (typeof window === "undefined" || !window.location) return "/";

  const path = `${window.location.pathname || "/"}${
    window.location.search || ""
  }${window.location.hash || ""}`;

  if (!path.startsWith("/") || path.startsWith("//")) return "/";

  return path;
}

function storeMasqueradeCompletionReturnPath() {
  if (typeof window === "undefined" || !window.localStorage) return;

  const path = currentMasqueradeReturnPath();

  if (isMasqueradeCompletionPublicPath(window.location.pathname || "/")) return;

  try {
    window.localStorage.setItem(MASQUERADE_COMPLETION_NEXT_URL_KEY, path);
  } catch (_error) {
    // ignore
  }
}

function consumeMasqueradeCompletionReturnPath() {
  if (typeof window === "undefined" || !window.localStorage) return "/";

  try {
    const path = String(
      window.localStorage.getItem(MASQUERADE_COMPLETION_NEXT_URL_KEY) || "/",
    );

    window.localStorage.removeItem(MASQUERADE_COMPLETION_NEXT_URL_KEY);

    if (!path.startsWith("/") || path.startsWith("//")) return "/";
    if (isMasqueradeCompletionPublicPath(path.split(/[?#]/)[0])) return "/";

    return path;
  } catch (_error) {
    return "/";
  }
}

function fetchMasqueradeCompletionState({ force = false } = {}) {
  if (!canUseMasqueradeCompletionGate()) {
    masqueradeCompletionState = {
      ...masqueradeCompletionState,
      checkedAt: Date.now(),
      loading: false,
      loaded: true,
      profileComplete: true,
      fields: [],
      answers: {},
      missingFields: [],
      promise: null,
    };

    return Promise.resolve(masqueradeCompletionState);
  }

  const cacheFresh =
    masqueradeCompletionState.loaded &&
    Date.now() - masqueradeCompletionState.checkedAt <
      MASQUERADE_COMPLETION_CACHE_MS;

  if (!force && cacheFresh) {
    return Promise.resolve(masqueradeCompletionState);
  }

  if (masqueradeCompletionState.promise) {
    return masqueradeCompletionState.promise;
  }

  masqueradeCompletionState = {
    ...masqueradeCompletionState,
    loading: true,
  };

  masqueradeCompletionState.promise = app
    .request({
      method: "GET",
      url: `${app.forum.attribute("apiUrl")}/masquerade/configure`,
    })
    .then((payload) => updateMasqueradeCompletionStateFromPayload(payload))
    .catch(() => {
      masqueradeCompletionState = {
        ...masqueradeCompletionState,
        checkedAt: Date.now(),
        loading: false,
        promise: null,
      };

      return masqueradeCompletionState;
    });

  return masqueradeCompletionState.promise;
}

function showMasqueradeCompletionAlertOnce() {
  if (!app.alerts?.show) return;

  const now = Date.now();

  if (now - lastMasqueradeCompletionAlertAt < 6000) return;

  lastMasqueradeCompletionAlertAt = now;

  app.alerts.show(
    { type: "warning" },
    forumTrans("profileCompletion", "incompleteAlert"),
  );
}

function enforceMasqueradeCompletionGate({ force = false } = {}) {
  if (!canUseMasqueradeCompletionGate()) return Promise.resolve(true);

  return fetchMasqueradeCompletionState({ force }).then((state) => {
    if (state.profileComplete || isMasqueradeCompletionPublicPath()) {
      return state.profileComplete;
    }

    storeMasqueradeCompletionReturnPath();
    showMasqueradeCompletionAlertOnce();
    closeMobileDrawer();

    if (m?.route?.set) {
      m.route.set(MASQUERADE_COMPLETION_ROUTE);
    } else if (typeof window !== "undefined") {
      window.location.assign(MASQUERADE_COMPLETION_ROUTE);
    }

    return false;
  });
}

function redirectAfterMasqueradeCompletion() {
  const nextPath = consumeMasqueradeCompletionReturnPath();

  if (m?.route?.set) {
    m.route.set(nextPath || "/");
    return;
  }

  if (typeof window !== "undefined") {
    window.location.assign(nextPath || "/");
  }
}

class CirklaMasqueradeSettingsFields extends Component {
  oninit() {
    this.loading = true;
    this.saving = false;
    this.dirty = false;
    this.error = "";
    this.fields = [];
    this.answers = {};
    this.load();
  }

  load() {
    if (!app.session?.user) {
      this.loading = false;
      return;
    }

    app
      .request({
        method: "GET",
        url: `${app.forum.attribute("apiUrl")}/masquerade/configure`,
      })
      .then((payload) => {
        const includedAnswers = new Map(
          (payload?.included || [])
            .filter((item) => item?.type === "masquerade-answer")
            .map((item) => [
              String(item.id),
              String(item?.attributes?.content || ""),
            ]),
        );

        this.fields = (payload?.data || [])
          .filter((field) => field?.type === "masquerade-field")
          .sort((a, b) => {
            const aSort = Number(a?.attributes?.sort);
            const bSort = Number(b?.attributes?.sort);
            return (
              (Number.isFinite(aSort) ? aSort : 999) -
              (Number.isFinite(bSort) ? bSort : 999)
            );
          });

        this.answers = this.fields.reduce((answers, field) => {
          answers[field.id] = answerValueForMasqueradeField(
            field,
            includedAnswers,
          );
          return answers;
        }, {});

        this.loading = false;
        this.error = "";
        m.redraw();
      })
      .catch(() => {
        this.loading = false;
        this.error = String(forumTrans("profileCompletion", "loadError"));
        m.redraw();
      });
  }

  setAnswer(fieldId, value) {
    this.answers[fieldId] = value;
    this.dirty = true;
  }

  missingCompletionFields() {
    return this.fields.filter(
      (field) => !masqueradeFieldHasAnswer(field, this.answers[field.id]),
    );
  }

  completionErrorMessage(missingFields = this.missingCompletionFields()) {
    const labels = missingFields.map(masqueradeFieldLabel).filter(Boolean);

    if (!labels.length) return "";

    return String(
      forumTrans("profileCompletion", "missingFields", {
        fields: labels.join(", "),
      }),
    );
  }

  save(event) {
    event.preventDefault();

    if (this.saving) return;

    const missingFields = this.missingCompletionFields();

    if (missingFields.length) {
      this.error = this.completionErrorMessage(missingFields);
      m.redraw();
      return;
    }

    if (!this.dirty) return;

    this.saving = true;
    this.error = "";

    app
      .request({
        method: "POST",
        url: `${app.forum.attribute("apiUrl")}/masquerade/configure`,
        body: this.answers,
      })
      .then((payload) => {
        this.saving = false;
        this.dirty = false;

        if (payload) {
          const includedAnswers = new Map(
            (payload?.included || [])
              .filter((item) => item?.type === "masquerade-answer")
              .map((item) => [
                String(item.id),
                String(item?.attributes?.content || ""),
              ]),
          );

          this.fields = (payload?.data || this.fields).filter(
            (field) => field?.type === "masquerade-field",
          );

          this.answers = this.fields.reduce((answers, field) => {
            answers[field.id] = answerValueForMasqueradeField(
              field,
              includedAnswers,
            );
            return answers;
          }, {});
        }

        if (payload) {
          updateMasqueradeCompletionStateFromPayload(payload);
        }

        if (app.alerts?.show) {
          app.alerts.show(
            { type: "success" },
            forumTrans("profileCompletion", "saved"),
          );
        }

        if (!this.missingCompletionFields().length) {
          redirectAfterMasqueradeCompletion();
          return;
        }

        m.redraw();
      })
      .catch(() => {
        this.saving = false;
        this.error = String(forumTrans("profileCompletion", "saveError"));
        m.redraw();
      });
  }

  fieldInput(field) {
    const attrs = field?.attributes || {};
    const fieldId = String(field.id);
    const value = this.answers[fieldId] || "";
    const options = optionsFromMasqueradeValidation(attrs.validation);
    const commonAttrs = {
      id: `cirkla-masquerade-field-${fieldId}`,
      className: "FormControl",
      value,
      required: masqueradeFieldRequiresCompletion(field),
      oninput: (event) => this.setAnswer(fieldId, event.target.value),
      onchange: (event) => this.setAnswer(fieldId, event.target.value),
    };

    if (attrs.type === "select" && options.length) {
      return m("select", commonAttrs, [
        m("option", { value: "" }, forumTrans("common", "selectOption")),
        options.map((option) => m("option", { value: option }, option)),
      ]);
    }

    if (
      String(attrs.name || "")
        .trim()
        .toLowerCase() === "bio"
    ) {
      return m("textarea", {
        ...commonAttrs,
        rows: 4,
        maxlength: 500,
      });
    }

    return m("input", {
      ...commonAttrs,
      type: attrs.type === "email" ? "email" : "text",
    });
  }

  view() {
    const canEditMasquerade = Boolean(
      app.forum?.attribute?.("canHaveMasquerade"),
    );

    if (!canEditMasquerade) return null;

    return m(
      "fieldset.FieldSet.Settings-profileDetails.CirklaMasqueradeSettings",
      [
        m("legend", forumTrans("profileCompletion", "sectionTitle")),
        m(
          ".CirklaMasqueradeSettings-copy",
          forumTrans("profileCompletion", "sectionDescription"),
        ),
        this.loading
          ? m(
              ".CirklaMasqueradeSettings-state",
              forumTrans("profileCompletion", "loadingFields"),
            )
          : null,
        this.error
          ? m(".Alert.Alert--error.CirklaMasqueradeSettings-error", this.error)
          : null,

        !this.loading && this.missingCompletionFields().length
          ? m(
              ".Alert.Alert--warning.CirklaMasqueradeSettings-notice",
              this.completionErrorMessage(),
            )
          : null,

        !this.loading && this.fields.length
          ? m(
              "form.CirklaMasqueradeSettings-form",
              { onsubmit: this.save.bind(this) },
              [
                this.fields.map((field) => {
                  const attrs = field?.attributes || {};
                  const label = String(attrs.name || "").trim();

                  return m(".Form-group.CirklaMasqueradeSettings-field", [
                    m("label", { for: `cirkla-masquerade-field-${field.id}` }, [
                      label,
                      masqueradeFieldRequiresCompletion(field)
                        ? m(
                            "span.CirklaMasqueradeSettings-required",
                            ` ${forumTrans("profileCompletion", "requiredMarker")}`,
                          )
                        : null,
                    ]),
                    attrs.description
                      ? m(".helpText", attrs.description)
                      : null,
                    this.fieldInput(field),
                  ]);
                }),
                m(
                  Button,
                  {
                    className: "Button Button--primary",
                    type: "submit",
                    loading: this.saving,
                    disabled: !this.dirty || this.saving,
                  },
                  forumTrans("profileCompletion", "saveFields"),
                ),
              ],
            )
          : null,
        !this.loading && !this.fields.length
          ? m(
              ".CirklaMasqueradeSettings-state",
              forumTrans("profileCompletion", "noFields"),
            )
          : null,
      ],
    );
  }
}

function answerFieldLabel(answer) {
  try {
    const field =
      answer && typeof answer.field === "function" ? answer.field() : null;
    const modelLabel =
      (field && typeof field.name === "function" ? field.name() : null) ||
      (field && typeof field.attribute === "function"
        ? field.attribute("name")
        : null) ||
      field?.data?.attributes?.name;

    if (typeof modelLabel === "string" && modelLabel.trim()) {
      return modelLabel.trim();
    }

    const answerData =
      typeof answer?.data === "function" ? answer.data() : answer?.data;
    const fieldData = answerData?.relationships?.field?.data;

    if (typeof fieldData?.attributes?.name === "string") {
      return String(fieldData.attributes.name || "").trim();
    }
  } catch (_error) {
    return "";
  }

  return "";
}

function renderMasqueradeBioFallback(answer) {
  const label = answerFieldLabel(answer);
  let value = "";

  try {
    value =
      typeof answer?.content === "function"
        ? String(answer.content() ?? "").trim()
        : "";
  } catch (_error) {
    value = "";
  }

  if (!label || !value) return null;

  return m(".Masquerade-Bio-Set", [
    m(".Masquerade-Bio-Field", `${label}: `),
    m(".Masquerade-Bio-Answer", value),
  ]);
}

function createSideNavLinkItem({ key, label, href, iconClass }) {
  const item = document.createElement("li");
  item.className = `item-${key} item-link Navigation-item`;

  const link = document.createElement("a");
  link.className = "Navigation-link";
  link.href = href;
  link.addEventListener("click", (event) => {
    if (!m?.route?.set) return;
    event.preventDefault();
    m.route.set(href);
  });

  const iconNode = document.createElement("i");
  iconNode.className = `icon ${iconClass} Button-icon`;

  const labelNode = document.createElement("span");
  labelNode.className = "CirklaSidebarChatLabelWrap";
  labelNode.textContent = label;

  link.append(iconNode, labelNode);
  item.appendChild(link);
  return item;
}

function ensureSideNavLinkFallbacks() {
  if (!app.session?.user || typeof document === "undefined") return;

  if (!canUseConversationFeatures()) {
    document
      .querySelectorAll(
        ".IndexPage-nav .item-cirkla-private-chat, .sideNav .item-cirkla-private-chat",
      )
      .forEach((node) => (node.closest("li") || node).remove());
    return;
  }

  // Do not add home sidebar links on user profile pages.
  if (isUserProfileRoute()) {
    removeProfileChatPrivateItems();
    return;
  }

  const containers = document.querySelectorAll(
    ".IndexPage-nav ul, .IndexPage-nav .Dropdown-menu",
  );

  containers.forEach((container) => {
    if (!container.querySelector(".item-cirkla-private-chat")) {
      const chatItem = createSideNavLinkItem({
        key: "cirkla-private-chat",
        label: String(forumTrans("sidebar", "chat")),
        href: resolveChatRouteHref() || "/conversations",
        iconClass: "fas fa-comment-alt",
      });
      container.appendChild(chatItem);
    }

    container
      .querySelectorAll(".item-cirkla-private-chat")
      .forEach((item) =>
        item.classList.add("item-link", "Navigation-item"),
      );

    container
      .querySelectorAll(
        ".item-allDiscussions .icon, .item-fof-user-directory .icon, .item-userDirectory .icon",
      )
      .forEach((iconNode) => {
        iconNode.classList.remove("far");
        iconNode.classList.add("fas");
      });
  });

  syncSidebarChatUnreadState();
  removeProfileChatPrivateItems();
}

function buildSendMessageLabel(user) {
  const name = String(user?.displayName?.() || user?.username?.() || "").trim();
  return String(
    name
      ? forumTrans("discussion", "sendMessageTo", { name })
      : forumTrans("discussion", "sendMessage"),
  );
}

function removeProfileChatPrivateItems() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(
      [
        ".UserPage-nav .item-privateDiscussions",
        ".UserPage-nav .item-private-discussions",
        ".UserPage-nav .item-private-discussion",
        ".UserPage-nav .item-cirkla-private-chat",
        ".UserPage-nav .item-cirkla-private-discussion",
        ".UserPage-nav .item-byobu",
        ".UserPage-nav a[href*='/private']",
        ".UserPage-nav a[href*='/conversations']",

        ".UserPage .sideNav .item-privateDiscussions",
        ".UserPage .sideNav .item-private-discussions",
        ".UserPage .sideNav .item-private-discussion",
        ".UserPage .sideNav .item-cirkla-private-chat",
        ".UserPage .sideNav .item-byobu",
        ".UserPage .sideNav a[href*='/private']",
        ".UserPage .sideNav a[href*='/conversations']",
      ].join(","),
    )
    .forEach((node) => {
      const item = node.closest("li") || node;
      item.remove();
    });
}

function renderQuickStartConversationUsers(modalRoot, users, query) {
  // Intentionally disabled:
  // Use the extension's native "USERS" search results only.
  // We only clean up any stale custom list from older injections.
  if (!modalRoot) return;
  modalRoot
    .querySelectorAll(".CirklaQuickConversationUsers")
    .forEach((list) => list.remove());
}

function dedupeConversationSearchResults(modalRoot) {
  if (!modalRoot) return;

  modalRoot
    .querySelectorAll(".Search-results, .Dropdown-menu.Search-results")
    .forEach((list) => {
      const seen = new Set();

      list
        .querySelectorAll("li, .UserSearchResult, .item-user")
        .forEach((item) => {
          const key = normalizeText(item.textContent || "");
          if (!key) return;

          if (seen.has(key)) {
            item.remove();
            return;
          }

          seen.add(key);
        });
    });
}

function findUserByUsernameApi(username) {
  const value = String(username || "").trim();
  if (!value) return Promise.resolve(null);

  return app
    .request({
      method: "GET",
      url: `${app.forum.attribute("apiUrl")}/users`,
      params: { "filter[q]": value, "page[limit]": 8 },
    })
    .then((payload) => {
      const data = payload?.data || [];
      const normalized = value.toLowerCase();
      const users = data
        .map((entry) => app.store.getById("users", String(entry?.id || "")))
        .filter(Boolean)
        .filter(
          (candidate) =>
            String(candidate?.id?.() || "") !==
            String(app.session?.user?.id?.() || ""),
        );

      return (
        users.find(
          (candidate) =>
            String(candidate?.username?.() || "").toLowerCase() === normalized,
        ) ||
        users.find((candidate) =>
          String(candidate?.username?.() || "")
            .toLowerCase()
            .startsWith(normalized),
        ) ||
        users[0] ||
        null
      );
    })
    .catch(() => null);
}

function submitStartConversationFromModal(modalRoot) {
  if (!modalRoot || modalRoot.dataset.cirklaSubmitting === "1") return;

  const recipient = app.cache?.conversationsRecipient || null;
  if (!recipient) return;

  const textarea = modalRoot.querySelector("textarea");
  const messageContents = String(textarea?.value || "").trim();
  if (!messageContents) return;

  modalRoot.dataset.cirklaSubmitting = "1";
  app.store
    .createRecord("conversations")
    .save({ messageContents, recipient: recipient.id() })
    .then((conversation) => {
      safeCloseModal();
      const id = String(conversation?.id?.() || "");
      const baseUrl = String(app.forum.attribute("baseUrl") || "").replace(
        /\/$/,
        "",
      );
      if (id) {
        window.location.assign(`${baseUrl}/conversations/${id}`);
      } else {
        window.location.assign(`${baseUrl}/conversations`);
      }
    })
    .catch(() => {})
    .finally(() => {
      modalRoot.dataset.cirklaSubmitting = "0";
    });
}

// -----------------------------------------------------------------------------
// Start-conversation modal integration
// -----------------------------------------------------------------------------

/**
 * Prefills and stabilizes the start-conversation modal after a profile/card
 * action opens it indirectly.
 */
function enhanceStartConversationModal() {
  if (
    typeof document === "undefined" ||
    !isConversationRoute() ||
    !canUseConversationFeatures()
  ) {
    return;
  }

  const modalRoot = document.querySelector(".StartConversationModal");
  if (!modalRoot) return;

  installStartConversationSubmitPatch(modalRoot);

  const input = modalRoot.querySelector("input.RecipientsInput");
  if (!input) return;

  dedupeConversationSearchResults(modalRoot);

  if (
    (pendingChatStarterUser || pendingChatStarterUsername) &&
    !input.dataset.cirklaPrefilled
  ) {
    input.dataset.cirklaPrefilled = "1";

    const user = pendingChatStarterUser;
    const username = String(
      user?.username?.() || pendingChatStarterUsername || "",
    ).trim();

    input.focus();
    input.value = username;
    input.dispatchEvent(new Event("input", { bubbles: true }));

    if (user) {
      setConversationRecipient(user);
    } else if (username) {
      findUserByUsernameApi(username).then((foundUser) => {
        if (foundUser) {
          setConversationRecipient(foundUser);
          m.redraw();
        }
      });
    }

    pendingChatStarterUser = null;
    pendingChatStarterUsername = "";
  }
}

// -----------------------------------------------------------------------------
// User-control integration
// -----------------------------------------------------------------------------

/**
 * Replaces private-discussion actions with CIRKLA chat actions for user cards.
 */
function installChatUserControlOverride() {
  if (
    app.__cirklaChatUserControlOverrideInstalled ||
    !canUseConversationFeatures()
  ) {
    return;
  }

  extend(UserControls, "controls", function (items, user) {
    if (!resolveDedicatedChatRouteHref()) return;
    if (!app.session?.user || !user || user === app.session.user) return;
    items.add(
      "private-discussion",
      Button.component(
        {
          icon: "fas fa-comments",
          onclick: (event) => {
            event.preventDefault();
            openConversationStarterForUser(user);
          },
        },
        buildSendMessageLabel(user),
      ),
      95,
    );
  });

  app.__cirklaChatUserControlOverrideInstalled = true;
}

function installDirectoryUserCardControls() {
  if (app.__cirklaDirectoryUserCardControlsInstalled) return;

  extend(UserControls, "controls", function (items, user) {
    if (!isUserDirectoryRoute() || !user || items.has("cirkla-view-profile")) {
      return;
    }

    items.add(
      "cirkla-view-profile",
      Button.component(
        {
          icon: "fas fa-user",
          onclick: (event) => {
            event.preventDefault();
            m.route.set(app.route.user(user));
          },
        },
        forumTrans("directory", "viewProfile"),
      ),
      110,
    );
  });

  app.__cirklaDirectoryUserCardControlsInstalled = true;
}

function resolveCurrentUserEmail() {
  const user = app.session?.user;
  if (!user) return "";

  const candidates = [
    typeof user.email === "function" ? user.email() : null,
    typeof user.attribute === "function" ? user.attribute("email") : null,
    user.data?.attributes?.email,
  ];

  return String(
    candidates.find((value) => typeof value === "string" && value.trim()) || "",
  ).trim();
}

/**
 * Fetches the current user with email included when the session payload does
 * not already expose an email attribute.
 */
function fetchCurrentUserEmail() {
  const user = app.session?.user;
  if (!user) return Promise.resolve("");

  const immediateEmail = resolveCurrentUserEmail();
  if (immediateEmail) return Promise.resolve(immediateEmail);

  const userId =
    (typeof user.id === "function" ? user.id() : null) || user.data?.id || null;

  if (!userId || !app.store?.find) return Promise.resolve("");

  return app.store
    .find("users", userId)
    .then((record) => {
      const candidates = [
        typeof record?.email === "function" ? record.email() : null,
        typeof record?.attribute === "function"
          ? record.attribute("email")
          : null,
        record?.data?.attributes?.email,
      ];

      return String(
        candidates.find((value) => typeof value === "string" && value.trim()) ||
          "",
      ).trim();
    })
    .catch(() => "");
}

// -----------------------------------------------------------------------------
// Newsletter signup component
// -----------------------------------------------------------------------------

class CirklaNewsletterBox extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    this.agree = false;
    this.email = resolveCurrentUserEmail();
    this.isEmailValid = true;
    this.isLoading = false;
    this.isSubscribed = false;
    this.isAvailable = true;
    this.errorMessage = "";
    this.initializeEmailAndSubscriptionState();
  }

  onFieldChange(value) {
    return this.emailRegex.test(value);
  }

  initializeEmailAndSubscriptionState() {
    return fetchCurrentUserEmail()
      .then((email) => {
        if (email) {
          this.email = email;
          this.isEmailValid = this.onFieldChange(this.email);
        }

        return this.loadSubscriptionState();
      })
      .finally(() => {
        m.redraw();
      });
  }

  loadSubscriptionState() {
    if (!app.session?.user) return Promise.resolve();

    this.isLoading = true;
    this.errorMessage = "";

    return app
      .request({
        method: "GET",
        url: `${app.forum.attribute("apiUrl")}/cirkla/newsletter/status`,
      })
      .then((response) => {
        const profileEmail = String(response?.email || "").trim();

        if (profileEmail) {
          this.email = profileEmail;
          this.isEmailValid = this.onFieldChange(this.email);
        }

        this.isSubscribed = !!response?.subscribed;
        if (this.isSubscribed) {
          this.agree = true;
        }
      })
      .catch(() => {
        this.isAvailable = false;
        this.errorMessage = "";
      })
      .finally(() => {
        this.isLoading = false;
        m.redraw();
      });
  }

  toggleSubscription(e) {
    e.preventDefault();

    this.email = String(this.email || "").trim();
    this.isEmailValid = this.onFieldChange(this.email);
    this.errorMessage = "";

    if (!this.email || !this.isEmailValid) {
      this.errorMessage = String(forumTrans("newsletter", "invalidEmail"));
      return;
    }

    if (!this.isSubscribed && !this.agree) {
      this.errorMessage = String(forumTrans("newsletter", "consentRequired"));
      return;
    }

    this.isLoading = true;

    const request = this.isSubscribed
      ? app.request({
          method: "DELETE",
          url: `${app.forum.attribute("apiUrl")}/cirkla/newsletter/delete`,
          params: { email: this.email },
        })
      : app.request({
          method: "POST",
          url: `${app.forum.attribute("apiUrl")}/cirkla/newsletter/add`,
          body: { email: this.email },
        });

    return request
      .then(() => {
        this.isSubscribed = !this.isSubscribed;
        if (this.isSubscribed) {
          this.agree = true;
        }
        app.alerts.show(
          { type: "success" },
          this.isSubscribed
            ? forumTrans("newsletter", "successSubscribe")
            : forumTrans("newsletter", "successUnsubscribe"),
        );
      })
      .catch((error) => {
        this.errorMessage =
          String(
            error?.response?.errors?.[0]?.detail || error?.message || "",
          ).trim() || String(forumTrans("newsletter", "error"));
      })
      .finally(() => {
        this.isLoading = false;
        m.redraw();
      });
  }

  view() {
    if (!this.isAvailable) {
      return null;
    }

    return m("div", { className: "newsletter-optin-container" }, [
      m("div.newsletter-heading", [
        m(
          "span.newsletter-headingIcon",
          { "aria-hidden": "true" },
          m("i.far.fa-envelope"),
        ),
        m(
          "h3",
          { className: "newsletter-title" },
          forumTrans("newsletter", "title"),
        ),
      ]),
      m(
        "p",
        { className: "newsletter-description" },
        forumTrans("newsletter", "description"),
      ),
      m("form", { onsubmit: (e) => this.toggleSubscription(e) }, [
        m("input", {
          className: "FormControl",
          type: "email",
          placeholder: "email@example.com",
          value: this.email,
          disabled: this.isLoading,
          oninput: (e) => {
            this.email = e.target.value;
            this.isEmailValid = this.onFieldChange(this.email);
          },
        }),
        !this.isEmailValid
          ? m(
              "small",
              { className: "hint-text", style: { color: "red" } },
              forumTrans("newsletter", "invalidEmail"),
            )
          : null,
        this.errorMessage
          ? m(
              "small",
              {
                className: "hint-text",
                style: { color: "red", display: "block" },
              },
              this.errorMessage,
            )
          : null,
        this.isSubscribed
          ? m("div.newsletter-status", [
              m("i.fas.fa-check-circle", { "aria-hidden": "true" }),
              m("span", forumTrans("newsletter", "subscribed")),
            ])
          : m("label", { className: "newsletter-agree" }, [
              m("input", {
                type: "checkbox",
                checked: this.agree,
                disabled: this.isLoading,
                onchange: (e) => {
                  this.agree = !!e.target.checked;
                },
              }),
              m("span", { className: "newsletter-agree-label" }, [
                `${forumTrans("newsletter", "consentPrefix")} `,
                m(
                  "a",
                  {
                    href: CIRKLA_FOOTER_PAGE_ROUTES[currentFooterLocale()].privacy,
                    target: "_blank",
                    rel: "noreferrer",
                  },
                  forumTrans("newsletter", "privacyPolicy"),
                ),
                ".",
              ]),
            ]),
        m(
          "button",
          {
            className: "Button Button--primary newsletter-button",
            type: "submit",
            disabled:
              this.isLoading ||
              !this.isEmailValid ||
              (!this.isSubscribed && !this.agree),
          },
          [
            this.isLoading
              ? m("span", {
                  className: "CirklaNewsletterSpinner",
                  "aria-hidden": "true",
                })
              : null,
            this.isLoading
              ? this.isSubscribed
                ? forumTrans("newsletter", "unsubscribing")
                : forumTrans("newsletter", "subscribing")
              : this.isSubscribed
                ? forumTrans("newsletter", "unsubscribe")
                : forumTrans("newsletter", "subscribe"),
          ],
        ),
      ]),
    ]);
  }
}

// -----------------------------------------------------------------------------
// Invite-link modal
// -----------------------------------------------------------------------------

class UserInviteLinkModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.inviteUrl = "";
    this.copySuccess = false;
    this.errorMessage = "";
  }

  className() {
    return "Modal--small CirklaUserInviteModal";
  }

  title() {
    return forumTrans("invite", "modalTitle");
  }

  content() {
    return m("div", { className: "Modal-body" }, [
      m("div", { className: "Form" }, [
        m(
          "p",
          { className: "helpText" },
          forumTrans("invite", "helpText"),
        ),
        this.errorMessage
          ? m(
              "p",
              { className: "helpText", style: { color: "#b42318" } },
              this.errorMessage,
            )
          : null,
        this.inviteUrl
          ? m("div", { className: "Form-group" }, [
              m("label", forumTrans("invite", "linkLabel")),
              m("input", {
                className: "FormControl",
                type: "text",
                value: this.inviteUrl,
                readonly: true,
                onclick: (event) => event.target.select(),
              }),
              this.copySuccess
                ? m(
                    "p",
                    { className: "helpText" },
                    forumTrans("invite", "linkCopied"),
                  )
                : null,
            ])
          : null,
        m("div", { className: "Form-group" }, [
          m(
            Button,
            {
              className: "Button Button--primary Button--block",
              loading: this.loading,
              disabled: this.loading,
              onclick: () => this.generateInvite(),
            },
            this.inviteUrl
              ? forumTrans("invite", "generateNew")
              : forumTrans("invite", "generateLink"),
          ),
        ]),
        this.inviteUrl
          ? m("div", { className: "Form-group" }, [
              m(
                Button,
                {
                  className: "Button Button--block",
                  disabled: this.loading,
                  onclick: () => this.copyInviteLink(),
                },
                forumTrans("invite", "copyLink"),
              ),
            ])
          : null,
      ]),
    ]);
  }

  generateInvite() {
    this.loading = true;
    this.copySuccess = false;
    this.errorMessage = "";

    return app
      .request({
        method: "POST",
        url: `${app.forum.attribute("apiUrl")}/cirkla/user-invite`,
        errorHandler: this.onerror.bind(this),
      })
      .then((response) => {
        this.inviteUrl = String(
          response?.data?.attributes?.inviteUrl || "",
        ).trim();
        if (!this.inviteUrl) {
          throw new Error(String(forumTrans("invite", "missingUrl")));
        }
      })
      .catch((error) => {
        this.errorMessage =
          String(error?.message || "").trim() ||
          String(forumTrans("invite", "createError"));
      })
      .finally(() => {
        this.loaded();
        m.redraw();
      });
  }

  copyInviteLink() {
    if (!this.inviteUrl) return;

    const copyWithFallback = () => {
      const input = document.querySelector(
        ".CirklaUserInviteModal .FormControl",
      );
      if (!input) return false;

      input.focus();
      input.select();

      try {
        return document.execCommand("copy");
      } catch (error) {
        return false;
      }
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(this.inviteUrl)
        .then(() => {
          this.copySuccess = true;
          m.redraw();
        })
        .catch(() => {
          this.copySuccess = copyWithFallback();
          m.redraw();
        });
      return;
    }

    this.copySuccess = copyWithFallback();
    m.redraw();
  }
}

// -----------------------------------------------------------------------------
// Flarum initializers
// -----------------------------------------------------------------------------

app.initializers.add("cirkla-title-prefix", () => {
  const mithril = window.m;
  override(IndexPage.prototype, "sidebarItems", function (original) {
    const items = original.call(this);
    items.remove("newsLetterBox");
    items.remove("justoverclockNewsletterBox");
    items.remove("justoverclock-newsletter");
    return items;
  });

  const PREFIX = "CIRKLA Community";
  const SEP = " - ";
  const m = window.m;

  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const forumTitle =
    (app.forum &&
      typeof app.forum.attribute === "function" &&
      app.forum.attribute("title")) ||
    (app.forum &&
      app.forum.data &&
      app.forum.data.attributes &&
      app.forum.data.attributes.title) ||
    "";

  const SUFFIXES = ["A FreeFlarum Forum", forumTitle]
    .map((s) => String(s || "").trim())
    .filter((s, i, arr) => s && arr.indexOf(s) === i);

  const stripPrefix = (title) => {
    const re = new RegExp(
      "^" + escapeRegExp(PREFIX) + "\\s*(?:[+\\-|]\\s*)?",
      "i",
    );

    return String(title || "")
      .replace(re, "")
      .trim();
  };

  const splitParts = (title) =>
    String(title || "")
      .trim()
      .split(/\s+(?:-+|\|)\s+/)
      .map((p) => p.trim())
      .filter(Boolean);

  const stripKnownSuffixes = (parts) => {
    const isSuffix = (p) =>
      SUFFIXES.some((s) => s && p.toLowerCase() === s.toLowerCase());

    while (parts.length && isSuffix(parts[parts.length - 1])) parts.pop();

    return parts;
  };

  const buildTitleFrom = (rawTitle) => {
    const noPrefix = stripPrefix(rawTitle);
    let parts = splitParts(noPrefix);

    parts = parts.filter((p) => p.toLowerCase() !== PREFIX.toLowerCase());
    parts = stripKnownSuffixes(parts);

    const pageTitle = parts.join(" - ").trim();

    return pageTitle ? `${PREFIX}${SEP}${pageTitle}` : PREFIX;
  };

  const applyTitle = () => {
    const next = buildTitleFrom(document.title);
    if (document.title !== next) document.title = next;
  };

  const patchFn = (fnName) => {
    if (typeof app[fnName] !== "function") return false;

    const original = app[fnName].bind(app);
    app[fnName] = (title, ...rest) => original(buildTitleFrom(title), ...rest);

    return true;
  };

  patchFn("setTitle");
  patchFn("updateTitle");

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const r = originalRouteSet.apply(this, arguments);
      setTimeout(applyTitle, 0);
      setTimeout(applyTitle, 50);
      setTimeout(applyTitle, 250);
      return r;
    };
  }

  const titleEl = document.querySelector("title");
  if (titleEl && typeof MutationObserver !== "undefined") {
    new MutationObserver(() => applyTitle()).observe(titleEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  applyTitle();
  setTimeout(applyTitle, 50);
  setTimeout(applyTitle, 250);
});

// -----------------------------------------------------------------------------
// Shell cleanup utilities
// -----------------------------------------------------------------------------

const CIRKLA_FOOTER_PAGE_ROUTES = {
  en: {
    imprint: "/p/legal-notice",
    privacy: "/p/privacy",
    userGuides: "/p/user-guides",
  },
  de: {
    imprint: "/p/impressum",
    privacy: "/p/datenschutzerklaerung",
    userGuides: "/p/benutzerhandbuch",
  },
  fr: {
    imprint: "/p/mentions-legales",
    privacy: "/p/politique-de-confidentialite",
    userGuides: "/p/guides-utilisateur",
  },
};

function currentFooterLocale() {
  if (typeof document === "undefined") return "en";

  const rawLocale = document.documentElement.lang || app.data?.locale || "en";
  const locale = String(rawLocale).toLowerCase().split(/[-_]/)[0];

  return CIRKLA_FOOTER_PAGE_ROUTES[locale] ? locale : "en";
}

function footerTranslationText(value) {
  if (Array.isArray(value)) return value.map(footerTranslationText).join("");
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (value.children !== undefined) return footerTranslationText(value.children);

  return String(value);
}

/**
 * Appends any admin-linked page groups placed in the footer (via "Link
 * Pages" or the "where to show" checkboxes) after the three built-in legal
 * links, keyed by data-cirkla-footer-key so re-runs (this fires on every DOM
 * mutation via the same observer that drives patchFooterLabel) update or
 * remove existing nodes in place instead of duplicating them. Only touches
 * the DOM when something actually changed, so it doesn't retrigger its own
 * MutationObserver in a loop.
 */
function renderCustomFooterLinks(nav) {
  const locale = currentSiteLocale();

  // Reserve the built-in links across every locale, not just the one being
  // viewed - fof-pages slugs are global (not per-locale), so a page used as
  // e.g. the German "imprint" link is the exact same destination no matter
  // which locale bucket an admin happens to file it under in a custom
  // group. Checking only the current locale's routes meant an admin who
  // mismatched their EN/DE/FR picks (e.g. put the English page in the
  // German slot) would see the duplicate silently reappear whenever they
  // switched locale, since only the one locale that happened to line up got
  // deduped.
  const reservedHrefs = Object.values(CIRKLA_FOOTER_PAGE_ROUTES).flatMap((routes) =>
    Object.values(routes),
  );

  const desired = resolveDedupedCustomEntries(
    parseCustomPageEntries().filter((entry) => entry.placements.has("footer")),
    locale,
    reservedHrefs,
  ).map((item) => ({ key: item.entry.key, href: item.href, label: item.label }));

  const existingCustom = Array.from(nav.querySelectorAll("a[data-cirkla-footer-key]"));

  existingCustom.forEach((link) => {
    if (!desired.some((item) => item.key === link.getAttribute("data-cirkla-footer-key"))) {
      link.remove();
    }
  });

  desired.forEach((item) => {
    let link = existingCustom.find(
      (el) => el.isConnected && el.getAttribute("data-cirkla-footer-key") === item.key,
    );

    if (!link) {
      link = document.createElement("a");
      link.setAttribute("data-cirkla-footer-key", item.key);
      nav.appendChild(link);
    }

    if (link.textContent !== item.label) link.textContent = item.label;
    if (link.getAttribute("href") !== item.href) link.setAttribute("href", item.href);
  });
}

function patchFooterLabel() {
  if (typeof document === "undefined") return;

  const footer = document.querySelector(".CirklaFooter");
  if (!footer) return;

  const links = footer.querySelectorAll(".CirklaFooter-links a");
  const locale = currentFooterLocale();
  const routes = CIRKLA_FOOTER_PAGE_ROUTES[locale];
  const footerLinkKeys = ["imprint", "privacy", "userGuides"];

  footerLinkKeys.forEach((key, index) => {
    const link = links[index];
    if (!link) return;

    const label = footerTranslationText(forumTrans("footer", key));
    if (link.textContent !== label) link.textContent = label;
    if (link.getAttribute("href") !== routes[key]) link.setAttribute("href", routes[key]);
  });

  const footerLinks = footer.querySelector(".CirklaFooter-links");
  if (footerLinks) {
    const ariaLabel = footerTranslationText(forumTrans("footer", "legalLinks"));
    if (footerLinks.getAttribute("aria-label") !== ariaLabel) {
      footerLinks.setAttribute("aria-label", ariaLabel);
    }

    renderCustomFooterLinks(footerLinks);
  }

  const walker = document.createTreeWalker(footer, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();

  while (current) {
    if (current.nodeValue && current.nodeValue.includes("HTW Saar")) {
      current.nodeValue = current.nodeValue.replace(/HTW Saar/g, "htw saar");
    }

    current = walker.nextNode();
  }
}

/**
 * Flarum's own locale dropdown always reloads the page at the current URL
 * (saving the new locale to the user's preferences, or - for guests - a
 * "locale" cookie, then calling window.location.reload()) - there's no
 * client-side route change to hook into. If the member is currently reading
 * a linked page (e.g. /p/support) and picks a new language, that reload
 * would land them back on the exact same (now wrong-language) page under
 * the new language's chrome. A capture-phase click listener runs before
 * Flarum's own bubble-phase handler on the dropdown item (same technique as
 * installFofPagesPlacementCapture in admin.js), so it can cancel that
 * default reload-in-place and instead do the same preference/cookie save
 * followed by a redirect straight to that page's variant for the new
 * locale.
 */
function installLinkedPageLocaleRedirect() {
  if (typeof document === "undefined" || app.__cirklaLinkedPageLocaleRedirectInstalled) {
    return;
  }
  app.__cirklaLinkedPageLocaleRedirectInstalled = true;

  document.addEventListener(
    "click",
    (event) => {
      if (!(event.target instanceof Element)) return;

      const item = event.target.closest(".item-locale .Dropdown-menu button, .item-locale .Dropdown-menu a");
      if (!item) return;

      const match = getCurrentPathname().match(/^\/p\/([^/]+)\/?$/);
      if (!match) return;

      const entry = parseCustomPageEntries().find((candidate) =>
        ["en", "de", "fr"].some((locale) => candidate[locale]?.slug === match[1]),
      );
      if (!entry) return;

      const locales = app.data?.locales || {};
      const label = item.textContent.trim();
      const targetLocale = Object.keys(locales).find((code) => locales[code] === label);
      if (!targetLocale) return;

      const resolved = customPageHref(entry, targetLocale);
      if (!resolved || resolved.href === getCurrentPathname()) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      const persistLocale = app.session?.user
        ? app.session.user.save({
            preferences: { ...app.session.user.preferences(), locale: targetLocale },
          })
        : Promise.resolve().then(() => {
            const expires = new Date();
            expires.setFullYear(expires.getFullYear() + 1);
            document.cookie = `locale=${targetLocale}; path=/; expires=${expires.toUTCString()}`;
          });

      Promise.resolve(persistLocale)
        .catch(() => {})
        .then(() => {
          window.location.href = resolved.href;
        });
    },
    true,
  );
}

app.initializers.add("cirkla-footer-label", () => {
  const applyFooterLabel = () => {
    patchFooterLabel();
    setTimeout(patchFooterLabel, 0);
    setTimeout(patchFooterLabel, 100);
  };

  applyFooterLabel();
  installLinkedPageLocaleRedirect();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyFooterLabel();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => patchFooterLabel()).observe(document.body, {
      childList: true,
      subtree: true,
    });

    new MutationObserver(() => applyFooterLabel()).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"],
    });
  }
});

// -----------------------------------------------------------------------------
// Composer and navigation utilities
// -----------------------------------------------------------------------------

function forceUserDirectoryListMode() {
  const attributes = app.forum?.data?.attributes;

  if (attributes) {
    attributes.userDirectorySmallCards = false;
  }
}

function syncComposerVisibilityClass() {
  if (typeof document === "undefined") return;

  const body = document.body;
  if (!body) return;

  const hasVisibleComposer = Array.from(
    document.querySelectorAll(".App-composer .Composer.visible"),
  ).some((node) => {
    return !node.classList.contains("minimized");
  });

  body.classList.toggle("CirklaComposerVisible", hasVisibleComposer);
}

function forceComposerDomClosed() {
  if (typeof document === "undefined") return;

  document.body?.classList.remove("CirklaComposerVisible");

  document
    .querySelectorAll(".composer-backdrop, .Composer-backdrop")
    .forEach((node) => node.remove());

  document
    .querySelectorAll(".App-composer .Composer.visible")
    .forEach((composer) => {
      if (!(composer instanceof HTMLElement)) return;
      composer.classList.remove("visible", "normal", "fullScreen", "active");
      composer.setAttribute("aria-hidden", "true");
      composer.style.display = "none";
    });
}

function closeVisibleComposer() {
  const composerState = app.composer || app.composerState;

  forceComposerDomClosed();

  if (composerState) {
    composerState.position = "hidden";
    composerState.body = { attrs: {} };
    composerState.onExit = null;
  }

  if (composerState && typeof composerState.hide === "function") {
    composerState.hide();
    forceComposerDomClosed();
    window.requestAnimationFrame(() => {
      forceComposerDomClosed();
      syncComposerVisibilityClass();
    });
    return;
  }

  if (composerState && typeof composerState.clear === "function") {
    composerState.clear();
    if (m && typeof m.redraw === "function") m.redraw();
    forceComposerDomClosed();
    window.requestAnimationFrame(() => {
      forceComposerDomClosed();
      syncComposerVisibilityClass();
    });
    return;
  }

  if (composerState && typeof composerState.close === "function") {
    composerState.close();
    if (m && typeof m.redraw === "function") m.redraw();
    forceComposerDomClosed();
    window.requestAnimationFrame(() => {
      forceComposerDomClosed();
      syncComposerVisibilityClass();
    });
    return;
  }

  const fallbackButton = document.querySelector(
    [
      ".App-composer .Composer.visible .Composer-controls .item-close .Button",
      ".App-composer .Composer.visible .Composer-controls .item-closeFullScreen .Button",
      ".App-composer .Composer.visible .Composer-controls [aria-label*='Close']",
      ".App-composer .Composer.visible .Composer-controls [title*='Close']",
      ".App-composer .Composer.visible .Composer-controls [aria-label*='Schlie']",
      ".App-composer .Composer.visible .Composer-controls [title*='Schlie']",
    ].join(","),
  );

  if (fallbackButton instanceof HTMLElement) {
    fallbackButton.click();
    window.requestAnimationFrame(() => {
      forceComposerDomClosed();
      syncComposerVisibilityClass();
    });
    return;
  }

  forceComposerDomClosed();
}

function handleComposerCloseButtonEvent(event) {
  const target = event.target;

  if (!(target instanceof Element)) return;
  if (!target.closest(".CirklaComposerCloseButton")) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  closeVisibleComposer();
}

function installComposerCloseButtonHandler() {
  if (typeof document === "undefined") return;
  if (document.body?.dataset.cirklaComposerCloseHandler === "1") return;

  if (document.body) {
    document.body.dataset.cirklaComposerCloseHandler = "1";
  }

  document.addEventListener(
    "pointerdown",
    handleComposerCloseButtonEvent,
    true,
  );
  document.addEventListener("click", handleComposerCloseButtonEvent, true);
}

function removeRankingsSidebarItems() {
  if (typeof document === "undefined") return;

  const selectors = [
    ".sideNav .item-rankings",
    ".IndexPage-nav .item-rankings",
    ".Dropdown-menu .item-rankings",
    ".sideNav li[class*='rank']",
    ".IndexPage-nav li[class*='rank']",
  ];

  document.querySelectorAll(selectors.join(",")).forEach((node) => {
    node.remove();
  });

  document.querySelectorAll("a, button").forEach((node) => {
    const href =
      typeof node.getAttribute === "function" ? node.getAttribute("href") : "";
    const text = String(node.textContent || "")
      .trim()
      .toLowerCase();

    if (
      (href && href.toLowerCase().includes("rankings")) ||
      text === "rankings"
    ) {
      const item = node.closest("li");
      if (item) item.remove();
    }
  });
}

// -----------------------------------------------------------------------------
// Chat unread badge utilities
// -----------------------------------------------------------------------------

function getUnreadChatCount() {
  const user = app.session?.user;
  if (!user) return 0;

  try {
    if (typeof user.unreadMessages === "function") {
      return Math.max(0, Number(user.unreadMessages() || 0));
    }

    return Math.max(0, Number(user.unreadMessages || 0));
  } catch (_error) {
    return 0;
  }
}

function sidebarChatHasUnread() {
  return getUnreadChatCount() > 0;
}

function buildSidebarChatLabel(label = String(forumTrans("sidebar", "chat"))) {
  return m("span", { className: "CirklaSidebarChatLabelWrap" }, [
    m("span", label),
  ]);
}

function syncSidebarChatUnreadState() {
  if (typeof document === "undefined") return;

  try {
    const hasUnread = sidebarChatHasUnread();

    const chatLinks = Array.from(
      document.querySelectorAll(
        [
          ".item-cirkla-private-chat .Navigation-link",
          ".item-cirkla-private-chat-fallback .Navigation-link",
          ".sideNav a[href='/conversations']",
          ".sideNav a[href*='/conversations']",
          ".IndexPage-nav a[href='/conversations']",
          ".IndexPage-nav a[href*='/conversations']",
          ".App-drawer a[href='/conversations']",
          ".App-drawer a[href*='/conversations']",
          ".App-navigation a[href='/conversations']",
          ".App-navigation a[href*='/conversations']",
        ].join(","),
      ),
    ).filter((link) => {
      if (!(link instanceof HTMLElement)) return false;
      if (link.closest(".ConversationsList")) return false;
      return true;
    });

    chatLinks.forEach((link) => {
      link.classList.toggle("has-unread", hasUnread);

      const chatIcon = link.querySelector(".Button-icon, .icon");
      if (chatIcon) {
        chatIcon.classList.remove("far", "fa-comments", "fa-comment-dots");
        chatIcon.classList.add("fas", "fa-comment-alt");
      }

      const dots = Array.from(link.querySelectorAll(".CirklaUnreadDot"));
      const existingDot = dots[0];

      dots.slice(1).forEach((dot) => dot.remove());

      if (!hasUnread) {
        existingDot?.remove();
        return;
      }

      if (existingDot) return;

      const dot = document.createElement("span");
      dot.className = "CirklaUnreadDot";
      const unreadLabel = String(forumTrans("discussion", "unreadMessages"));
      dot.setAttribute("aria-label", unreadLabel);
      dot.setAttribute("title", unreadLabel);

      link.appendChild(dot);
    });
  } catch (_error) {
    // Unread decorations should never interrupt Flarum's page boot.
  }
}

function conversationUnreadCount(conversation) {
  try {
    const attrs = conversation?.data?.attributes || {};

    /*
     * Use only CIRKLA's backend-calculated unread value.
     * Do not use unReadCount, totalMessages, or newMessages here.
     */
    const count = Number(attrs.cirklaUnreadCount || 0);

    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch (_error) {
    return 0;
  }
}

function conversationId(conversation) {
  try {
    if (typeof conversation?.id === "function") {
      const id = conversation.id();

      if (id !== undefined && id !== null && id !== "") {
        return String(id);
      }
    }
  } catch (_error) {
    // Fall back to raw JSON:API id below.
  }

  const id = conversation?.data?.id;

  return id === undefined || id === null ? "" : String(id);
}

function unreadConversationSources() {
  const conversations = [];

  if (Array.isArray(app.cache?.conversations)) {
    conversations.push(...app.cache.conversations);
  }

  if (typeof app.store?.all === "function") {
    try {
      conversations.push(...(app.store.all("conversations") || []));
    } catch (_error) {
      // The conversation model may not be registered during early boot.
    }
  }

  const seen = new Set();

  return conversations.filter((conversation) => {
    const id = conversationId(conversation);
    if (!id) return true;
    if (seen.has(id)) return false;

    seen.add(id);
    return true;
  });
}

function conversationListItemId(item) {
  if (!(item instanceof HTMLElement)) return "";

  const dataId =
    item.dataset.cirklaConversationId ||
    item.dataset.conversationId ||
    item.dataset.id ||
    "";

  if (dataId) return String(dataId);

  const href =
    item.getAttribute("href") ||
    item.querySelector("a[href*='/conversations/']")?.getAttribute("href") ||
    "";

  const match = String(href).match(/\/conversations\/([^/?#]+)/);

  return match ? decodeURIComponent(match[1]) : "";
}

function syncDesktopConversationIdsFromMobile() {
  if (typeof document === "undefined") return;

  const mobileRows = Array.from(
    document.querySelectorAll(
      ".ConversationsList-list.mobile .UserListItem[href*='/conversations/']",
    ),
  ).filter((row) => row instanceof HTMLElement);

  const desktopRows = Array.from(
    document.querySelectorAll(".ConversationsList-list.desktop .UserListItem"),
  ).filter((row) => row instanceof HTMLElement);

  desktopRows.forEach((desktopRow, index) => {
    const mobileRow = mobileRows[index];
    if (!mobileRow) return;

    const id = conversationListItemId(mobileRow);

    if (id) {
      desktopRow.dataset.cirklaConversationId = id;
    }
  });
}

function hideNativeConversationUnreadCounts(item) {
  if (!(item instanceof HTMLElement)) return;

  item
    .querySelectorAll(
      [
        ".Button-badge",
        ".badge",
        ".Badge",
        ".UnreadBadge",
        ".item-unread",
        "[class*='badge']",
        "[class*='Badge']",
      ].join(","),
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      if (
        node.classList.contains("CirklaConversationUnreadDot") ||
        node.classList.contains("CirklaUnreadDot")
      ) {
        return;
      }

      const text = String(node.textContent || "").trim();

      if (/^\d+$/.test(text)) {
        node.classList.add("CirklaNativeUnreadCountHidden");
        node.setAttribute("aria-hidden", "true");
      }
    });
}

function setConversationRowUnreadState(item, count) {
  if (!(item instanceof HTMLElement)) return;

  const hasUnread = count > 0;

  item.classList.toggle("has-unread", hasUnread);
  item.dataset.cirklaUnreadCount = hasUnread ? String(count) : "0";

  const dots = Array.from(
    item.querySelectorAll(".CirklaConversationUnreadDot"),
  );
  const existingDot = dots[0];

  dots.slice(1).forEach((dot) => dot.remove());

  if (!hasUnread) {
    existingDot?.remove();
    return;
  }

  const label = String(forumTrans("discussion", "unreadCount", { count }));
  const dot = existingDot || document.createElement("span");

  if (!existingDot) {
    dot.className = "CirklaConversationUnreadDot";
    item.appendChild(dot);
  }

  dot.setAttribute("aria-label", label);
  dot.setAttribute("title", label);
}

function syncConversationListUnreadState() {
  if (typeof document === "undefined") return;

  try {
    const totalUnread = getUnreadChatCount();

    const listItems = Array.from(
      document.querySelectorAll(".ConversationsList .UserListItem"),
    ).filter((item) => item instanceof HTMLElement);

    if (!listItems.length) return;

    syncDesktopConversationIdsFromMobile();

    const conversations = unreadConversationSources();
    const unreadById = new Map();

    conversations.forEach((conversation) => {
      const id = conversationId(conversation);
      const count = conversationUnreadCount(conversation);

      if (id) {
        unreadById.set(id, count);
      }
    });

    listItems.forEach((item) => {
      hideNativeConversationUnreadCounts(item);

      const id = conversationListItemId(item);
      let count = 0;

      if (totalUnread > 0 && id && unreadById.has(id)) {
        count = unreadById.get(id);
      }

      setConversationRowUnreadState(item, count);
    });
  } catch (_error) {
    // Keep the conversation page usable even if extension internals change.
  }
}

function installSidebarChatUnreadIndicatorSync() {
  if (typeof window === "undefined" || chatUnreadIndicatorInterval) return;

  const runSync = () => {
    syncSidebarChatUnreadState();
    syncConversationListUnreadState();
  };

  runSync();

  chatUnreadIndicatorInterval = window.setInterval(runSync, 1200);

  window.addEventListener("focus", runSync, { passive: true });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      runSync();
    }
  });
}

function findTagModalSubmitButton(modal) {
  return (
    modal.querySelector(
      ".TagSelectionModal-form-submit button[type='submit']",
    ) ||
    modal.querySelector(".TagSelectionModal-form-submit .Button") ||
    modal.querySelector("button[type='submit']")
  );
}

function findTagModalBypassButton(modal) {
  const buttons = Array.from(
    modal.querySelectorAll(
      ".TagSelectionModal-controls button, .TagSelectionModal-controls .Button",
    ),
  ).filter((button) => button instanceof HTMLElement);

  return (
    buttons.find((button) =>
      /bypass|without|skip|no tag|requirement/i.test(
        String(button.textContent || button.getAttribute("aria-label") || ""),
      ),
    ) || buttons[0]
  );
}

function submitTagSelectionModal(modal) {
  const submitButton = findTagModalSubmitButton(modal);
  if (submitButton instanceof HTMLElement) {
    submitButton.click();
    return;
  }

  const form = modal.querySelector("form");
  if (form instanceof HTMLFormElement) {
    form.dispatchEvent(
      new Event("submit", { bubbles: true, cancelable: true }),
    );
  }
}

function ensureMobileTagModalActions() {
  if (typeof document === "undefined") return;

  document.querySelectorAll(".TagSelectionModal").forEach((modal) => {
    if (!(modal instanceof HTMLElement)) return;

    if (!isMobileViewport()) {
      modal.querySelector(".CirklaMobileTagActions")?.remove();
      return;
    }

    if (modal.querySelector(".CirklaMobileTagActions")) return;

    const body =
      modal.querySelector(".Modal-body") ||
      modal.querySelector(".modal-body") ||
      modal;
    const actionRow = document.createElement("div");
    actionRow.className = "CirklaMobileTagActions";

    const submit = document.createElement("button");
    submit.type = "button";
    submit.className = "Button Button--primary CirklaMobileTagSubmit";
    submit.textContent = String(forumTrans("tags", "applyChoice"));
    submit.addEventListener("click", () => submitTagSelectionModal(modal));

    const bypass = document.createElement("button");
    bypass.type = "button";
    bypass.className = "Button Button--flat CirklaMobileTagBypass";
    bypass.textContent = String(forumTrans("tags", "postWithoutTags"));
    bypass.addEventListener("click", () => {
      const bypassButton = findTagModalBypassButton(modal);
      if (bypassButton instanceof HTMLElement) {
        bypassButton.click();
      }
      window.setTimeout(() => submitTagSelectionModal(modal), 0);
    });

    actionRow.append(submit, bypass);
    body.appendChild(actionRow);
  });
}

// -----------------------------------------------------------------------------
// Tag and calendar route helpers
// -----------------------------------------------------------------------------

function isTagsRoute() {
  return app.current?.get?.("routeName") === "tags";
}

function isCalendarRoute() {
  return app.current?.get?.("routeName") === "advevents";
}

function canViewCalendarEvents() {
  const user = app.session?.user;

  if (!user) return false;

  try {
    if (typeof user.canViewEvents === "function") {
      return Boolean(user.canViewEvents());
    }

    if (typeof user.attribute === "function") {
      const value = user.attribute("canViewEvents");
      if (typeof value !== "undefined") return Boolean(value);
    }

    return Boolean(user.data?.attributes?.canViewEvents);
  } catch (_error) {
    return false;
  }
}

function shouldHidePageHero() {
  const routeName = app.current?.get?.("routeName");

  return (
    ["tag", "following", "byobuPrivate"].includes(routeName) ||
    isLegalReadingPage()
  );
}

// -----------------------------------------------------------------------------
// Tag page enhancements
// -----------------------------------------------------------------------------

function enhanceTagTiles() {
  if (typeof document === "undefined" || !isTagsRoute()) return;

  document.querySelectorAll(".TagsPage .TagTile").forEach((card) => {
    const listItem = card.closest("li");

    delete card.dataset.cirklaExpandable;
    card.classList.remove("is-compact", "is-expanded");
    listItem?.classList.remove("is-compact", "is-expanded");
    card.querySelectorAll(".TagTile-toggle").forEach((toggle) => toggle.remove());
  });
}

// -----------------------------------------------------------------------------
// Calendar page enhancements
// -----------------------------------------------------------------------------

/**
 * Adds CIRKLA-specific calendar chrome and empty/loading states around the
 * calendar extension output.
 */
function enhanceCalendarPage() {
  if (typeof document === "undefined" || !isCalendarRoute()) return;

  const results = document.querySelector(".CalendarPage .IndexPage-results");
  const calendar = document.querySelector(".CalendarPage #calendar");

  if (results) {
    results.classList.add("CirklaCalendarResults");
  }

  if (!calendar) return;

  if (!canViewCalendarEvents()) {
    stopCalendarLoading();
    calendar
      .querySelectorAll(".fc-event, .fc-daygrid-event, .fc-list-event")
      .forEach((eventNode) => eventNode.remove());
    return;
  }

  calendar.classList.add("CirklaCalendarSurface");

  if (calendar.dataset.cirklaDateCaptureInstalled !== "1") {
    calendar.dataset.cirklaDateCaptureInstalled = "1";
    calendar.addEventListener(
      "click",
      (event) => {
        if (event.target?.closest?.(".fc-event")) return;

        const day = event.target?.closest?.(".fc-daygrid-day[data-date]");
        const selectedDate = String(day?.dataset?.date || "").trim();

        if (selectedDate) calendarSelectedDate = selectedDate;
      },
      true,
    );
  }

  const wrapper = calendar.parentElement;
  if (wrapper?.classList.contains("CirklaCalendarCard")) {
    wrapper.parentNode.insertBefore(calendar, wrapper);
    wrapper.remove();
  }

  if (!calendarLoadingActive && !isCalendarRenderable(calendar)) {
    startCalendarLoading();
  }

  if (calendarLoadingActive) {
    advanceCalendarLoadingState();
  }

  syncCalendarLoadingState();
}

function isCalendarRenderable(calendar) {
  if (!calendar) return false;

  return !!calendar.querySelector(
    ".fc-daygrid-body, .fc-list-table, .fc-scrollgrid, .fc-view-harness .fc-view",
  );
}

function observeCalendarLoading() {
  if (
    typeof document === "undefined" ||
    typeof MutationObserver === "undefined"
  )
    return;

  const calendar = document.querySelector(".CalendarPage #calendar");
  if (!calendar) return;

  if (calendarLoadingObserver) {
    calendarLoadingObserver.disconnect();
  }

  calendarLoadingObserver = new MutationObserver(() => {
    advanceCalendarLoadingState();
    syncCalendarLoadingState();
  });

  calendarLoadingObserver.observe(calendar, {
    childList: true,
    subtree: true,
  });
}

function startCalendarLoading() {
  calendarLoadingActive = true;
  calendarLoadingSawRequest = false;
  calendarLoadingStartedAt = Date.now();
  calendarLoadingSettledAt = 0;
  observeCalendarLoading();

  if (calendarLoadingStopTimer) {
    clearTimeout(calendarLoadingStopTimer);
  }

  calendarLoadingStopTimer = setTimeout(() => {
    stopCalendarLoading();
  }, 10000);

  advanceCalendarLoadingState();
  syncCalendarLoadingState();
}

function stopCalendarLoading() {
  calendarLoadingActive = false;
  calendarLoadingSawRequest = false;
  calendarLoadingStartedAt = 0;
  calendarLoadingSettledAt = 0;

  if (calendarLoadingObserver) {
    calendarLoadingObserver.disconnect();
    calendarLoadingObserver = null;
  }

  if (calendarLoadingStopTimer) {
    clearTimeout(calendarLoadingStopTimer);
    calendarLoadingStopTimer = null;
  }

  syncCalendarLoadingState();
}

/**
 * Wraps Flarum requests related to the calendar so the calendar page can show
 * stable loading and settling states without affecting unrelated pages.
 */
function installCalendarStateHooks() {
  const calendarState = app.calendarState;
  if (!calendarState || calendarState.__cirklaLoadingHooksInstalled === true)
    return;

  if (typeof calendarState.refresh === "function") {
    const originalRefresh = calendarState.refresh.bind(calendarState);
    calendarState.refresh = function () {
      if (!canViewCalendarEvents()) {
        stopCalendarLoading();
        return Promise.resolve([]);
      }

      const calendar = document.querySelector(".CalendarPage #calendar");
      if (!isCalendarRenderable(calendar)) {
        startCalendarLoading();
      }
      return originalRefresh.apply(this, arguments);
    };
  }

  if (typeof calendarState.fetchEvents === "function") {
    const originalFetchEvents = calendarState.fetchEvents.bind(calendarState);
    calendarState.fetchEvents = function (
      info,
      successCallback,
      failureCallback,
    ) {
      if (!canViewCalendarEvents()) {
        stopCalendarLoading();
        if (typeof successCallback === "function") {
          successCallback([]);
        }
        return Promise.resolve([]);
      }

      const calendar = document.querySelector(".CalendarPage #calendar");
      if (!isCalendarRenderable(calendar)) {
        startCalendarLoading();
      }

      const wrappedSuccess = (events) => {
        const normalizedEvents = Array.isArray(events) ? events : [];

        if (this && typeof this === "object") {
          this.events = normalizedEvents;
        }

        if (typeof successCallback === "function") {
          successCallback(normalizedEvents);
        }

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            setTimeout(() => stopCalendarLoading(), 120);
          });
        });
      };

      const wrappedFailure = (error) => {
        stopCalendarLoading();

        if (typeof failureCallback === "function") {
          failureCallback(error);
        }
      };

      return originalFetchEvents(info, wrappedSuccess, wrappedFailure);
    };
  }

  // The calendar extension's own "listYearFromToday" view spans exactly one
  // year starting from whatever date is currently focused (see its
  // `visibleRange`), but never sets a `titleFormat` for that view, so
  // FullCalendar falls back to its default range title - a plain "2026" only
  // when that window happens to land on a year boundary, and a "2026 - 2027"
  // range title otherwise (i.e. almost always, since "today" is rarely
  // January 1st). It also never sets a `duration`, which FullCalendar needs
  // to know how far to move the focused date on prev/next for a view with a
  // custom `visibleRange` - without it, navigating repeatedly can drift by
  // an inconsistent amount and appear to "stop working". Re-declaring the
  // view here with both fixed keeps its original visibleRange/day format and
  // makes the title always read as a single year, with prev/next reliably
  // moving by exactly one year every time.
  if (typeof calendarState.calendar?.setOption === "function") {
    calendarState.calendar.setOption("views", {
      listYearFromToday: {
        type: "list",
        duration: { years: 1 },
        titleFormat: { year: "numeric" },
        visibleRange: (currentDate) => {
          const startDate = new Date(currentDate.valueOf());
          const endDate = new Date(currentDate.valueOf());
          endDate.setFullYear(endDate.getFullYear() + 1);
          return { start: startDate, end: endDate };
        },
        listDaySideFormat: { weekday: "long" },
      },
    });
  }

  calendarState.__cirklaLoadingHooksInstalled = true;
}

function advanceCalendarLoadingState() {
  if (!calendarLoadingActive) return;

  const calendar = document.querySelector(".CalendarPage #calendar");
  if (!calendar) return;

  if (pendingRequests > 0) {
    calendarLoadingSawRequest = true;
    calendarLoadingSettledAt = 0;
  }

  const renderable = isCalendarRenderable(calendar);
  const hasEventNodes = !!calendar.querySelector(
    ".fc-event, .fc-daygrid-event, .fc-list-event",
  );
  const elapsed = Date.now() - calendarLoadingStartedAt;

  if (renderable && hasEventNodes && elapsed > 150) {
    stopCalendarLoading();
    return;
  }

  if (calendarLoadingSawRequest && pendingRequests === 0) {
    if (!calendarLoadingSettledAt) {
      calendarLoadingSettledAt = Date.now();
    }

    const settledFor = Date.now() - calendarLoadingSettledAt;

    if (hasEventNodes && settledFor > 150) {
      stopCalendarLoading();
      return;
    }

    if (renderable && settledFor > 1800) {
      stopCalendarLoading();
      return;
    }
  }

  if (!calendarLoadingSawRequest && renderable && elapsed > 1200) {
    stopCalendarLoading();
    return;
  }

  if (
    calendarLoadingSawRequest &&
    pendingRequests === 0 &&
    !hasEventNodes &&
    elapsed > 4000
  ) {
    stopCalendarLoading();
  }
}

function syncCalendarLoadingState() {
  if (typeof document === "undefined" || typeof window === "undefined") return;

  const results = document.querySelector(".CalendarPage .IndexPage-results");
  const calendar = document.querySelector(".CalendarPage #calendar");
  if (!results || !calendar) return;

  let overlay = results.querySelector(".CirklaCalendarLoading");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.className = "CirklaCalendarLoading";

    const spinner = document.createElement("div");
    spinner.className = "CirklaCalendarLoading-spinner";
    spinner.setAttribute("aria-hidden", "true");

    const text = document.createElement("p");
    text.className = "CirklaCalendarLoading-text";
    text.textContent = String(forumTrans("events", "loading"));

    overlay.append(spinner, text);
    results.appendChild(overlay);
  }

  const shouldShow = calendarLoadingActive;

  overlay.hidden = !shouldShow;
  results.classList.toggle("CirklaCalendarResults--loading", shouldShow);
}

function syncCalendarRouteScrollPosition() {
  if (typeof document === "undefined" || typeof window === "undefined") return;

  if (!isCalendarRoute()) {
    delete document.body.dataset.cirklaCalendarScrollKey;
    stopCalendarLoading();
    return;
  }

  const scrollKey = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (document.body.dataset.cirklaCalendarScrollKey === scrollKey) return;

  document.body.dataset.cirklaCalendarScrollKey = scrollKey;

  const jumpToTop = () => {
    window.scrollTo(0, 0);
  };

  jumpToTop();
  window.requestAnimationFrame(jumpToTop);
  setTimeout(jumpToTop, 60);
}

function installCalendarPageLifecycleGuard() {
  const CalendarPage = app.routes?.advevents?.component;
  const prototype = CalendarPage?.prototype;

  if (!prototype || prototype.__cirklaLifecycleGuardInstalled === true) return;

  const originalOnupdate = prototype.onupdate;
  const originalOpenCreateModal = prototype.openCreateModal;

  prototype.onupdate = function () {
    if (!this.state || typeof this.state.refresh !== "function") return;

    return originalOnupdate?.apply(this, arguments);
  };

  if (typeof originalOpenCreateModal === "function") {
    prototype.openCreateModal = function (info = {}) {
      calendarSelectedDate = String(info?.dateStr || "").trim();
      return originalOpenCreateModal.apply(this, arguments);
    };
  }

  prototype.__cirklaLifecycleGuardInstalled = true;
}

// -----------------------------------------------------------------------------
// Calendar modal actions
// -----------------------------------------------------------------------------

function closeActiveModal() {
  safeCloseModal();

  document
    .querySelector(
      ".ModalManager .Modal-close .Button, .ModalManager .Modal-close button",
    )
    ?.click();

  if (typeof m?.redraw === "function") {
    m.redraw();
  }
}

function setCalendarModalSubmitState(modalRoot, isSubmitting) {
  if (!modalRoot) return;

  modalRoot.classList.toggle("CirklaCalendarModal--submitting", isSubmitting);

  const submitButton = modalRoot.querySelector(
    ".PollModal-SubmitButton, button[type='submit']",
  );
  const status = modalRoot.querySelector(".CirklaCalendarSubmitStatus");

  if (submitButton) {
    if (!submitButton.dataset.cirklaOriginalLabel) {
      submitButton.dataset.cirklaOriginalLabel =
        submitButton.textContent || String(forumTrans("events", "submit"));
    }

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute("aria-busy", isSubmitting ? "true" : "false");
    submitButton.textContent = isSubmitting
      ? String(forumTrans("events", "submitting"))
      : submitButton.dataset.cirklaOriginalLabel;
  }

  if (status) {
    status.hidden = !isSubmitting;
  }
}

function formatCalendarModalDate(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return String(forumTrans("events", "selectDateTime"));
  }

  return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function updateCalendarDateSummary(modalRoot, dates = []) {
  if (!modalRoot) return;

  const start = modalRoot.querySelector("[data-cirkla-calendar-start]");
  const end = modalRoot.querySelector("[data-cirkla-calendar-end]");

  const startText = formatCalendarModalDate(dates[0]);
  const endText = formatCalendarModalDate(dates[1]);

  if (start && start.textContent !== startText) start.textContent = startText;
  if (end && end.textContent !== endText) end.textContent = endText;
}

function enhanceCalendarModalFields(modalRoot) {
  const form = modalRoot?.querySelector("form");
  const groups = form?.querySelectorAll(".Form-group");
  if (!form || !groups || groups.length < 3) return;

  const titleGroup = groups[0];
  const datesGroup = groups[1];
  const descriptionGroup = groups[2];

  [titleGroup, datesGroup, descriptionGroup].forEach((group) => {
    const label = group.querySelector("label");
    if (!label || label.querySelector(".CirklaRequiredMark")) return;

    const requiredMark = document.createElement("span");
    requiredMark.className = "CirklaRequiredMark";
    requiredMark.setAttribute("aria-hidden", "true");
    requiredMark.textContent = " *";
    label.appendChild(requiredMark);
  });

  titleGroup.querySelector("input")?.setAttribute("aria-required", "true");
  descriptionGroup
    .querySelector("textarea")
    ?.setAttribute("aria-required", "true");

  const dateInput = datesGroup.querySelector("input[data-input]");
  dateInput?.setAttribute("aria-required", "true");

  if (!datesGroup.querySelector(".CirklaCalendarDateGuidance")) {
    const guidance = document.createElement("div");
    guidance.className = "CirklaCalendarDateGuidance";
    guidance.innerHTML = `
      <label class="CirklaCalendarDateValue">
        <span>${escapeHtmlText(String(forumTrans("events", "startTime")))} <b aria-hidden="true">*</b></span>
        <input type="datetime-local" class="CirklaCalendarNativeDateInput" data-cirkla-calendar-start required>
      </label>
      <label class="CirklaCalendarDateValue">
        <span>${escapeHtmlText(String(forumTrans("events", "endTime")))} <b aria-hidden="true">*</b></span>
        <input type="datetime-local" class="CirklaCalendarNativeDateInput" data-cirkla-calendar-end required>
      </label>
    `;
    datesGroup.appendChild(guidance);
  }

  const startInput = datesGroup.querySelector("[data-cirkla-calendar-start]");
  const endInput = datesGroup.querySelector("[data-cirkla-calendar-end]");

  const toLocalInputValue = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const syncNativeDateInputs = () => {
    const start = startInput?.value ? new Date(startInput.value) : null;
    let end = endInput?.value ? new Date(endInput.value) : null;

    if (!(start instanceof Date) || Number.isNaN(start.getTime())) return;

    if (
      !(end instanceof Date) ||
      Number.isNaN(end.getTime()) ||
      end.getTime() <= start.getTime()
    ) {
      end = new Date(start.getTime() + 60 * 60 * 1000);
      if (endInput) endInput.value = toLocalInputValue(end);
    }

    dateInput?._flatpickr?.setDate([start, end], true);
    datesGroup.classList.remove("CirklaCalendarField--invalid");
  };

  if (startInput && startInput.dataset.cirklaDateSyncInstalled !== "1") {
    startInput.dataset.cirklaDateSyncInstalled = "1";
    startInput.addEventListener("change", syncNativeDateInputs);
    endInput?.addEventListener("change", syncNativeDateInputs);
  }

  if (calendarSelectedDate && !startInput?.value) {
    const match = calendarSelectedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const start = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        0,
        0,
      );
      const end = new Date(start.getTime() + 60 * 60 * 1000);

      startInput.value = toLocalInputValue(start);
      endInput.value = toLocalInputValue(end);
    }
  }

  // The page-wide observer can run many times while Flatpickr loads. Keep one
  // bounded setup loop per actual input node to avoid an observer/timer storm.
  if (modalRoot.__cirklaCalendarPickerInput === dateInput) return;
  modalRoot.__cirklaCalendarPickerInput = dateInput;

  const attachPickerEnhancements = (attempt = 0) => {
    if (!document.body.contains(modalRoot) || !dateInput?.isConnected) return;

    const picker = dateInput?._flatpickr;
    if (!picker) {
      if (attempt < 200) {
        window.setTimeout(() => attachPickerEnhancements(attempt + 1), 50);
      }
      return;
    }

    if (dateInput.dataset.cirklaPickerEnhanced !== "1") {
      dateInput.dataset.cirklaPickerEnhanced = "1";
      picker.config.onChange.push(() => {
        datesGroup.classList.remove("CirklaCalendarField--invalid");
      });
    }

    if (startInput?.value) {
      syncNativeDateInputs();
      calendarSelectedDate = "";
    } else if (picker.selectedDates?.length) {
      if (startInput) {
        startInput.value = toLocalInputValue(picker.selectedDates[0]);
      }
      if (endInput) {
        endInput.value = toLocalInputValue(
          picker.selectedDates[1] || picker.selectedDates[0],
        );
      }
    }
  };

  attachPickerEnhancements();
}

function calendarModalValidation(modalRoot) {
  const form = modalRoot?.querySelector("form");
  const groups = form?.querySelectorAll(".Form-group");
  if (!form || !groups || groups.length < 3) return null;

  const titleInput = groups[0].querySelector("input");
  const dateInput = groups[1].querySelector("input[data-input]");
  const descriptionInput = groups[2].querySelector("textarea");
  const nativeStart = groups[1].querySelector("[data-cirkla-calendar-start]");
  const nativeEnd = groups[1].querySelector("[data-cirkla-calendar-end]");
  const selectedDates =
    nativeStart?.value && nativeEnd?.value
      ? [new Date(nativeStart.value), new Date(nativeEnd.value)]
      : dateInput?._flatpickr?.selectedDates || [];
  const missing = [];

  groups.forEach((group) => group.classList.remove("CirklaCalendarField--invalid"));
  [titleInput, dateInput, descriptionInput].forEach((input) =>
    input?.removeAttribute("aria-invalid"),
  );

  if (!String(titleInput?.value || "").trim()) {
    missing.push(String(forumTrans("events", "fieldEventName")));
    groups[0].classList.add("CirklaCalendarField--invalid");
    titleInput?.setAttribute("aria-invalid", "true");
  }

  if (selectedDates.length < 2) {
    missing.push(String(forumTrans("events", "fieldDateRange")));
    groups[1].classList.add("CirklaCalendarField--invalid");
    dateInput?.setAttribute("aria-invalid", "true");
  }

  if (!String(descriptionInput?.value || "").trim()) {
    missing.push(String(forumTrans("events", "fieldDescription")));
    groups[2].classList.add("CirklaCalendarField--invalid");
    descriptionInput?.setAttribute("aria-invalid", "true");
  }

  if (!missing.length) return null;

  return {
    message: String(
      forumTrans("events", "missingFieldsMessage", {
        fields: missing.join(", "),
      }),
    ),
    focusTarget:
      (!titleInput?.value?.trim() && titleInput) ||
      (selectedDates.length < 2 && dateInput) ||
      descriptionInput,
  };
}

/**
 * Adds predictable loading feedback to calendar event create/edit/delete flows.
 */
function installCalendarModalActions(modalRoot) {
  if (!modalRoot) return;

  enhanceCalendarModalFields(modalRoot);

  if (modalRoot.dataset.cirklaCalendarActionsInstalled === "1") return;

  const form = modalRoot.querySelector("form");
  const submitButton = modalRoot.querySelector(
    ".PollModal-SubmitButton, button[type='submit']",
  );

  if (!form || !submitButton) return;

  modalRoot.dataset.cirklaCalendarActionsInstalled = "1";

  const closeButton = modalRoot.querySelector(
    ".Modal-close .Button, .Modal-close button",
  );
  if (closeButton && !closeButton.dataset.cirklaCalendarClosePatched) {
    closeButton.dataset.cirklaCalendarClosePatched = "1";
    closeButton.addEventListener("click", (event) => {
      event.preventDefault();
      closeActiveModal();
    });
  }

  const submitGroup =
    submitButton.closest(".Form-group") || submitButton.parentElement;
  if (submitGroup && !submitGroup.querySelector(".CirklaCalendarActionRow")) {
    const actionRow = document.createElement("div");
    actionRow.className = "CirklaCalendarActionRow";
    submitGroup.insertBefore(actionRow, submitButton);
    actionRow.appendChild(submitButton);

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.className = "Button Button--flat CirklaCalendarCancelButton";
    cancelButton.textContent = String(forumTrans("events", "cancel"));
    cancelButton.addEventListener("click", (event) => {
      event.preventDefault();
      closeActiveModal();
    });
    actionRow.appendChild(cancelButton);
  }

  if (!submitGroup.querySelector(".CirklaCalendarSubmitStatus")) {
    const status = document.createElement("p");
    status.className = "helpText CirklaCalendarSubmitStatus";
    status.hidden = true;
    status.textContent = String(forumTrans("events", "savingWait"));
    submitGroup.appendChild(status);
  }

  form.addEventListener(
    "submit",
    (event) => {
      const validation = calendarModalValidation(modalRoot);
      let validationMessage = modalRoot.querySelector(
        ".CirklaCalendarValidationMessage",
      );

      if (validation) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (!validationMessage) {
          validationMessage = document.createElement("p");
          validationMessage.className = "CirklaCalendarValidationMessage";
          validationMessage.setAttribute("role", "alert");
          form.insertBefore(validationMessage, form.firstChild);
        }

        validationMessage.textContent = validation.message;
        validationMessage.hidden = false;
        validation.focusTarget?.focus?.();
        return;
      }

      if (validationMessage) validationMessage.hidden = true;

      if (modalRoot.dataset.cirklaCalendarSubmitting === "1") {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      modalRoot.dataset.cirklaCalendarSubmitting = "1";
      setCalendarModalSubmitState(modalRoot, true);

      const startedAt = Date.now();
      let sawRequest = pendingRequests > 0;

      const finishSubmitting = () => {
        delete modalRoot.dataset.cirklaCalendarSubmitting;
        setCalendarModalSubmitState(modalRoot, false);
      };

      const clearWhenIdle = () => {
        if (!document.body.contains(modalRoot)) return;

        const elapsed = Date.now() - startedAt;

        if (pendingRequests > 0) {
          sawRequest = true;
        }

        if (!sawRequest && elapsed > 700) {
          finishSubmitting();
          return;
        }

        if (sawRequest && pendingRequests === 0 && elapsed > 250) {
          // The extension removes the modal after a successful save. If it is
          // still present, submission failed and the entered values must stay.
          finishSubmitting();
          return;
        }

        if (elapsed > 10000) {
          finishSubmitting();
          return;
        }

        window.requestAnimationFrame(clearWhenIdle);
      };

      window.requestAnimationFrame(clearWhenIdle);
    },
    true,
  );
}

function enhanceCalendarEventModal() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(".ModalManager .EditEventsModal")
    .forEach((modalRoot) => installCalendarModalActions(modalRoot));
}

function setCalendarDetailsBusyState(modalRoot, isBusy) {
  if (!modalRoot) return;

  modalRoot.classList.toggle("CirklaCalendarDetails--busy", isBusy);

  const status = modalRoot.querySelector(".CirklaCalendarDetailsStatus");

  modalRoot
    .querySelectorAll(
      ".Modal-close .Button, .Modal-close button, .CirklaCalendarCloseButton, .Modal-body .Button--icon.Button--link",
    )
    .forEach((button) => {
      button.disabled = isBusy;
      button.setAttribute("aria-busy", isBusy ? "true" : "false");
    });

  if (status) {
    status.hidden = !isBusy;
  }
}

function installCalendarDetailsDeleteFeedback(modalRoot) {
  if (!modalRoot || modalRoot.dataset.cirklaCalendarDeletePatched === "1")
    return;

  const actionButtons = modalRoot.querySelectorAll(
    ".Modal-body .Button--icon.Button--link",
  );
  const deleteButton = actionButtons[actionButtons.length - 1];
  if (!deleteButton) return;

  modalRoot.dataset.cirklaCalendarDeletePatched = "1";

  deleteButton.addEventListener(
    "click",
    () => {
      setCalendarDetailsBusyState(modalRoot, true);

      const startedAt = Date.now();
      let sawNetworkRequest = false;

      // The calendar extension's own delete handler closes this modal via
      // `modal.hide()` as soon as its delete request resolves, which races
      // ahead of this rAF-polling loop (and its 150ms settle buffer) and can
      // remove `modalRoot` from the DOM before we notice the request
      // finished. Refreshing the calendar must not depend on the modal still
      // being present, or that race silently skips the refresh and the
      // deleted event only disappears after a manual page reload.
      const finishDeleteFlow = () => {
        startCalendarLoading();
        app.calendarState?.refresh?.(true);
        syncCalendarLoadingState();

        if (document.body.contains(modalRoot)) {
          closeActiveModal();
        }
      };

      const clearWhenIdle = () => {
        if (pendingRequests > 0) {
          sawNetworkRequest = true;
        }

        if (
          sawNetworkRequest &&
          pendingRequests === 0 &&
          Date.now() - startedAt > 150
        ) {
          finishDeleteFlow();
          return;
        }

        if (!sawNetworkRequest && Date.now() - startedAt > 1200) {
          if (document.body.contains(modalRoot)) {
            setCalendarDetailsBusyState(modalRoot, false);
          }
          return;
        }

        window.requestAnimationFrame(clearWhenIdle);
      };

      window.requestAnimationFrame(clearWhenIdle);
    },
    true,
  );
}

function installAriaHiddenFocusGuard() {
  if (typeof window === "undefined") return;
  if (app.__cirklaAriaHiddenFocusGuardInstalled) return;
  if (!window.Element?.prototype?.setAttribute) return;

  const originalSetAttribute = window.Element.prototype.setAttribute;

  window.Element.prototype.setAttribute = function (name, value) {
    if (
      String(name).toLowerCase() === "aria-hidden" &&
      String(value) === "true" &&
      typeof document !== "undefined"
    ) {
      const activeElement = document.activeElement;

      if (
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        this instanceof HTMLElement &&
        this.contains(activeElement)
      ) {
        activeElement.blur();
      }
    }

    return originalSetAttribute.apply(this, arguments);
  };

  app.__cirklaAriaHiddenFocusGuardInstalled = true;
}

function installCalendarDetailsModalActions(modalRoot) {
  if (!modalRoot || modalRoot.dataset.cirklaCalendarDetailsInstalled === "1")
    return;

  modalRoot.dataset.cirklaCalendarDetailsInstalled = "1";

  const closeButton = modalRoot.querySelector(
    ".Modal-close .Button, .Modal-close button",
  );

  if (closeButton && !closeButton.dataset.cirklaCalendarClosePatched) {
    closeButton.dataset.cirklaCalendarClosePatched = "1";
    closeButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeActiveModal();
    });
  }

  const body = modalRoot.querySelector(".Modal-body");
  if (body && !body.querySelector(".CirklaCalendarDetailsActions")) {
    const status = document.createElement("p");
    status.className = "helpText CirklaCalendarDetailsStatus";
    status.hidden = true;
    status.textContent = String(forumTrans("events", "deletingWait"));
    body.appendChild(status);

    const actions = document.createElement("div");
    actions.className = "CirklaCalendarDetailsActions";

    const closeAction = document.createElement("button");
    closeAction.type = "button";
    closeAction.className = "Button Button--flat CirklaCalendarCloseButton";
    closeAction.textContent = String(forumTrans("common", "close"));
    closeAction.addEventListener("click", (event) => {
      event.preventDefault();
      closeActiveModal();
    });

    actions.appendChild(closeAction);
    body.appendChild(actions);
  }

  installCalendarDetailsDeleteFeedback(modalRoot);
}

function enhanceCalendarDetailsModal() {
  if (typeof document === "undefined") return;

  document
    .querySelectorAll(".ModalManager .EventTeaser")
    .forEach((modalRoot) => installCalendarDetailsModalActions(modalRoot));
}

app.initializers.add("cirkla-user-directory-layout", () => {
  const applyUserDirectoryLayout = () => {
    forceUserDirectoryListMode();
    ensureUserDisplayPreferenceStyles();
    setTimeout(forceUserDirectoryListMode, 0);
    setTimeout(ensureUserDisplayPreferenceStyles, 0);
  };

  applyUserDirectoryLayout();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyUserDirectoryLayout();
      setTimeout(forceUserDirectoryListMode, 100);
      return result;
    };
  }
});

app.initializers.add("cirkla-composer-visibility-class", () => {
  installComposerCloseButtonHandler();

  const schedule = () => {
    window.requestAnimationFrame(() => {
      syncComposerVisibilityClass();
    });
  };

  schedule();
  setTimeout(schedule, 0);
  setTimeout(schedule, 100);

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      schedule();
      setTimeout(schedule, 100);
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver(() => schedule());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
  }
});

app.initializers.add("cirkla-shell-cleanup", () => {
  const cleanup = () => {
    removeOriginalNewsletterBox();
    ensureNewsletterNavPlacement();
    ensureHeaderBrandFallback();
  };

  cleanup();
  setTimeout(cleanup, 0);
  setTimeout(cleanup, 100);

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      cleanup();
      setTimeout(cleanup, 100);
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(cleanup).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});

app.initializers.add("cirkla-header-page-title", () => {
  extend(HeaderPrimary.prototype, "items", function (items) {
    items.remove("cirkla-page-title");
  });
});

app.initializers.add("cirkla-legal-page-shell", () => {
  const syncLegalPageShell = () => {
    applyLegalPageBodyClass();
    syncRouteBodyClasses();
    stripLegalPageTitleCard();
    polishLegalPageContent();
    ensureMobileUserPageNavPlacement();
    ensureMobileTagModalActions();
    ensureRouteBackButton();
    ensureConversationListBackButton();
    setTimeout(applyLegalPageBodyClass, 0);
    setTimeout(syncRouteBodyClasses, 0);
    setTimeout(stripLegalPageTitleCard, 0);
    setTimeout(polishLegalPageContent, 0);
    setTimeout(ensureMobileUserPageNavPlacement, 0);
    setTimeout(ensureMobileTagModalActions, 0);
    setTimeout(ensureRouteBackButton, 0);
    setTimeout(ensureConversationListBackButton, 0);
    setTimeout(applyLegalPageBodyClass, 100);
    setTimeout(syncRouteBodyClasses, 100);
    setTimeout(stripLegalPageTitleCard, 100);
    setTimeout(polishLegalPageContent, 100);
    setTimeout(ensureMobileUserPageNavPlacement, 100);
    setTimeout(ensureMobileTagModalActions, 100);
    setTimeout(ensureRouteBackButton, 100);
    setTimeout(ensureConversationListBackButton, 100);
    setTimeout(ensureConversationListBackButton, 350);
  };

  syncLegalPageShell();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      syncLegalPageShell();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => {
      applyLegalPageBodyClass();
      syncRouteBodyClasses();
      stripLegalPageTitleCard();
      polishLegalPageContent();
      ensureMobileUserPageNavPlacement();
      ensureMobileTagModalActions();
      ensureRouteBackButton();
      ensureConversationListBackButton();
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  window.addEventListener("resize", ensureRouteBackButton, { passive: true });
  window.addEventListener("resize", ensureMobileUserPageNavPlacement, {
    passive: true,
  });
});

app.initializers.add("cirkla-header-interreg-logo", () => {
  extend(HeaderPrimary.prototype, "items", function (items) {
    const homeHref =
      (app.route && typeof app.route === "function" && app.route("index")) ||
      "/";

    items.add(
      "cirkla-interreg-logo",
      m(
        "li",
        { className: "item-cirkla-interreg-logo" },
        m(
          "a",
          {
            className: "CirklaHeaderInterregLink",
            href: homeHref,
            title: forumTrans("common", "goHome"),
            "aria-label": forumTrans("common", "goHome"),
            oncreate: m.route.link,
          },
          m("img", {
            className: "CirklaHeaderInterregLogo CirklaHeaderProjectLogo",
            src: "/assets/branding/cirkla_wider_interreg_color.png",
            alt: forumTrans("header", "projectLogoAlt"),
          }),
        ),
      ),
      95,
    );

    items.add(
      "cirkla-home-link",
      m(
        "a",
        {
          className: "Button Button--link CirklaHeaderHomeLink",
          href: homeHref,
          title: forumTrans("common", "home"),
          oncreate: m.route.link,
        },
        [
          m("i", { className: "icon fas fa-home Button-icon" }),
          m("span", forumTrans("common", "home")),
        ],
      ),
      94,
    );
  });
});

app.initializers.add("cirkla-hide-page-heroes", () => {
  override(IndexPage.prototype, "hero", function (original) {
    if (shouldHidePageHero()) {
      return null;
    }

    return original();
  });
});

app.initializers.add("cirkla-hide-rankings-nav", () => {
  extend(IndexPage.prototype, "navItems", function (items) {
    items.remove("rankings");
  });

  extend(Composer.prototype, "controlItems", function (items) {
    if (this.state.position !== ComposerState.Position.FULLSCREEN) return;

    items.add(
      "minimizeFullScreen",
      m(ComposerButton, {
        icon: "fas fa-minus minimize",
        title: app.translator.trans("core.forum.composer.minimize_tooltip"),
        onclick: this.state.minimize.bind(this.state),
      }),
      -20,
    );

    items.add(
      "closeFullScreen",
      m(ComposerButton, {
        icon: "fas fa-times",
        title: app.translator.trans("core.forum.composer.close_tooltip"),
        onclick: this.state.close.bind(this.state),
      }),
      -10,
    );
  });

  const applyRankingCleanup = () => {
    removeRankingsSidebarItems();
    setTimeout(removeRankingsSidebarItems, 0);
    setTimeout(removeRankingsSidebarItems, 100);
  };

  applyRankingCleanup();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyRankingCleanup();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => removeRankingsSidebarItems()).observe(
      document.body,
      {
        childList: true,
        subtree: true,
      },
    );
  }
});

app.initializers.add("cirkla-tags-expand-toggle", () => {
  const applyTagTileEnhancements = () => {
    enhanceTagTiles();
    setTimeout(enhanceTagTiles, 0);
    setTimeout(enhanceTagTiles, 100);
  };

  applyTagTileEnhancements();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyTagTileEnhancements();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => enhanceTagTiles()).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});

app.initializers.add("cirkla-calendar-card", () => {
  const applyCalendarEnhancements = () => {
    installCalendarPageLifecycleGuard();
    installCalendarStateHooks();
    syncCalendarRouteScrollPosition();
    enhanceCalendarPage();
    enhanceCalendarEventModal();
    enhanceCalendarDetailsModal();
    setTimeout(installCalendarPageLifecycleGuard, 0);
    setTimeout(installCalendarStateHooks, 0);
    setTimeout(syncCalendarRouteScrollPosition, 0);
    setTimeout(enhanceCalendarPage, 0);
    setTimeout(enhanceCalendarEventModal, 0);
    setTimeout(enhanceCalendarDetailsModal, 0);
    setTimeout(installCalendarPageLifecycleGuard, 100);
    setTimeout(installCalendarStateHooks, 100);
    setTimeout(syncCalendarRouteScrollPosition, 100);
    setTimeout(enhanceCalendarPage, 100);
    setTimeout(enhanceCalendarEventModal, 100);
    setTimeout(enhanceCalendarDetailsModal, 100);
    setTimeout(installCalendarPageLifecycleGuard, 400);
    setTimeout(installCalendarStateHooks, 400);
    setTimeout(syncCalendarRouteScrollPosition, 400);
    setTimeout(enhanceCalendarPage, 400);
    setTimeout(enhanceCalendarEventModal, 400);
    setTimeout(enhanceCalendarDetailsModal, 400);
  };

  applyCalendarEnhancements();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyCalendarEnhancements();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => {
      installCalendarPageLifecycleGuard();
      installCalendarStateHooks();
      syncCalendarRouteScrollPosition();
      enhanceCalendarPage();
      enhanceCalendarEventModal();
      enhanceCalendarDetailsModal();
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});

app.initializers.add("cirkla-legal-acceptance-gate", () => {
  beginInitialSessionBoot();

  const applyGate = () => {
    enforceMandatoryPoliciesGate();
    setTimeout(enforceMandatoryPoliciesGate, 0);
    setTimeout(enforceMandatoryPoliciesGate, 100);
  };

  applyGate();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyGate();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => enforceMandatoryPoliciesGate()).observe(
      document.body,
      {
        childList: true,
        subtree: true,
      },
    );
  }
});

app.initializers.add(
  "cirkla-global-loader-chat-video",
  () => {
    beginInitialSessionBoot();
    ensureUserDisplayPreferenceStyles();
    installAriaHiddenFocusGuard();
    installGlobalRequestLoader();
    installSingleAlertPolicy();
    installDropdownViewportFix();
    installContextDropdownScrollCloser();
    installMobileDrawerToggleGuard();
    installMobileSearchOverlay();
    syncChatHistoryAutoScroll();
    installConversationsSyncObserver();
    installSidebarChatUnreadIndicatorSync();
    installCirklaLiveUpdates(app, m, {
      canUseConversationFeatures,
      isConversationRoute,
      currentConversationId,
      syncSidebarChatUnreadState,
      syncConversationListUnreadState,
    });
    installDirectoryUserCardControls();
    installChatUserControlOverride();
    extend(UserPage.prototype, "navItems", function (items) {
      items.remove("privateDiscussions");
      items.remove("private-discussions");
      items.remove("private-discussion");
      items.remove("byobu");
      items.remove("cirkla-private-chat");
      items.remove("cirkla-private-discussions");
      items.remove("cirkla-private-discussion");
    });

    extend(SettingsPage.prototype, "settingsItems", function (items) {
      if (!app.session?.user || !app.forum?.attribute?.("canHaveMasquerade")) {
        items.remove("cirkla-profile-details");
        return;
      }

      items.add(
        "cirkla-profile-details",
        m(CirklaMasqueradeSettingsFields),
        95,
      );
    });

    extend(UserCard.prototype, "infoItems", function (items) {
      const isProfile = isUserProfileRoute();
      const isDirectory = isUserDirectoryRoute();

      ensureUserDisplayPreferenceStyles();

      if (typeof items.remove === "function") {
        if (isDirectory) {
          [
            "lastSeen",
            "joined",
            "points",
            "votes",
            "gamification",
            "discussion-count",
            "discussions",
            "comment-count",
            "comments",
            "posts",
            "best-answer-count",
            "bestAnswerCount",
            "followers",
            "following",
            "followed",
            "follow-users-followers",
            "follow-users-following",
          ].forEach((itemName) => items.remove(itemName));
        }

        if (
          (isProfile &&
            !settingEnabled("cirkla.display.profile_discussions")) ||
          (isDirectory &&
            !settingEnabled("cirkla.display.directory_discussions"))
        ) {
          items.remove("discussion-count");
          items.remove("discussions");
        }

        if (
          (isProfile && !settingEnabled("cirkla.display.profile_posts")) ||
          (isDirectory && !settingEnabled("cirkla.display.directory_posts"))
        ) {
          items.remove("comment-count");
          items.remove("comments");
          items.remove("posts");
        }

        if (
          (isProfile &&
            !settingEnabled("cirkla.display.profile_best_answers")) ||
          (isDirectory &&
            !settingEnabled("cirkla.display.directory_best_answers"))
        ) {
          items.remove("best-answer-count");
          items.remove("bestAnswerCount");
        }

        if (
          (isProfile &&
            !settingEnabled("cirkla.display.profile_follow_counts")) ||
          (isDirectory &&
            !settingEnabled("cirkla.display.directory_follow_counts"))
        ) {
          items.remove("followers");
          items.remove("following");
          items.remove("followed");
          items.remove("follow-users-followers");
          items.remove("follow-users-following");
        }

        items.remove("masquerade-bio");
      }

      if (!isProfile && !isDirectory) return;

      const user = this.attrs?.user;

      if (isDirectory) {
        const lastSeenAt =
          typeof user?.lastSeenAt === "function" ? user.lastSeenAt() : null;
        const joinedAt =
          typeof user?.joinTime === "function" ? user.joinTime() : null;
        // Flarum core's isOnline() is `dayjs().subtract(5,'minutes').isBefore(this.lastSeenAt())`.
        // dayjs(undefined) resolves to "now" (not "invalid"), so when lastSeenAt is
        // undisclosed (the user turned off "allow others to see when I am online"),
        // isOnline() would otherwise evaluate to true instead of false. Core's own
        // UserCard.js avoids this by only calling isOnline() when lastSeenAt is
        // present; mirror that guard here so the privacy preference is respected.
        const online =
          Boolean(lastSeenAt) &&
          typeof user?.isOnline === "function" &&
          Boolean(user.isOnline());
        const directoryDetails = [
          {
            // No fallback to "Not available" here on purpose: that reads as
            // "this account is broken/gone" to other members, when it just
            // means the user turned off "allow others to see when I am
            // online". Leaving value empty drops the row entirely (see the
            // `!normalizedValue` skip below) so the remaining rows move up
            // instead of showing a confusing placeholder.
            key: "cirkla-last-seen",
            label: String(forumTrans("directory", "lastSeen")),
            value: online
              ? String(forumTrans("directory", "online"))
              : lastSeenAt
                ? humanTime(lastSeenAt)
                : "",
            online,
          },
          {
            key: "cirkla-joined",
            label: String(forumTrans("directory", "joined")),
            value: joinedAt
              ? humanTime(joinedAt)
              : String(forumTrans("common", "notAvailable")),
          },
          {
            key: "cirkla-organization",
            label: String(forumTrans("directory", "organization")),
            value:
              (typeof user?.attribute === "function"
                ? user.attribute("cirklaOrganization")
                : null) || user?.data?.attributes?.cirklaOrganization,
          },
          {
            key: "cirkla-job-title",
            label: String(forumTrans("directory", "jobTitle")),
            value:
              (typeof user?.attribute === "function"
                ? user.attribute("cirklaJobTitle")
                : null) || user?.data?.attributes?.cirklaJobTitle,
          },
        ];

        directoryDetails.forEach(({ key, label, value, online }, index) => {
          const normalizedValue = String(value || "").trim();
          if (!normalizedValue) return;

          items.add(
            key,
            m(".CirklaUserCardDetail", [
              m("span.CirklaUserCardDetail-label", label),
              m("span.CirklaUserCardDetail-value", [
                online
                  ? m("span.CirklaUserCardDetail-onlineDot", {
                      "aria-hidden": "true",
                    })
                  : null,
                normalizedValue,
              ]),
            ]),
            80 - index,
          );
        });

        return;
      }

      let bioFields = [];

      try {
        bioFields =
          typeof user?.bioFields === "function" ? user.bioFields() || [] : [];
      } catch (_error) {
        bioFields = [];
      }

      const rendered = bioFields
        .filter(answerHasRenderableContent)
        .filter((answer) =>
          shouldShowMasqueradeAnswer(
            answer,
            isDirectory ? "directory" : "profile",
          ),
        )
        .map(renderMasqueradeBioFallback)
        .filter(Boolean);

      if (!rendered.length) return;

      items.add(
        "cirkla-masquerade-bio-fallback",
        m(".Masquerade-Bio.CirklaMasqueradeBioFallback", rendered),
        55,
      );
    });

    extend(SessionDropdown.prototype, "items", function (items) {
      if (!app.session?.user) return;
      const canEditMasquerade = Boolean(
        app.forum?.attribute?.("canHaveMasquerade"),
      );
      const profileIncomplete =
        canEditMasquerade &&
        Boolean(
          app.forum?.attribute?.("masquerade.force-profile-completion"),
        ) &&
        !Boolean(app.forum?.attribute?.("masquerade.profile-completed"));

      if (canEditMasquerade) {
        items.add(
          "cirkla-profile-details",
          m(
            Button,
            {
              icon: "far fa-id-card",
              onclick: () => {
                closeMobileDrawer();
                m.route.set("/masquerade/configure");
              },
            },
            profileIncomplete
              ? forumTrans("header", "completeProfile")
              : forumTrans("header", "editProfileDetails"),
          ),
          42,
        );
      } else {
        items.remove("cirkla-profile-details");
      }

      const support = resolvedSupportLink();
      items.add(
        "cirkla-support",
        m(
          Button,
          {
            icon: "fas fa-life-ring",
            onclick: () => {
              closeMobileDrawer();
              m.route.set(support.href);
            },
          },
          support.label,
        ),
        45,
      );

      resolveDedupedCustomEntries(
        parseCustomPageEntries().filter(
          (entry) => !entry.isSupport && entry.placements.has("dropdown"),
        ),
        currentSiteLocale(),
        allLocaleSupportHrefs(),
      ).forEach((item, index) => {
        items.add(
          `cirkla-custom-${item.entry.key}`,
          m(
            Button,
            {
              icon: item.entry.icon || "fas fa-file-alt",
              onclick: () => {
                closeMobileDrawer();
                m.route.set(item.href);
              },
            },
            item.label,
          ),
          44 - index,
        );
      });

      items.add(
        "cirkla-security",
        m(
          Button,
          {
            icon: "fas fa-shield-alt",
            onclick: (event) => {
              event.preventDefault();
              event.stopPropagation();

              const href = currentUserSecurityHref();

              closeMobileDrawer();

              if (m?.route?.set) {
                m.route.set(href);
                return;
              }

              window.location.assign(href);
            },
          },
          forumTrans("header", "security"),
        ),
        45,
      );
      items.add(
        "cirkla-invite-member",
        m(
          Button,
          {
            icon: "fas fa-user-plus",
            onclick: () => {
              closeMobileDrawer();
              app.modal.show(UserInviteLinkModal);
            },
          },
          forumTrans("header", "inviteMember"),
        ),
        40,
      );
    });

    extend(IndexPage.prototype, "navItems", function (items) {
      if (app.session?.user) {
        items.remove("cirkla-support");

        const chatHref = canUseConversationFeatures()
          ? resolveChatRouteHref()
          : null;
        if (chatHref) {
          items.add(
            "cirkla-private-chat",
            m(
              "a",
              {
                className: "Navigation-link",
                href: chatHref,
                oncreate: m.route.link,
              },
              [
                m("i", { className: "icon fas fa-comment-alt Button-icon" }),
                buildSidebarChatLabel(),
              ],
            ),
            71,
          );
        }
      }

      const support = resolvedSupportLink();
      items.add(
        "cirkla-support",
        m(
          "a",
          {
            className: "Navigation-link",
            href: support.href,
            oncreate: m.route.link,
          },
          [
            m("i", { className: "icon fas fa-life-ring Button-icon" }),
            m("span", support.label),
          ],
        ),
        20,
      );

      resolveDedupedCustomEntries(
        parseCustomPageEntries().filter(
          (entry) => !entry.isSupport && entry.placements.has("sidebar"),
        ),
        currentSiteLocale(),
        allLocaleSupportHrefs(),
      ).forEach((item, index) => {
        // Signed-in users already see dropdown-placed entries in the
        // account menu - skip the sidebar copy so it isn't shown twice,
        // matching how the "support" item above behaves.
        if (app.session?.user && item.entry.placements.has("dropdown")) return;

        items.add(
          `cirkla-custom-${item.entry.key}`,
          m(
            "a",
            {
              className: "Navigation-link",
              href: item.href,
              oncreate: m.route.link,
            },
            [
              m("i", {
                className: `icon ${item.entry.icon || "fas fa-file-alt"} Button-icon`,
              }),
              m("span", item.label),
            ],
          ),
          19 - index,
        );
      });

      items.add(
        "cirklaNewsletterBox",
        m(
          "div",
          { className: "Navigation-link CirklaNewsletterNavCard" },
          m(CirklaNewsletterBox),
        ),
        -500,
      );
    });

    extend(HeaderSecondary.prototype, "items", function (items) {
      items.remove("Messages");
      items.remove("messages");
      items.remove("cirkla-private-chat-header");
      items.remove("cirkla-invite-member-header");
    });

    const applyPostEmbeds = () => {
      enhancePanoptoEmbeds();
      enhancePostMediaEmbeds();
      enhancePostLinkPreviews();

      setTimeout(() => {
        enhancePanoptoEmbeds();
        enhancePostMediaEmbeds();
        enhancePostLinkPreviews();
      }, 0);

      setTimeout(() => {
        enhancePanoptoEmbeds();
        enhancePostMediaEmbeds();
        enhancePostLinkPreviews();
      }, 150);
    };

    applyPostEmbeds();
    const applyMasqueradeCompletionGate = (options = {}) => {
      setTimeout(() => enforceMasqueradeCompletionGate(options), 0);
      setTimeout(() => enforceMasqueradeCompletionGate(), 350);
    };
    setTimeout(() => {
      applyMasqueradeCompletionGate({ force: true });
    }, 700);
    if (isMasqueradeConfigureRoute()) {
      closeMobileDrawer();
      setTimeout(closeMobileDrawer, 0);
      setTimeout(closeMobileDrawer, 120);
    }
    runChatInviteFallbacks();
    setTimeout(runChatInviteFallbacks, 0);
    setTimeout(runChatInviteFallbacks, 180);

    if (
      m &&
      m.route &&
      typeof m.route.set === "function" &&
      !app.__cirklaMasqueradeRoutePatchInstalled
    ) {
      const originalRouteSet = m.route.set;

      m.route.set = function () {
        const result = originalRouteSet.apply(this, arguments);

        setTimeout(() => {
          applyPostEmbeds();
          applyMasqueradeCompletionGate({ force: true });
        }, 250);

        setTimeout(runChatInviteFallbacks, 0);
        setTimeout(runChatInviteFallbacks, 180);

        return result;
      };

      app.__cirklaMasqueradeRoutePatchInstalled = true;
    }

    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(() => {
        enhancePanoptoEmbeds();
        enhancePostMediaEmbeds();
        enhancePostLinkPreviews();
      }).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    runChatInviteFallbacks();
    endInitialSessionBootSoon();
  },
  10000,
);

app.initializers.add("cirkla-chat-history-autoscroll", () => {
  const apply = ({ force = false } = {}) => {
    if (!isConversationRoute()) return;

    syncChatHistoryAutoScroll({ force });
    settleChatScrollToBottom(1800, { force });
  };

  apply({ force: true });
  setTimeout(() => apply({ force: true }), 0);
  setTimeout(() => apply({ force: true }), 100);
  setTimeout(() => apply({ force: true }), 500);

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      apply({ force: true });
      setTimeout(() => apply({ force: true }), 100);
      setTimeout(() => apply({ force: true }), 500);
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => apply()).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});

app.initializers.add("cirkla-runtime-display-settings", () => {
  applyCirklaRuntimeSettings();
  ensureUserDisplayPreferenceStyles();

  setTimeout(applyCirklaRuntimeSettings, 0);
  setTimeout(ensureUserDisplayPreferenceStyles, 0);

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => {
      applyCirklaRuntimeSettings();
      ensureUserDisplayPreferenceStyles();
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});

app.initializers.add("cirkla-discussion-only-search", () => {
  extend(Search.prototype, "sourceItems", function (items) {
    /*
     * Keep only Flarum discussion search.
     * Remove user search and any other extension-provided search sources.
     */
    Object.keys(items.items || {}).forEach((key) => {
      if (key !== "discussions") {
        items.remove(key);
      }
    });
  });
});
